/* Delfino — nail-scan.js („Scanare Unghii AI").
   Un singur fișier, fără pas de build (cerința lui Robert), împărțit în module interne cu
   interfețe mici, în ordinea din spec (§4): util · track · api · images · camera · stages ·
   intro · quiz · processing · result · plan + cta (29.09: comanda prin formularul Releasit, nu prin
   coș) · email · orderHelp (v1.2) · history + funnel (pâlnia spre Worker) · callback (05.10: scaner v2,
   „te sună un consultant") · progress · boot.

   Sarcina 64 livrează nucleul: util, track, api, stages + etapele intro și quiz. Modulele
   `images`/`camera` (sarcina 65), `processing`/`result` (66), `cart` (67), `email`/`progress` (68)
   au mai jos doar antetul lor, ca să știe fiecare sarcină unde scrie.

   Contractul DOM (comun tuturor sarcinilor): rădăcina `[data-ns-root]` poartă `data-ns-worker-url`;
   configul stă în `<script type="application/json" data-ns-config>`; etapele sunt
   `[data-ns-stage="intro|quiz|capture|review|processing|result|error|shared"]` (`shared` = pagina
   „rezultat primit", v1.1 §3a: o vede cine deschide un link `?s=v1.…`); acțiunile sunt
   `[data-ns-action=…]`; părțile randate din JS sunt sloturi `[data-ns-slot=…]`.

   O stare pe instanță (creată în `boot`, plimbată prin `ctx`) — NICIUN singleton mutabil la nivel
   de modul: două secțiuni pe aceeași pagină (sau o re-randare din editorul de teme) nu au voie să
   împartă starea. Tot ce se leagă (listeneri, cronometre, AbortController-e) trece prin
   ajutoarele din `ctx`, ca teardown-ul să le poată elibera pe toate.

   Fără texte românești în cod: tot ce citește clientul vine din `config.texts` (setările secțiunii).

   Înregistrare — exact tiparul celorlalte secțiuni (section-product-cases.js): fișierul e încărcat
   cu `defer` ȘI înaintea lui theme.js, deci la execuție `window.DL` încă nu există; `readyState`
   nu e niciodată 'loading' pentru un script defer, așa că așteptăm DOMContentLoaded (când tot codul
   de nivel superior al scripturilor defer a rulat, inclusiv theme.js) și doar un fișier injectat
   DUPĂ pornirea paginii (editorul de teme) prinde 'complete' și se înregistrează pe loc.
   Idempotent prin `dataset.bound`, cu teardown prin `DL.onDestroy` (editorul aruncă nodul
   secțiunii la fiecare modificare). */
(function () {
  'use strict';

  var STAGE_NAMES = ['intro', 'quiz', 'capture', 'review', 'processing', 'result', 'error', 'shared'];
  var QUIZ_STEPS = ['zone', 'duration', 'count'];
  /* Experimentul fără chestionar (05.10.2026): cu setarea `quiz_enabled` oprită, „Începe scanarea"
     duce direct la poze, iar Worker-ul primește zona `auto` și o citește el din poză. De aici
     încolo zona se ia DOAR din rezultat (`result.zone`), niciodată din `state.quiz`. */
  var ZONE_AUTO = 'auto';
  /* Lipsa cheii = pornit: o secțiune salvată înainte de setare se poartă exact ca până acum. */
  function quizEnabled(config) { return !(config && config.quizEnabled === false); }
  /* Timpul maxim de așteptare al unei scanări (brief-ul: „client timeout 90s → error"). */
  var SCAN_TIMEOUT_MS = 90000;
  /* Motivele acceptate de `POST /event` al Worker-ului; orice altceva nu se trimite deloc. */
  var EVENT_REASONS = ['camera_denied', 'camera_error', 'upload_invalid', 'timeout', 'network', 'server_error', 'overload', 'model_error', 'rate_limited', 'cancelled'];

  function getWindow() { return typeof window !== 'undefined' ? window : null; }

  /* ── util ── escapare, selectoare limitate la secțiune, completare de texte, stocare care nu
     aruncă niciodată, formatare de bani și id-uri scurte. Fără stare proprie. */

  var MONEY_FORMATS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', '.']
  };

  var util = {
    esc: function (value) {
      if (value === null || value === undefined) return '';
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    /* Căutare într-o hartă cu o cheie NESIGURĂ (severitate, zonă, unghie, problemă — toate alese
       de model). `map[key]` pe un obiect simplu găsește și numele de pe `Object.prototype`:
       `constructor` ar fi scos pe ecran „function Object() { [native code] }", iar `toString` ar fi
       ajuns într-un nume de clasă CSS. Aici trece doar o proprietate PROPRIE; altfel `null`. */
    own: function (map, key) {
      if (!map || typeof key !== 'string' || key === '') return null;
      if (!Object.prototype.hasOwnProperty.call(map, key)) return null;
      var value = map[key];
      return value === undefined || value === null ? null : value;
    },

    qs: function (scope, selector) {
      return scope && typeof scope.querySelector === 'function' ? scope.querySelector(selector) : null;
    },

    qsa: function (scope, selector) {
      if (!scope || typeof scope.querySelectorAll !== 'function') return [];
      return Array.prototype.slice.call(scope.querySelectorAll(selector));
    },

    /* `{n}`, `{i}`, `{delta}`… — un loc pentru care nu primim valoare rămâne exact cum era, ca
       să se vadă în pagină că setarea cere o completare (și să nu apară „undefined"). */
    fmt: function (template, vars) {
      if (template === null || template === undefined) return '';
      var values = vars || {};
      return String(template).replace(/\{([A-Za-z0-9_]+)\}/g, function (whole, key) {
        return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : whole;
      });
    },

    /* TEXT, nu markup: `textContent` nu poate produce niciodată o etichetă vie, oricât de urât ar
       fi ce vine de la model. Pentru markup construit de NOI există `util.tpl` (mai jos). */
    setText: function (el, value) {
      if (!el) return null;
      el.textContent = value === null || value === undefined ? '' : String(value);
      return el;
    },

    /* Șablon de markup scris de noi (de încredere) în care TOATE valorile sunt escapate:
         `tpl('<b>{n}</b>', { n: dinModel })`.
       Regula, pe scurt: text → `setText` (sau `esc(fmt(t, v))` dacă ai nevoie de șir);
       markup → `tpl(...)`. NICIODATĂ `fmt(esc(t), v)`: ar escapa șablonul și ar lăsa valorile
       nescapate, exact pe dos. */
    tpl: function (template, vars) {
      if (template === null || template === undefined) return '';
      var values = vars || {};
      return String(template).replace(/\{([A-Za-z0-9_]+)\}/g, function (whole, key) {
        return Object.prototype.hasOwnProperty.call(values, key) ? util.esc(values[key]) : whole;
      });
    },

    /* Safari în mod privat, stocare blocată din setările browserului, iframe fără permisiuni:
       accesul în sine poate arunca, deci TOT trece prin try/catch. */
    safeStorage: function (kind) {
      function store() {
        var w = getWindow();
        if (!w) return null;
        try { return kind === 'session' ? w.sessionStorage : w.localStorage; } catch (e) { return null; }
      }
      return {
        get: function (key) {
          try { var s = store(); return s ? s.getItem(key) : null; } catch (e) { return null; }
        },
        set: function (key, value) {
          try { var s = store(); if (s) s.setItem(key, String(value)); return true; } catch (e) { return false; }
        },
        remove: function (key) {
          try { var s = store(); if (s) s.removeItem(key); return true; } catch (e) { return false; }
        }
      };
    },

    /* Prin `DL.money` când theme.js e în pagină (folosește formatul magazinului), altfel după
       formatul primit din config — niciodată acolade pe ecran. */
    money: function (cents, format) {
      var w = getWindow();
      if (w && w.DL && typeof w.DL.money === 'function') {
        try { return w.DL.money(cents); } catch (e) { /* cădem pe varianta locală */ }
      }
      var pattern = String(format === null || format === undefined ? '' : format);
      var found = pattern.match(/\{\{\s*(\w+)\s*\}\}/);
      /* Numele formatului vine din setările magazinului: tot prin `own`, ca `{{constructor}}` să
         nu scoată funcția de pe prototip în locul regulii de formatare. */
      var rule = util.own(MONEY_FORMATS, (found && found[1]) || '') || MONEY_FORMATS.amount_with_comma_separator;
      var parts = (Number(cents) / 100).toFixed(rule[0]).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, rule[1]);
      var value = parts[1] ? whole + rule[2] + parts[1] : whole;
      return found ? pattern.replace(found[0], value) : value;
    },

    uid: function () {
      return 'ns' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    }
  };

  /* Cel mai apropiat strămoș (inclusiv elementul însuși) care poartă atributul cerut, oprit la
     rădăcina secțiunii: `closest('[attr]')` ar urca în TOATĂ pagina, iar o secțiune imbricată (sau
     o modală mutată de altcineva) ar putea nimeri handlerul altei instanțe. */
  function closestAttr(el, attribute, boundary) {
    var node = el;
    while (node && typeof node.hasAttribute === 'function') {
      if (node.hasAttribute(attribute)) return node;
      if (node === boundary) return null;
      node = node.parentNode;
    }
    return null;
  }

  /* „Dezactivat" înseamnă trei lucruri, după element: proprietatea `disabled` (doar controalele de
     formular o au), atributul `disabled` (un `<a>` randat din JS) și `aria-disabled="true"`. */
  function isDisabled(el) {
    if (!el) return true;
    if (el.disabled === true) return true;
    if (typeof el.hasAttribute === 'function' && el.hasAttribute('disabled')) return true;
    return typeof el.getAttribute === 'function' && el.getAttribute('aria-disabled') === 'true';
  }

  /* ── blocarea derulării din spatele unei ferestre modale ──
     Clasa se pune pe `<html>` DIN JS, nu prin `html:has(.ns-modal-open)`: `:has` lipsește pe
     Safari < 15.4 și pe Firefox < 121, adică tocmai pe telefoanele unde o pagină care se mișcă
     sub deget doare cel mai tare.
     Registrul e la nivel de MODUL, nu de instanță: două secțiuni pe aceeași pagină (sau editorul
     de teme) nu au voie să-și stingă una alteia blocarea. Clasa există cât timp măcar o fereastră,
     a oricărei instanțe, e deschisă. Fiecare instanță își scoate cheile la resetare și la teardown. */
  var MODAL_LOCK_CLASS = 'ns-modal-open';
  var openModals = {};

  function syncModalLock() {
    var w = getWindow();
    var root = w && w.document && w.document.documentElement;
    if (!root || !root.classList) return false;
    var any = false;
    for (var key in openModals) {
      if (Object.prototype.hasOwnProperty.call(openModals, key)) { any = true; break; }
    }
    try { root.classList[any ? 'add' : 'remove'](MODAL_LOCK_CLASS); } catch (e) { /* DOM fals */ }
    return any;
  }

  function setModalLock(key, on) {
    if (!key) return false;
    if (on) openModals[key] = 1;
    else delete openModals[key];
    return syncModalLock();
  }

  function releaseModalLocks(prefix) {
    for (var key in openModals) {
      if (Object.prototype.hasOwnProperty.call(openModals, key) && key.indexOf(prefix) === 0) delete openModals[key];
    }
    syncModalLock();
  }

  function emit(el, type, detail) {
    var event = null;
    try {
      if (typeof CustomEvent === 'function') event = new CustomEvent(type, { detail: detail, bubbles: true });
    } catch (e) { event = null; }
    if (!event) event = { type: type, detail: detail, bubbles: true };
    try { if (el && typeof el.dispatchEvent === 'function') el.dispatchEvent(event); } catch (e) { /* fără DOM real */ }
    return event;
  }

  /* ── track ── pixeli (fbq/ttq/gtag/Shopify.analytics), fiecare în propriul try/catch: nu aruncă
     niciodată, nu blochează fluxul și nu duce mai departe date personale, poze sau tokenul. */

  /* Evenimentele standard Meta merg pe `track`; ale noastre (ScanConsent, NailScanCompleted) pe
     `trackCustom` — pe un eveniment necunoscut `track` ar fi ignorat de pixel. */
  var FB_STANDARD = ['AddPaymentInfo', 'AddToCart', 'AddToWishlist', 'CompleteRegistration', 'Contact', 'CustomizeProduct', 'Donate', 'FindLocation', 'InitiateCheckout', 'Lead', 'PageView', 'Purchase', 'Schedule', 'Search', 'StartTrial', 'SubmitApplication', 'Subscribe', 'ViewContent'];
  /* Chei care nu au ce căuta în analytics: tokenul (deschide rezultatul), pozele, emailul. */
  var TRACK_DENY = /token|photo|image|blob|mail|phone|url|src|file|href/i;
  /* Cât de lung poate fi un tablou trimis mai departe (`content_ids`): un plan are câteva
     produse, nu zeci — un tablou lung înseamnă că s-a strecurat altceva. */
  var TRACK_LIST_MAX = 50;

  function isSimple(value) {
    var type = typeof value;
    return type === 'string' || type === 'number' || type === 'boolean';
  }

  function sanitizeTrack(data) {
    var out = {};
    if (!data || typeof data !== 'object') return out;
    for (var key in data) {
      if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
      if (TRACK_DENY.test(key)) continue;
      var value = data[key];
      var type = typeof value;
      /* doar valori simple — un obiect ar putea căra rezultatul întreg sau un obiect URL */
      if (type === 'string' || type === 'number' || type === 'boolean') { out[key] = value; continue; }
      /* Excepția: un tablou de valori SIMPLE (`content_ids` pentru AddToCart — pixelii îl cer
         chiar ca tablou). Dacă e măcar un obiect înăuntru, tabloul se aruncă întreg: acolo ar
         putea sta orice (un rezultat, un `File`, un `blob:`). */
      if (Array.isArray(value) && value.length <= TRACK_LIST_MAX && value.every(isSimple)) out[key] = value.slice();
    }
    return out;
  }

  /* ── lista ALBĂ pentru platformele de reclame ──
     Spre Meta, TikTok și Google pleacă DOAR cheile de mai jos, pe eveniment. Motivul: rezultatul
     scanării (`severity`, `score` și orice seamănă cu ele) e o informație despre STAREA DE SĂNĂTATE
     a unei persoane identificabile. Termenii Meta pentru Business Tools și cei TikTok interzic
     datele de sănătate — evenimentul e marcat „potentially violating personal data" și blocat,
     uneori cu avertisment pe tot contul de reclame; în UE e și categorie specială (GDPR art. 9).
     Lista e ALBĂ dinadins, nu neagră: o cheie nouă, adăugată cândva în cod, NU pleacă spre reclame
     până nu o trece cineva aici, cu bună știință. Numele evenimentelor rămân neschimbate.
     `Shopify.analytics.publish` primește în continuare încărcătura întreagă: rămâne în magazin,
     la Robert. Dacă se adaugă vreodată un Custom Pixel, acela nu are voie să ducă mai departe
     câmpurile de sănătate (vezi GHID-Robert.md, capitolul 2.13). */
  var AD_PARAMS = {
    /* comerciale — de astea au nevoie reclamele ca să optimizeze */
    AddToCart: ['value', 'currency', 'content_ids', 'num_items'],
    Lead: ['content_name'],
    Contact: ['content_name'],
    CompleteRegistration: ['content_name'],
    /* scaner v2 (05.10.2026): cererea „să mă sune un consultant" a plecat (204) — doar numele conținutului */
    Schedule: ['content_name'],
    /* poarta de email: „soft"/„hard", nimic despre unghii */
    NailScanGateShown: ['mode'],
    NailScanGateSkipped: ['mode'],
    NailScanGateWhatsApp: ['mode'],
    /* scanarea terminată: doar faptul că s-a terminat (+ dacă omul venise dintr-un link primit) */
    NailScanCompleted: ['from_shared'],
    /* v1.4: coada și limita pe browser — doar FAPTUL, fără nicio cheie */
    NailScanQueued: [],
    NailScanSessionLimit: [],
    ScanConsent: []
  };

  function adPayload(name, payload) {
    var allow = Object.prototype.hasOwnProperty.call(AD_PARAMS, name) ? AD_PARAMS[name] : [];
    var out = {};
    for (var i = 0; i < allow.length; i++) {
      if (Object.prototype.hasOwnProperty.call(payload, allow[i])) out[allow[i]] = payload[allow[i]];
    }
    return out;
  }

  /* ── consimțământ (Shopify Customer Privacy API) ──
     „Nu știm" înseamnă `true`: pe un magazin fără banner de cookie-uri, în editor sau în teste
     API-ul lipsește cu totul, iar comportamentul rămâne cel de până acum — nu inventăm un refuz.
     Doar un „nu" EXPLICIT oprește apelul. Nimic de aici nu aruncă vreodată. */
  function privacyApi() {
    try {
      var w = getWindow();
      var cp = w && w.Shopify && w.Shopify.customerPrivacy;
      return cp && typeof cp === 'object' ? cp : null;
    } catch (e) { return null; }
  }

  function consentSays(method) {
    var cp = privacyApi();
    if (!cp || typeof cp[method] !== 'function') return true;
    try { return cp[method]() !== false; } catch (e) { return true; }
  }

  var track = {
    standard: FB_STANDARD,
    event: function (name, data) {
      var w = getWindow();
      if (!w || !name) return;
      var payload = sanitizeTrack(data);
      var forAds = adPayload(name, payload);
      /* Pixelii de marketing (Meta, TikTok) cer acordul de marketing. */
      if (consentSays('marketingAllowed')) {
        try { if (typeof w.fbq === 'function') w.fbq(FB_STANDARD.indexOf(name) > -1 ? 'track' : 'trackCustom', name, forAds); } catch (e) { /* adblock */ }
        try { if (w.ttq && typeof w.ttq.track === 'function') w.ttq.track(name, forAds); } catch (e) { /* idem */ }
        /* `gtag` e și reclamă (Google Ads), și analitice (GA4): cere ambele acorduri. */
        try { if (consentSays('analyticsProcessingAllowed') && typeof w.gtag === 'function') w.gtag('event', name, forAds); } catch (e) { /* idem */ }
      }
      /* Shopify.analytics aplică singur consimțământul pe pixelii abonați și nu iese din magazin. */
      try {
        if (w.Shopify && w.Shopify.analytics && typeof w.Shopify.analytics.publish === 'function') w.Shopify.analytics.publish(name, payload);
      } catch (e) { /* idem */ }
    }
  };

  /* ── api ── Worker-ul: POST /scan (multipart, 90 s), GET /result/:token, POST /event (beacon).
     Nicio promisiune nu se respinge: fiecare apel se termină într-un `{kind:…}` normalizat, ca
     etapele să nu aibă de tratat excepții. */

  function asString(value) { return typeof value === 'string' ? value : ''; }

  /* ── `sid` — codul BROWSERULUI (v1.4) ──
     Worker-ul numără scanările pe browser, nu pe IP (doi oameni pe același wifi au același IP). Codul
     se face O SINGURĂ dată, stă în localStorage (limita e pe zi, nu pe filă) și pleacă DOAR în
     formularul lui `/scan`. Nu identifică un om — e un șir la întâmplare — dar tot nu are ce căuta
     la pixeli sau în Klaviyo: nu-l dăm nimănui altcuiva.
     Stocare blocată (Safari privat, iframe) → rămâne în memoria paginii: limita ține cât ține
     pagina, iar Worker-ul are oricum găleata de rezervă pe amprenta de IP. */
  var SID_KEY = 'ns:sid';
  var SID_RE = /^[a-z0-9]{24,40}$/;
  var SID_LENGTH = 32;
  var SID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
  var memorySid = '';

  function makeSid() {
    var out = '';
    var bytes = null;
    try {
      var w = getWindow();
      var source = (w && w.crypto) || (typeof crypto !== 'undefined' ? crypto : null);
      if (source && typeof source.getRandomValues === 'function' && typeof Uint8Array === 'function') {
        bytes = new Uint8Array(SID_LENGTH);
        source.getRandomValues(bytes);
      }
    } catch (e) { bytes = null; }
    for (var i = 0; i < SID_LENGTH; i++) {
      var n = bytes ? bytes[i] : Math.floor(Math.random() * SID_ALPHABET.length);
      out += SID_ALPHABET.charAt(n % SID_ALPHABET.length);
    }
    return out;
  }

  function sessionId() {
    if (memorySid) return memorySid;
    var store = util.safeStorage('local');
    var saved = store.get(SID_KEY);
    if (typeof saved === 'string' && SID_RE.test(saved)) { memorySid = saved; return memorySid; }
    memorySid = makeSid();
    store.set(SID_KEY, memorySid);
    return memorySid;
  }

  /* Rutele de ISTORIC ale Worker-ului (v1.4 §C1) și ce acțiuni cunoaște: enum ÎNCHIS, ca la motive. */
  var HISTORY_PATHS = { plan: '/plan', action: '/action' };
  /* 29.09.2026 („altfel suntem de gard"): pe lângă cele 6 de la început, TOATĂ pâlnia rezultatului —
     vezi `createFunnel` și worker/nail-scan/src/history.js (ACELAȘI enum, citit de tabloul din Merlo).
     `add_to_cart` rămâne în enum doar pentru compatibilitate: tema nu-l mai trimite (nu mai e coș). */
  var HISTORY_ACTIONS = {
    add_to_cart: 1, order_whatsapp: 1, order_call: 1, email_saved: 1, phone_saved: 1, share_opened: 1,
    result_viewed: 1, lead_viewed: 1, lead_focus: 1, plan_viewed: 1, sticky_click: 1, addon_toggled: 1, cod_click: 1,
    scroll_50: 1, scroll_100: 1, time_30s: 1, time_90s: 1, form_opened: 1, code_applied: 1,
    /* scaner v2 (05.10.2026): cardul de apel a intrat în ecran. `callback_requested` o scrie Worker-ul
       la `POST /callback` — tema NU o trimite, deci nici nu e aici. */
    callback_viewed: 1
  };
  var HISTORY_MAX_BYTES = 4096;

  function byteLength(text) {
    try { return new Blob([text]).size; } catch (e) { /* fără Blob: estimăm acoperitor */ }
    return String(text).length * 3;
  }

  function doFetch(url, init) {
    try {
      var w = getWindow();
      if (w && typeof w.fetch === 'function') return w.fetch(url, init);
      if (typeof fetch === 'function') return fetch(url, init);
    } catch (e) { return Promise.reject(e); }
    return Promise.reject(new Error('fetch missing'));
  }

  /* `doFetch` întoarce o promisiune respinsă și când `fetch` lipsește cu totul — pentru `api.event`
     e nevoie să deosebim „cererea a eșuat" (nu mai încercăm nimic) de „browserul n-are fetch"
     (mai avem baliza). */
  function hasFetch() {
    try {
      var w = getWindow();
      if (w && typeof w.fetch === 'function') return true;
      return typeof fetch === 'function';
    } catch (e) { return false; }
  }

  function readJson(res) {
    return Promise.resolve()
      .then(function () { return res && typeof res.json === 'function' ? res.json() : null; })
      .catch(function () { return null; });
  }

  function networkKind(err, timedOut) {
    if (timedOut) return { kind: 'timeout' };
    if (err && (err.name === 'AbortError' || err.name === 'CanceledError')) return { kind: 'aborted' };
    return { kind: 'network' };
  }

  /* AbortController lipsă (browser foarte vechi) → un înlocuitor inofensiv, ca restul codului să
     nu aibă de verificat nimic. Singurul loc unde se construiește unul e aici. */
  function makeAborter() {
    try { return new AbortController(); } catch (e) { return { abort: function () {}, signal: undefined }; }
  }

  /* `ctx` e obligatoriu în producție (îl dă `boot`): prin el cronometrul de 90 s și
     AbortController-ul intră în registrul de curățare al instanței, ca teardown-ul secțiunii să le
     poată opri. Fără `ctx` (folosire izolată) cădem pe cronometre libere. */
  function createApi(workerUrl, ctx) {
    var base = String(workerUrl || '').trim().replace(/\/+$/, '');

    function startTimer(fn) { return ctx && ctx.timer ? ctx.timer(fn, SCAN_TIMEOUT_MS) : setTimeout(fn, SCAN_TIMEOUT_MS); }
    function stopTimer(id) { if (ctx && ctx.clearTimer) ctx.clearTimer(id); else clearTimeout(id); }
    function takeAborter() { return ctx && ctx.aborter ? ctx.aborter() : makeAborter(); }
    function dropAborter(c) { if (ctx && ctx.releaseAborter) ctx.releaseAborter(c); }

    /* Cronometrul de 90 s + un AbortController propriu; dacă apelantul a dat și el un `signal`
       (Task 66 anulează scanarea la „Reia pozele"), îl legăm de al nostru. */
    function withTimeout(externalSignal, run) {
      var controller = takeAborter();
      var timedOut = false;
      var timer = startTimer(function () {
        timedOut = true;
        try { controller.abort(); } catch (e) { /* deja oprit */ }
      });
      function onExternalAbort() { try { controller.abort(); } catch (e) { /* idem */ } }
      if (externalSignal) {
        if (externalSignal.aborted) onExternalAbort();
        else if (typeof externalSignal.addEventListener === 'function') externalSignal.addEventListener('abort', onExternalAbort);
      }
      function done(out) {
        stopTimer(timer);
        dropAborter(controller);
        if (externalSignal && typeof externalSignal.removeEventListener === 'function') externalSignal.removeEventListener('abort', onExternalAbort);
        return out;
      }
      return run(controller.signal)
        .catch(function (err) { return networkKind(err, timedOut); })
        .then(done);
    }

    function normaliseScan(res, data) {
      var body = data || {};
      var status = (res && res.status) || 0;
      if (status === 403 || body.error === 'consent_required') return { kind: 'consent_required' };
      /* v1.4: limita pe BROWSER (2 scanări reușite / 8 încercări pe zi). Mesajul românesc al
         Worker-ului NU se citește: ecranul are textele lui. `limit` e indiciul citibil de mașină
         („ok" = a primit deja rezultatele zilei, „tries" = prea multe încercări), dacă Worker-ul îl
         trimite; altfel rămâne gol și ecranul arată textul neutru. ÎNAINTE de `rate_limited`: și
         acela e un 429. */
      if (body.error === 'session_limit') {
        var hint = asString(body.reason) || asString(body.limit);
        return { kind: 'session_limit', retryAt: asString(body.retry_at), limit: hint === 'ok' || hint === 'tries' ? hint : '' };
      }
      if (status === 429 || body.error === 'rate_limited') return { kind: 'rate_limited', message: asString(body.message) };
      if (status >= 500) return { kind: 'server_error' };
      if (status === 400 || body.error === 'bad_request') return { kind: 'bad_request', message: asString(body.message) };
      /* Tokenul poate lipsi la un răspuns REUȘIT: modelul a răspuns, dar stocarea rezultatului a
         eșuat. Rezultatul e bun și se afișează; ce depinde de token (link `?r=`, email, evoluție,
         proprietatea `_nail_scan`) trebuie ascuns de modulele următoare. */
      if (body.success === true && body.result) return { kind: 'ok', token: asString(body.token) || null, result: body.result };
      /* v1.4: „mai așteaptă puțin" — serviciul e plin ACUM, dar se eliberează. Nu e o eroare: omul
         rămâne pe ecranul de așteptare și aceleași poze pleacă din nou peste `retryAfter` secunde. */
      if (body.wait === true) return { kind: 'wait', retryAfter: Number(body.retry_after), reason: asString(body.reason) };
      if (body.overload === true) return { kind: 'overload' };
      if (body.success === false && body.error) return { kind: 'model_error', error: String(body.error), message: asString(body.message) };
      return { kind: 'server_error' };
    }

    /* `fetch` cu `keepalive` ESTE transportul, nu rezerva — iar `credentials: 'omit'` e
       partea care contează.
       `navigator.sendBeacon` trimite ÎNTOTDEAUNA cu „credentials: include", iar o cerere
       credențializată cere de la server `Access-Control-Allow-Credentials: true`. Worker-ul
       nu-l trimite (și bine face: e un contor anonim, n-are ce căuta cu cookie-uri), deci
       browserul taie cererea la preflight — dar `sendBeacon` întorsese deja `true`, așa că
       nimeni n-avea cum să observe. Probat în Chromium, cross-origin: beacon → `true` în
       pagină, eroare CORS în consolă, ZERO evenimente în jurnalul Worker-ului; același
       eveniment prin `fetch` fără credențiale → 204, consolă curată, evenimentul în jurnal.
       `keepalive` îi dă aceeași garanție ca balizei: cererea supraviețuiește navigării.
       `sendBeacon` rămâne doar pentru browserele fără `fetch`. */
    function sendJson(url, body) {
      if (!hasFetch()) return false;
      try {
        var request = doFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body,
          keepalive: true,
          credentials: 'omit',
          mode: 'cors'
        });
        if (request && typeof request.catch === 'function') request.catch(function () { /* pâlnia nu strică nimic */ });
      } catch (e) { /* nici măcar n-a plecat; tot „am încercat" rămâne */ }
      return true;
    }

    return {
      base: base,
      reasons: EVENT_REASONS,
      sid: sessionId,
      timeoutMs: SCAN_TIMEOUT_MS,

      scan: function (options) {
        var opts = options || {};
        if (!base) return Promise.resolve({ kind: 'not_configured' });
        /* Worker-ul ar răspunde oricum 403; nu urcăm pozele degeaba. */
        if (!opts.consent) return Promise.resolve({ kind: 'consent_required' });
        var form;
        try {
          var quiz = opts.quiz || {};
          form = new FormData();
          form.append('consent', opts.consent ? '1' : '0');
          if (opts.auto) {
            /* Fără chestionar (`quiz_enabled` oprit): nu există răspunsuri de trimis. Zona o
               hotărăște Worker-ul din poză, deci pleacă doar `auto` — fără durată, fără număr. */
            form.append('zone', ZONE_AUTO);
            form.append('quiz', JSON.stringify({ zone: ZONE_AUTO }));
          } else {
            form.append('zone', String(quiz.zone || ''));
            form.append('quiz', JSON.stringify({ zone: quiz.zone || '', duration: quiz.duration || '', count: quiz.count || '' }));
          }
          form.append('sid', sessionId());
          var blobs = opts.blobs || [];
          for (var i = 0; i < blobs.length; i++) form.append('images', blobs[i], 'photo-' + (i + 1) + '.jpg');
        } catch (e) {
          return Promise.resolve({ kind: 'network' });
        }
        return withTimeout(opts.signal, function (signal) {
          /* fără `Content-Type` pus de noi: browserul trebuie să adauge boundary-ul multipart */
          return doFetch(base + '/scan', { method: 'POST', body: form, signal: signal })
            .then(function (res) { return readJson(res).then(function (data) { return normaliseScan(res, data); }); });
        });
      },

      result: function (token) {
        if (!base) return Promise.resolve({ kind: 'not_configured' });
        var id = String(token || '');
        return withTimeout(null, function (signal) {
          return doFetch(base + '/result/' + encodeURIComponent(id), { method: 'GET', signal: signal })
            .then(function (res) {
              return readJson(res).then(function (data) {
                var body = data || {};
                var status = (res && res.status) || 0;
                if (status === 404 || body.error === 'not_found') return { kind: 'not_found' };
                if (status >= 500) return { kind: 'server_error' };
                if (body.success === true && body.result) return { kind: 'ok', token: asString(body.token) || id, result: body.result };
                return { kind: 'server_error' };
              });
            });
        });
      },

      /* Pâlnie: consent_accepted / scan_started / scan_failed(+motiv). `sendBeacon` supraviețuiește
         navigării (de-aia îl preferăm), `fetch keepalive` e rezerva. Nu întoarce niciodată o
         promisiune respinsă și nu oprește fluxul. */
      event: function (type, reason) {
        if (!base) return false;
        /* Modul „vizitator" (v1.1 §3a): cine a deschis un link primit nu e în pâlnia NOASTRĂ de
           scanare — de la pagina lui nu pleacă nimic spre Worker, nici măcar un contor. */
        if (ctx && ctx.state && ctx.state.viewer) return false;
        var payload = { type: String(type || '') };
        if (reason && EVENT_REASONS.indexOf(String(reason)) > -1) payload.reason = String(reason);
        var body = JSON.stringify(payload);
        var url = base + '/event';
        var w = getWindow();
        var nav = (w && w.navigator) || (typeof navigator !== 'undefined' ? navigator : null);
        if (sendJson(url, body)) return true;

        if (nav && typeof nav.sendBeacon === 'function') {
          try {
            if (nav.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return true;
          } catch (e) { return false; }
        }
        return false;
      },

      /* Istoricul (v1.4 §C1): `plan` = planul propus, `action` = ce a făcut omul cu el. Același
         transport ca pâlnia (`fetch` + `keepalive`, fără credențiale) și aceleași garanții: nu
         așteaptă nimeni răspunsul, un eșec e tăcut, în modul „vizitator" nu pleacă nimic. FĂRĂ
         rezerva `sendBeacon`: ar pleca cu credențiale și ar fi tăiată de CORS (vezi mai sus).
         Un mesaj peste 4 KB ar fi aruncat de Worker — nu-l mai trimitem. */
      history: function (kind, payload) {
        if (!base) return false;
        if (ctx && ctx.state && (ctx.state.viewer || ctx.state.destroyed)) return false;
        var path = util.own(HISTORY_PATHS, kind);
        if (!path || !payload || typeof payload !== 'object') return false;
        var body;
        try { body = JSON.stringify(payload); } catch (e) { return false; }
        if (byteLength(body) > HISTORY_MAX_BYTES) return false;
        return sendJson(base + path, body);
      }
    };
  }

  /* ── images ── orice poză (din cameră sau din galerie) trece pe aici: canvas → max 1024 px pe
     latura lungă → JPEG 0,85. Plus gărzile din brief: HEIC/HEIF, blob sub 4 KB („poza pare
     goală"), cronometru de siguranță la decodare (5 s) și capcana iOS `naturalWidth === 0`.
     Nimic de-aici nu respinge vreodată o promisiune: orice eșec se întoarce ca
     `{ ok:false, reason, message }`, ca etapele să nu aibă de prins excepții. */

  var MAX_SIDE = 1024;
  var JPEG_QUALITY = 0.85;
  /* Worker-ul acceptă JPEG/PNG între 4 KB și 5 MB; sub 4 KB poza e practic goală. */
  var MIN_BYTES = 4 * 1024;
  var MAX_BYTES = 5 * 1024 * 1024;
  var DECODE_TIMEOUT_MS = 5000;

  var HEIC_NAME = /\.(heic|heif)$/i;
  var HEIC_TYPE = /^image\/hei[cf]/i;
  var UPLOADABLE_TYPE = /^image\/(jpeg|jpg|png)$/i;
  var UPLOADABLE_NAME = /\.(jpe?g|png)$/i;

  /* Browsere „din aplicație" (webview), unde getUserMedia sau nu există, sau pornește și rămâne
     neagră. Lista, pe bucăți: Facebook (`FBAN`, `FBAV`, `FB_IAB`, `FBIOS`), Instagram
     (`Instagram`), TikTok (`TikTok`, `musical_ly`, `Bytedance` — aplicația pune mai multe
     variante, după versiune și țară) și LINE (`Line/`). LINE se caută cu majusculă și urmat de
     cifră, ca să nu se potrivească „online/" dintr-un user-agent oarecare. */
  var IN_APP_RE = /FBAN|FBAV|FB_IAB|FBIOS|Instagram|TikTok|musical_ly|Bytedance/i;
  var LINE_RE = /(^|[\s;(])Line\/\d/;

  var IMAGE_ERROR_TEXT = {
    heic: 'err_heic',
    blank: 'err_blank',
    decode: 'err_decode',
    too_large: 'err_too_large',
    unsupported: 'err_unsupported'
  };

  /* `URL.createObjectURL` stă pe `window.URL` în browserele reale; îl luăm de-acolo ca să poată fi
     înlocuit în teste, cu rezervă pe global. Nu aruncă niciodată. */
  function urlApi() {
    var w = getWindow();
    if (w && w.URL && typeof w.URL.createObjectURL === 'function') return w.URL;
    if (typeof URL !== 'undefined' && URL && typeof URL.createObjectURL === 'function') return URL;
    return null;
  }
  function createObjectUrl(blob) {
    var api = urlApi();
    try { return api ? api.createObjectURL(blob) : null; } catch (e) { return null; }
  }
  function revokeObjectUrl(url) {
    var api = urlApi();
    try { if (api && url) api.revokeObjectURL(url); } catch (e) { /* deja revocat */ }
  }
  function closeBitmap(bitmap) {
    try { if (bitmap && typeof bitmap.close === 'function') bitmap.close(); } catch (e) { /* ImageBitmap vechi */ }
  }

  function isHeic(file) {
    if (!file) return false;
    return HEIC_NAME.test(String(file.name || '')) || HEIC_TYPE.test(String(file.type || ''));
  }

  function isInAppBrowser(ua) {
    var s = String(ua || '');
    return IN_APP_RE.test(s) || LINE_RE.test(s);
  }

  /* Niciodată în sus: o poză de 800 px rămâne de 800 px (mărită ar fi doar mai grea, nu mai bună). */
  function targetSize(width, height, max) {
    var limit = Number(max) > 0 ? Number(max) : MAX_SIDE;
    var w = Math.max(0, Math.round(Number(width) || 0));
    var h = Math.max(0, Math.round(Number(height) || 0));
    if (!w || !h) return { width: 0, height: 0 };
    var longest = Math.max(w, h);
    if (longest <= limit) return { width: w, height: h };
    var ratio = limit / longest;
    return { width: Math.max(1, Math.round(w * ratio)), height: Math.max(1, Math.round(h * ratio)) };
  }

  /* Dimensiunile sursei, oricare ar fi ea: <video>, <img>, ImageBitmap sau canvas. */
  function sourceSize(source) {
    if (!source) return { width: 0, height: 0 };
    return {
      width: Number(source.videoWidth || source.naturalWidth || source.width || 0),
      height: Number(source.videoHeight || source.naturalHeight || source.height || 0)
    };
  }

  function dataUrlToBlob(dataUrl) {
    try {
      var parts = String(dataUrl || '').split(',');
      if (parts.length < 2) return null;
      var w = getWindow();
      var fromBase64 = (w && typeof w.atob === 'function') ? w.atob : (typeof atob === 'function' ? atob : null);
      if (!fromBase64) return null;
      var binary = fromBase64(parts[1]);
      var bytes = new Uint8Array(binary.length);
      for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      /* Tipul vine din ANTETUL întors de browser, nu din ce am cerut noi: WebKit-ul vechi ignoră
         tipul cerut și dă PNG. Worker-ul acceptă și JPEG, și PNG — o etichetă mincinoasă ar strica
         doar validarea de acolo. */
      var header = String(parts[0] || '').match(/^data:([^;,]+)/);
      return new Blob([bytes], { type: (header && header[1]) || 'image/jpeg' });
    } catch (e) { return null; }
  }

  /* `toBlob` lipsește pe WebKit-urile vechi (iOS < 11): acolo cade pe `toDataURL`. */
  function canvasToBlob(canvas, quality) {
    return new Promise(function (resolve) {
      if (canvas && typeof canvas.toBlob === 'function') {
        try {
          canvas.toBlob(function (blob) { resolve(blob || null); }, 'image/jpeg', quality);
          return;
        } catch (e) { /* cădem pe toDataURL */ }
      }
      var url = '';
      try { url = canvas && typeof canvas.toDataURL === 'function' ? canvas.toDataURL('image/jpeg', quality) : ''; } catch (e) { url = ''; }
      resolve(dataUrlToBlob(url));
    });
  }

  /* Orientarea EXIF NU se corectează de mână: browserele moderne aplică singure `Orientation`
     atât la `<img>` (proprietatea CSS `image-orientation: from-image` e valoarea implicită din
     Chrome 81 / Firefox 26 / Safari 13.4), cât și la `createImageBitmap(file, {imageOrientation:
     'from-image'})`. Ce desenăm pe canvas e deja rotit cum trebuie. */
  function toJpeg(source, options) {
    var opts = options || {};
    var raw = sourceSize(source);
    var size = targetSize(raw.width, raw.height, opts.maxSide || MAX_SIDE);
    if (!size.width || !size.height) return Promise.resolve(null);
    var canvas = null;
    try { canvas = document.createElement('canvas'); } catch (e) { return Promise.resolve(null); }
    canvas.width = size.width;
    canvas.height = size.height;
    var cx = null;
    try { cx = typeof canvas.getContext === 'function' ? canvas.getContext('2d') : null; } catch (e) { cx = null; }
    if (!cx) return Promise.resolve(null);
    try {
      /* NICIO oglindire: e camera din spate, care arată lumea așa cum e. Deci un singur
         `drawImage`, drept — fără `scale(-1, 1)` și fără translatare. */
      cx.drawImage(source, 0, 0, size.width, size.height);
    } catch (e) { return Promise.resolve(null); }
    return canvasToBlob(canvas, typeof opts.quality === 'number' ? opts.quality : JPEG_QUALITY)
      .then(function (blob) {
        /* Pânza și-a făcut treaba: o dăm la zero ca să elibereze memoria pe loc (pe telefoanele
           slabe, trei canvas-uri de 1024 px ținute degeaba chiar contează). */
        try { canvas.width = 0; canvas.height = 0; } catch (e) { /* canvas fals */ }
        return blob ? { blob: blob, width: size.width, height: size.height } : null;
      });
  }

  /* Peste atâția octeți, o poză are aproape sigur latura lungă peste 1024 px — deci îi putem cere
     browserului s-o decodeze DIRECT mai mică. Pragul există ca să nu trimitem `resizeWidth` pentru
     o poză mică: acolo opțiunea ar MĂRI-O (`resizeWidth` e o lățime exactă, nu un plafon). */
  var RESIZE_HINT_BYTES = 1200 * 1024;

  /**
   * Opțiunile pentru `createImageBitmap`.
   *
   * De ce contează: fără `resizeWidth`, o poză de 48 MP se rasterizează ÎNTREAGĂ în memorie
   * (≈190 MB) doar ca s-o micșorăm noi imediat pe canvas — exact felul în care se închide fila pe
   * un telefon mai vechi. Cu opțiunea, browserul scalează în timpul decodării.
   *
   * Cerem doar `resizeWidth`: cu ambele dimensiuni, poza s-ar turti (sunt valori EXACTE, nu un
   * plafon care păstrează proporția). Rezultatul poate rămâne peste 1024 pe latura cealaltă (o
   * poză portret iese 1024×1365), dar acolo `toJpeg` îl duce la capăt — pe o pânză mică, ieftină.
   * Browserele care nu cunosc opțiunile le ignoră, deci nu e nevoie de altă detecție decât
   * existența lui `createImageBitmap` (calea cu `<img>` rămâne oricum rezerva).
   */
  function bitmapOptions(file) {
    var options = { imageOrientation: 'from-image' };
    var size = file && typeof file.size === 'number' ? file.size : 0;
    if (size >= RESIZE_HINT_BYTES) {
      options.resizeWidth = MAX_SIDE;
      options.resizeQuality = 'medium';
    }
    return options;
  }

  function createImages(ctx) {
    /* Decodările încă neterminate. Cronometrul lor de 5 s trece prin `ctx.timer`, iar teardown-ul
       îl STINGE — deci fără cârligul de mai jos o decodare rămasă în aer nu s-ar închide niciodată
       și și-ar duce cu ea object URL-ul temporar, nerevocat. */
    var pending = [];
    ctx.onTeardown(function () {
      pending.splice(0).forEach(function (close) {
        try { close(); } catch (e) { /* o decodare stricată nu blochează restul curățării */ }
      });
    });

    function fail(reason) {
      return { ok: false, reason: reason, message: ctx.text(IMAGE_ERROR_TEXT[reason] || 'err_decode') };
    }

    /* Verdictul final asupra unui blob gata făcut (din cameră sau din fișier). */
    function validate(out) {
      if (!out || !out.blob) return fail('decode');
      if (out.blob.size < MIN_BYTES) return fail('blank');
      if (out.blob.size > MAX_BYTES) return fail('too_large');
      return { ok: true, blob: out.blob, width: out.width, height: out.height };
    }

    /* Capcana iOS: poza s-a „încărcat", dar are `naturalWidth === 0` (memorie insuficientă la o
       poză uriașă). Dacă fișierul ales e oricum JPEG/PNG de mărime bună, îl trimitem AȘA CUM E —
       Worker-ul îl acceptă — în loc să-i arătăm clientului o eroare pentru o poză perfect bună.
       ACELAȘI drum se folosește și când decodarea depășește cronometrul de 5 s: pe un telefon slab
       o poză bună poate dura mai mult, iar „Nu am putut citi poza" ar fi o minciună. */
    function useOriginal(file) {
      var type = String(file.type || '');
      var name = String(file.name || '');
      var usable = UPLOADABLE_TYPE.test(type) || (!type && UPLOADABLE_NAME.test(name));
      if (!usable) return fail('unsupported');
      if (file.size > MAX_BYTES) return fail('too_large');
      if (file.size < MIN_BYTES) return fail('blank');
      return { ok: true, blob: file, width: 0, height: 0, original: true };
    }

    /* Decodare cu cronometru DUR de 5 s: pe telefoanele slabe o poză de 12 MP poate bloca
       decodarea la nesfârșit, iar clientul ar rămâne cu ecranul înghețat. */
    function decodeFile(file) {
      return new Promise(function (resolve) {
        var w = getWindow();
        var done = false;
        var tempUrl = null;
        var timer = ctx.timer(function () { finish({ kind: 'timeout' }); }, DECODE_TIMEOUT_MS);

        function finish(out) {
          if (done) return;
          done = true;
          ctx.clearTimer(timer);
          var at = pending.indexOf(closeNow);
          if (at > -1) pending.splice(at, 1);
          if (tempUrl) { revokeObjectUrl(tempUrl); tempUrl = null; }
          resolve(out);
        }
        function closeNow() { finish({ kind: 'error' }); }
        pending.push(closeNow);

        function viaImg() {
          var img = null;
          try { img = document.createElement('img'); } catch (e) { finish({ kind: 'error' }); return; }
          img.onload = function () {
            if (!img.naturalWidth) { finish({ kind: 'blank' }); return; }
            finish({ kind: 'ok', source: img });
          };
          img.onerror = function () { finish({ kind: 'error' }); };
          tempUrl = createObjectUrl(file);
          if (!tempUrl) { finish({ kind: 'error' }); return; }
          try { img.src = tempUrl; } catch (e) { finish({ kind: 'error' }); }
        }

        if (w && typeof w.createImageBitmap === 'function') {
          var bitmapPromise = null;
          try { bitmapPromise = w.createImageBitmap(file, bitmapOptions(file)); } catch (e) { bitmapPromise = null; }
          if (bitmapPromise && typeof bitmapPromise.then === 'function') {
            bitmapPromise.then(function (bitmap) {
              if (done) { closeBitmap(bitmap); return; }
              if (!bitmap || !bitmap.width) { closeBitmap(bitmap); finish({ kind: 'blank' }); return; }
              finish({ kind: 'ok', source: bitmap, bitmap: bitmap });
            }, function () {
              /* Unele browsere nu digeră un `File` sau opțiunea de orientare: mai avem o cale. */
              if (!done) viaImg();
            });
            return;
          }
        }
        viaImg();
      });
    }

    function fromFile(file) {
      if (!file) return Promise.resolve(fail('unsupported'));
      /* HEIC nici nu se încearcă: canvas-ul n-are ce decoda, iar clientul are nevoie de sfatul
         concret (Setări → Cameră → Formate → Cea mai compatibilă). */
      if (isHeic(file)) return Promise.resolve(fail('heic'));
      return decodeFile(file).then(function (decoded) {
        if (decoded.kind === 'ok') {
          return toJpeg(decoded.source, {}).then(function (out) {
            closeBitmap(decoded.bitmap);
            return validate(out);
          });
        }
        /* `timeout` = decodarea n-a apucat să se termine în 5 s; `blank` = capcana iOS. În
           amândouă cazurile fișierul poate fi perfect bun: îl trimitem ca atare, dacă trece de
           limitele Worker-ului. Doar o decodare care chiar a EȘUAT rămâne o eroare. */
        if (decoded.kind === 'blank' || decoded.kind === 'timeout') return useOriginal(file);
        return fail('decode');
      });
    }

    return {
      isHeic: isHeic,
      isInAppBrowser: isInAppBrowser,
      targetSize: targetSize,
      toJpeg: toJpeg,
      fromFile: fromFile,
      validate: validate,
      fail: fail,
      limits: { maxSide: MAX_SIDE, quality: JPEG_QUALITY, minBytes: MIN_BYTES, maxBytes: MAX_BYTES, decodeMs: DECODE_TIMEOUT_MS }
    };
  }

  /* ── camera ── camera live: getUserMedia cu rezervă pe constrângeri, atașare DUPĂ ce etapa e
     vizibilă, `loadedmetadata` cu cronometru dur, verificarea cadrului negru și oprirea
     track-urilor la orice ieșire. Mesajele sunt distincte pe fiecare `err.name`, iar fiecare
     eroare lasă la vedere calea cu încărcarea unei poze. */

  var METADATA_TIMEOUT_MS = 1500;
  var BLACK_FRAME_MS = 2200;
  /* Pătrat-ish și 1280: telefoanele dau fie 4:3, fie 16:9; `ideal` nu respinge nimic. */
  var CAMERA_CONSTRAINTS = {
    video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 1280 } },
    audio: false
  };

  function cameraErrorInfo(err) {
    var name = (err && err.name) || '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
      return { reason: 'denied', key: 'err_camera_denied', event: 'camera_denied' };
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
      return { reason: 'notfound', key: 'err_camera_notfound', event: 'camera_error' };
    }
    if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
      return { reason: 'busy', key: 'err_camera_busy', event: 'camera_error' };
    }
    return { reason: 'generic', key: 'err_camera_generic', event: 'camera_error' };
  }

  function mediaApi() {
    var w = getWindow();
    var nav = (w && w.navigator) || (typeof navigator !== 'undefined' ? navigator : null);
    var devices = nav && nav.mediaDevices;
    return devices && typeof devices.getUserMedia === 'function' ? devices : null;
  }

  /* Pe `http://` camera e blocată din start de browser: nu are rost s-o cerem (și nici să-i
     arătăm clientului o eroare de permisiune care nu e vina lui). */
  function secureContext() {
    var w = getWindow();
    if (!w) return false;
    if (typeof w.isSecureContext === 'boolean') return w.isSecureContext;
    var loc = w.location || {};
    return loc.protocol === 'https:' || loc.hostname === 'localhost' || loc.hostname === '127.0.0.1';
  }

  function stopTracks(stream) {
    if (!stream) return;
    var tracks = [];
    try { tracks = typeof stream.getTracks === 'function' ? stream.getTracks() : []; } catch (e) { tracks = []; }
    for (var i = 0; i < tracks.length; i++) {
      try { tracks[i].stop(); } catch (e) { /* track deja oprit */ }
    }
  }

  function createCamera(ctx, images) {
    var stream = null;
    var videoEl = null;
    /* SEMN DE GENERAȚIE. Între atingerea butonului și acceptarea permisiunii pot trece secunde, iar
       clientul apucă să plece de pe etapă (linkul „Încarcă o poză" e vizibil TOT timpul ăsta) sau
       să reintre. `stop()` și fiecare `start()` îl incrementează; o cerere care se întoarce cu un
       semn vechi nu mai are voie nici să atașeze fluxul, nici să-l lase pornit — altfel rămâne
       becul camerei aprins pe telefon, fără niciun buton care să-l stingă. */
    var gen = 0;

    /* „Răspunsul ăsta a rămas în urmă": altă generație, secțiune aruncată, sau am plecat de pe
       etapa de fotografiere. Se verifică DUPĂ fiecare await din `start`. */
    function stale(myGen) {
      return myGen !== gen || ctx.state.destroyed || ctx.state.stage !== 'capture';
    }

    function failure(err) {
      var info = cameraErrorInfo(err);
      return { ok: false, reason: info.reason, message: ctx.text(info.key), event: info.event };
    }

    function ask(devices, constraints) {
      try {
        return devices.getUserMedia(constraints).then(
          function (s) { return { stream: s }; },
          function (e) { return { error: e || {} }; }
        );
      } catch (e) {
        return Promise.resolve({ error: e || {} });
      }
    }

    function attach(video, media) {
      /* Fără astea, iOS deschide poza pe tot ecranul în loc s-o redea în pagină. Le punem și ca
         proprietăți, și ca atribute (markup-ul le are deja, dar elementul poate fi reînnoit). */
      try { video.playsInline = true; video.muted = true; video.autoplay = true; } catch (e) { /* video fals */ }
      try {
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.setAttribute('autoplay', '');
      } catch (e) { /* idem */ }
      try { video.srcObject = media; } catch (e) { /* browser foarte vechi */ }
    }

    /* Cronometru DUR: pe unele Android-uri `loadedmetadata` pur și simplu nu vine, dar fluxul e
       bun — mergem mai departe la play() după 1,5 s. */
    function waitMetadata(video) {
      return new Promise(function (resolve) {
        if (Number(video.readyState) >= 1) { resolve(); return; }
        var done = false;
        var timer = ctx.timer(function () {
          if (done) return;
          done = true;
          resolve();
        }, METADATA_TIMEOUT_MS);
        ctx.on(video, 'loadedmetadata', function () {
          if (done) return;
          done = true;
          ctx.clearTimer(timer);
          resolve();
        }, { once: true });
      });
    }

    /* `play()` respins (politica de autoplay, tab în fundal) nu are voie să rămână o promisiune
       neprinsă: îl tratăm ca eroare generică, cu aceeași ieșire — încarcă o poză. */
    function playVideo(video) {
      var started;
      try { started = video.play(); } catch (e) { return Promise.resolve(false); }
      if (!started || typeof started.then !== 'function') return Promise.resolve(true);
      return started.then(function () { return true; }, function () { return false; });
    }

    /* La 2,2 s: dacă suntem tot pe etapa de fotografiere și cadrul e încă 0×0, camera e ținută de
       altă aplicație (tipic pe Android). */
    function watchBlackFrame(onLateError, myGen) {
      ctx.timer(function () {
        if (stale(myGen)) return;
        if (!videoEl || Number(videoEl.videoWidth) !== 0) return;
        stop();
        if (typeof onLateError === 'function') {
          onLateError({ ok: false, reason: 'busy', message: ctx.text('err_camera_busy'), event: 'camera_error' });
        }
      }, BLACK_FRAME_MS);
    }

    /* Fluxul ăsta nu mai e al nimănui: îl desprindem (dacă apucase să fie atașat) și îi oprim
       FIECARE track. Nu e o eroare de arătat clientului — de-aia `reason: 'aborted'`. */
    function abandoned(media) {
      if (media && stream === media) stream = null;
      if (videoEl && media) {
        try { if (videoEl.srcObject === media) videoEl.srcObject = null; } catch (e) { /* video fals */ }
      }
      stopTracks(media);
      return { ok: false, reason: 'aborted', message: '', event: null };
    }

    function start(video, onLateError) {
      var devices = mediaApi();
      if (!video || !devices) { videoEl = video || null; return Promise.resolve(failure({ name: 'NotFoundError' })); }
      /* O pornire nouă scoate din joc orice cerere rămasă în zbor (re-intrare pe etapă, dublu-tap)
         ȘI oprește fluxul deja ATAȘAT — semnul de generație singur nu-l atinge pe acela, fiindcă
         cererea lui s-a terminat de mult; al doilea `start()` doar scria peste `stream`, iar primul
         rămânea aprins (becul camerei pe telefon, fără niciun buton care să-l stingă).
         `stop()` incrementează el însuși generația și curăță `srcObject` de pe elementul VECHI. */
      stop();
      videoEl = video;
      var myGen = gen;
      return ask(devices, CAMERA_CONSTRAINTS)
        .then(function (res) {
          if (stale(myGen)) return { late: true, stream: res.stream };
          /* Singura rezervă cerută de brief: camera nu poate da ce i-am cerut → cere orice. */
          if (res.error && res.error.name === 'OverconstrainedError') return ask(devices, { video: true });
          return res;
        })
        .then(function (res) {
          if (res.late || stale(myGen)) return abandoned(res.stream);
          if (res.error) return failure(res.error);
          stream = res.stream;
          attach(videoEl, stream);
          return waitMetadata(videoEl)
            .then(function () { return stale(myGen) ? false : playVideo(videoEl); })
            .then(function (played) {
              if (stale(myGen)) return abandoned(res.stream);
              if (!played) { stop(); return failure({ name: 'PlayFailed' }); }
              watchBlackFrame(onLateError, myGen);
              return { ok: true };
            });
        });
    }

    /* Cadrul curent al video-ului → JPEG, pe același drum ca pozele din galerie. */
    function capture(video) {
      return toJpeg(video || videoEl, {}).then(function (out) { return images.validate(out); });
    }

    function stop() {
      /* Și cererile care n-au răspuns încă: `stream` e tot `null` pentru ele, deci fără semnul de
         generație `stop()` n-ar avea ce opri, iar fluxul ar porni după plecarea clientului. */
      gen += 1;
      var media = stream;
      stream = null;
      stopTracks(media);
      if (videoEl) {
        try { videoEl.srcObject = null; } catch (e) { /* video fals */ }
      }
    }

    return {
      start: start,
      capture: capture,
      stop: stop,
      available: function () { return !!mediaApi() && secureContext(); },
      active: function () { return !!stream; },
      constraints: CAMERA_CONSTRAINTS,
      errorInfo: cameraErrorInfo
    };
  }

  /* ── stages ── mașina de stări: o singură etapă vizibilă (restul `hidden` + `inert`), focus pe
     titlul etapei, derularea cadrului (în centru; la ÎNCEPUTUL cardului pentru rezultat și eroare),
     evenimentul `ns:stage` și etapa de eroare. */

  /* Etapele la care vederea urcă la ÎNCEPUTUL cardului, nu în centrul lui (v1.1 §2). Rezultatul e
     înalt de câteva ecrane: „în centru" însemna mijlocul cardului, cu scorul rămas mult deasupra —
     exact ce a văzut Robert pe telefon. Eroarea urmează aceeași regulă, ca titlul ei să fie primul
     lucru din ecran. Locul pentru antetul lipit al temei îl lasă CSS-ul (`scroll-margin-top` pe
     `.ns-frame`, cu tokenul `--dl-hdr-h` al temei), deci aici nu măsurăm nimic. */
  var TOP_STAGES = { result: 1, error: 1 };

  function createStages(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var map = {};
    util.qsa(root, '[data-ns-stage]').forEach(function (el) { map[el.getAttribute('data-ns-stage')] = el; });

    function reducedMotion() {
      var w = getWindow();
      try { return !!(w && w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
    }

    /* Derulăm CADRUL, nu etapa: etapele au înălțimi diferite, iar cadrul are înălțime minimă —
       așa nu sare pagina între pași (cerința „no layout shift between stages"). */
    function frameOf(stage) {
      var node = stage;
      while (node && node !== root) {
        if (node.classList && node.classList.contains('ns-frame')) return node;
        node = node.parentNode;
      }
      return stage.parentNode && typeof stage.parentNode.scrollIntoView === 'function' ? stage.parentNode : stage;
    }

    /* `quiet` = derularea o facem NOI imediat după: fără `preventScroll`, browserul ar sări întâi
       la titlu (pe loc), apoi ar porni și derularea noastră — două mișcări în loc de una. Un
       browser vechi ignoră pur și simplu argumentul. */
    function focusTitle(stage, quiet) {
      var title = util.qs(stage, '[data-ns-stage-title]');
      if (!title) return null;
      if (!title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
      try {
        if (typeof title.focus === 'function') {
          if (quiet) title.focus({ preventScroll: true });
          else title.focus();
        }
      } catch (e) { /* DOM fals */ }
      return title;
    }

    function show(name, options) {
      /* O promisiune care se rezolvă DUPĂ ce secțiunea a fost aruncată (editorul de teme, navigare)
         nu mai are voie să atingă DOM-ul desprins. Sarcinile 66–68: verificați `state.destroyed`
         după fiecare `await`. */
      if (state.destroyed) return null;
      var target = map[name];
      if (!target) return null;
      var opts = options || {};
      var from = state.stage;
      for (var key in map) {
        if (!Object.prototype.hasOwnProperty.call(map, key)) continue;
        if (key === name) {
          map[key].removeAttribute('hidden');
          map[key].removeAttribute('inert');
        } else {
          map[key].setAttribute('hidden', '');
          map[key].setAttribute('inert', '');
        }
      }
      state.stage = name;
      var scrolls = opts.scroll !== false;
      if (opts.focus !== false) focusTitle(target, scrolls);
      if (scrolls) {
        var frame = frameOf(target);
        /* `top: true` = începutul cardului și pentru o etapă care altfel stă în centru: butonul
           „Fă-ți și tu scanarea" (v1.1 §3a) aduce omul din josul paginii primite la începutul
           scanerului, nu la mijlocul lui. */
        var block = opts.top === true || util.own(TOP_STAGES, name) ? 'start' : 'center';
        try {
          if (frame && typeof frame.scrollIntoView === 'function') frame.scrollIntoView({ block: block, behavior: reducedMotion() ? 'auto' : 'smooth' });
        } catch (e) { /* DOM fals */ }
      }
      emit(root, 'ns:stage', { from: from, to: name });
      return target;
    }

    /* Starea unei scanări noi: răspunsuri, poze, token, rezultat. Consimțământul NU se șterge —
       o dată dat, nu-l mai cerem încă o dată la „Reia".
       ORDINEA CONTEAZĂ: întâi oprim ce e în zbor, apoi lăsăm cârligele `onReset` să vadă starea
       VECHE (acolo se revocă object URL-urile pozelor, se oprește camera, se golesc sloturile) și
       abia la final golim starea. Invers, cârligele ar primi un `state.photos` gol și fiecare
       object URL ar rămâne agățat în memorie. */
    function reset() {
      if (state.destroyed) return;
      ctx.abortAll();
      ctx.clearTimers();
      ctx.runReset();
      QUIZ_STEPS.forEach(function (step) { state.quiz[step] = null; });
      state.photos.length = 0;
      state.token = null;
      state.result = null;
      /* Orice pornire de la capăt iese din modul „vizitator" (v1.1 §3a). `fromShared` NU se
         șterge: spune de unde a venit omul, nu în ce stare e ecranul. */
      state.viewer = false;
    }

    /* Formularul „te anunțăm când e liber” are sens DOAR dacă există unde să ajungă adresa. Fără
       cheia publică și fără lista Klaviyo (starea de azi) nu pleacă nicio cerere, deci un formular
       ar fi o cutie poștală zidită: omul scrie, apasă, nu primește nimeni nimic. */
    function emailReady() {
      try {
        return !!(ctx.email && typeof ctx.email.configured === 'function' && ctx.email.configured());
      } catch (e) { return false; }
    }

    function setHidden(el, on) {
      if (!el) return;
      if (on) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    }

    function error(info) {
      if (state.destroyed) return null;
      var opts = info || {};
      var stage = map.error;
      if (!stage) return null;
      util.setText(util.qs(stage, '[data-ns-slot="error-title"]'), opts.title || ctx.text('error_title'));
      util.setText(util.qs(stage, '[data-ns-slot="error-message"]'), opts.message || '');
      var form = util.qs(stage, '[data-ns-form="overload"]');
      if (form) {
        if (opts.overload && emailReady()) form.removeAttribute('hidden');
        else form.setAttribute('hidden', '');
      }
      /* v1.4 — varianta CALMĂ („ai făcut deja scanările de azi"): fără butonul de reluare (s-ar lovi
         iar de limită), cu ora la care se reia și, dacă există un rezultat în sesiune, cu drumul
         înapoi la el. ORICE altă eroare pune etapa la loc, ca piesele limitei să nu rămână agățate. */
      var limit = opts.limit && typeof opts.limit === 'object' ? opts.limit : null;
      setHidden(util.qs(stage, '[data-ns-action="retry"]') || util.qs(stage, '[data-ns-action="retry-photos"]'), !!limit);
      var whenEl = util.qs(stage, '[data-ns-slot="limit-when"]');
      util.setText(whenEl, limit ? limit.when || '' : '');
      setHidden(whenEl, !(limit && limit.when));
      setHidden(util.qs(stage, '[data-ns-action="limit-result"]'), !(limit && limit.hasResult));
      var orderEl = util.qs(stage, '[data-ns-slot="limit-order"]');
      var orderShown = false;
      try {
        if (ctx.orderHelp && typeof ctx.orderHelp.renderLimit === 'function') orderShown = ctx.orderHelp.renderLimit(!!(limit && limit.hasResult));
      } catch (e) { orderShown = false; }
      setHidden(orderEl, !orderShown);
      return show('error', opts);
    }

    /* După un `?r=TOKEN` care nu s-a putut încărca, `r=` e încă în adresă. Fără curățarea de aici,
       scanarea nouă și-ar trimite evenimentele de pixel cu tokenul vechi în adresa paginii, iar la
       prima reîncărcare `tokenFromUrl()` ar câștiga în fața sesiunii și ar arăta iar eroarea veche. */
    ctx.actions.register('retry', function () {
      stripResultParam();
      reset();
      show('intro');
    });

    return {
      el: map,
      names: STAGE_NAMES,
      get: function (name) { return map[name] || null; },
      current: function () { return state.stage; },
      show: show,
      error: error,
      reset: reset,
      reducedMotion: reducedMotion
    };
  }

  /* ── intro ── poarta de consimțământ: butonul de start e blocat până la bifă ȘI un Worker
     configurat; la PRIMA bifă trimitem `consent_accepted` + `ScanConsent`, o singură dată. */

  function initIntro(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var box = util.qs(root, '[data-ns-consent]');
    var startBtn = util.qs(root, '[data-ns-action="start"]');
    var note = util.qs(root, '[data-ns-slot="not-configured"]');
    var hint = util.qs(root, '[data-ns-slot="consent-hint"]');
    var hintReady = util.qs(root, '[data-ns-slot="consent-ready"]');
    var hintRow = util.qs(root, '[data-ns-slot="consent-hints"]');

    /* Config necitibil = nu știm niciun text și nicio setare: etapa intro rămâne pe ecran (ca să
       nu dispară secțiunea din pagină), dar scanarea NU poate porni, oricâte bife ar pune
       clientul — altfel ar merge mai departe cu toate etichetele goale. */
    function ready() { return !!state.consent && !!ctx.workerUrl && !state.configBroken; }

    /* Un buton care nu face nimic, fără nicio explicație, e cel mai prost lucru pe care îl poate
       întâlni un vizitator. Cât timp secțiunea n-are Worker (sau are un config ilizibil), sub
       butonul stins apare textul `err_not_configured` — ca TEXT, dintr-o setare, niciodată dintr-un
       literal din JS. Fără text în setări nu inventăm nimic: nota rămâne ascunsă. */
    function syncNote() {
      if (!note) return;
      /* Doar motivele de CONFIGURARE, nu bifa: fără bifă butonul e stins pentru că așa trebuie, și
         clientul vede chiar caseta de lângă. (Cu un config ilizibil n-avem nici texte, deci
         `ctx.text` întoarce '' și nota rămâne ascunsă — n-avem ce spune.) */
      var unusable = !ctx.workerUrl || state.configBroken;
      var message = unusable ? ctx.text('err_not_configured') : '';
      if (message) {
        util.setText(note, message);
        note.removeAttribute('hidden');
      } else {
        util.setText(note, '');
        note.setAttribute('hidden', '');
      }
    }

    /* Rândul de sub buton: cât lipsește bifa spune DE CE e stins butonul (`intro_hint_consent`),
       după bifă confirmă în ACELAȘI loc (`intro_hint_ready`) — nimic de sub buton nu sare și nu
       rămâne un gol. Ambele texte sunt ale setărilor, scrise de Liquid în markup; aici doar se
       comută. Cu un scaner nepornit tac amândouă: vorbește nota de mai sus, iar două explicații una
       sub alta ar încurca. Butonul poartă îndemnul ca descriere doar cât e pe ecran (un element
       ascuns, dar referit prin `aria-describedby`, ar fi citit în continuare). */
    function setShown(el, on) {
      if (!el) return;
      if (on) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    }
    function syncHint() {
      var unusable = !ctx.workerUrl || state.configBroken;
      var needsConsent = !state.consent && !unusable;
      /* Rândul întreg dispare doar când scanerul nu e pornit; altfel cel ascuns dintre cele două
         își păstrează locul din CSS, ca la bifă să nu sară nimic. */
      setShown(hintRow, !unusable);
      setShown(hint, needsConsent);
      setShown(hintReady, ready());
      if (!startBtn || !hint) return;
      var id = hint.getAttribute('id');
      if (id && needsConsent) startBtn.setAttribute('aria-describedby', id);
      else startBtn.removeAttribute('aria-describedby');
    }

    function sync() {
      if (box) box.checked = !!state.consent;
      syncNote();
      syncHint();
      if (!startBtn) return;
      if (ready()) startBtn.removeAttribute('disabled');
      else startBtn.setAttribute('disabled', '');
    }

    ctx.actions.registerChange('data-ns-consent', function (ev, el) {
      state.consent = !!(el && el.checked);
      sync();
      if (state.configBroken) return; /* nici pâlnie, nici pixeli pentru o secțiune neconfigurată */
      if (!state.consent || state.consentReported) return;
      state.consentReported = true;
      ctx.api.event('consent_accepted');
      ctx.track.event('ScanConsent');
    });

    ctx.actions.register('start', function () {
      if (!ready()) return;
      if (state.stage !== 'intro') return; /* a doua apăsare nu repornește nimic */
      /* Fără chestionar: direct la poze. Etapa `quiz` nu se arată niciodată, deci nici „Înapoi"-ul
         și indicatorul ei de pas; răspunsurile rămân `null`. */
      if (!quizEnabled(ctx.config)) { ctx.stages.show('capture'); return; }
      ctx.quiz.start();
    });

    ctx.onReset(sync);

    /* Browserul poate păstra bifa la reîncărcare/„înapoi"; pornim din ce e în pagină. */
    if (box && box.checked) state.consent = true;
    sync();

    var video = introVideo(ctx);

    return { sync: sync, ready: ready, video: video };
  }

  /* ── videoul din cardul bleumarin (2026-09-24) ──
     Același clip ca pe Acasă (`hero-scan-tall.mp4`). Markup-ul NU are `src`: fișierul se cere abia
     când cardul chiar e pe ecran (IntersectionObserver), deci o pagină deschisă și lăsată jos nu
     trage 0,8 MB degeaba. La „mișcare redusă" sau „economie de date" nu se cere NICIODATĂ — rămâne
     posterul (o poză de 36 KB, cu dimensiuni fixe în markup: nimic nu sare când apare). Clipul
     stă peste poster și apare lin doar când chiar rulează (`is-playing`), deci nici cadru negru.
     Pleacă de pe ecran (sau omul trece la chestionar) → pauză; revine → pornește din nou. */
  function introVideo(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var video = util.qs(root, '[data-ns-intro-video]');
    var media = video ? closestAttr(video, 'data-ns-intro-media', root) || video : null;
    var loaded = false;
    var inView = false;
    var observer = null;

    function allowed() {
      if (!video) return false;
      if (ctx.stages && typeof ctx.stages.reducedMotion === 'function' && ctx.stages.reducedMotion()) return false;
      try {
        var w = getWindow();
        var conn = w && w.navigator && w.navigator.connection;
        if (conn && conn.saveData) return false;
      } catch (e) { /* fără Network Information API */ }
      return !!video.getAttribute('data-src');
    }

    function load() {
      if (loaded || !allowed()) return false;
      loaded = true;
      /* doar atributul `muted` nu ajunge pentru politica de redare automată odată ce `src` vine din JS */
      try { video.muted = true; } catch (e) { /* DOM fals */ }
      video.setAttribute('src', video.getAttribute('data-src'));
      try { if (typeof video.load === 'function') video.load(); } catch (e) { /* idem */ }
      return true;
    }

    function play() {
      if (!inView || state.stage !== 'intro' || state.destroyed) return;
      if (!loaded && !load()) return;
      try {
        var run = typeof video.play === 'function' ? video.play() : null;
        if (run && typeof run.catch === 'function') run.catch(function () { /* redarea blocată: rămâne posterul */ });
      } catch (e) { /* idem */ }
    }

    function pause() {
      try { if (video && typeof video.pause === 'function' && !video.paused) video.pause(); } catch (e) { /* DOM fals */ }
    }

    if (!video) return { loaded: function () { return false; } };

    ctx.on(video, 'playing', function () { if (video.classList) video.classList.add('is-playing'); });
    ctx.on(root, 'ns:stage', function () { if (state.stage === 'intro') play(); else pause(); });

    var w = getWindow();
    var IO = w && w.IntersectionObserver;
    if (typeof IO === 'function') {
      try {
        observer = new IO(function (entries) {
          for (var i = 0; i < entries.length; i++) inView = !!entries[i].isIntersecting;
          if (inView) play(); else pause();
        }, { rootMargin: '120px 0px' });
        observer.observe(media);
      } catch (e) { observer = null; }
    }
    /* Fără IntersectionObserver: cardul e sus, pe primul ecran — pornim direct. */
    if (!observer) { inView = true; play(); }

    ctx.onTeardown(function () {
      if (observer) { try { observer.disconnect(); } catch (e) { /* deja oprit */ } }
      observer = null;
      pause();
    });

    return { loaded: function () { return loaded; }, play: play, pause: pause };
  }

  /* ── quiz ── trei întrebări, una pe ecran, fără sărit: fiecare atingere salvează răspunsul și
     avansează; „Înapoi" apare de la a doua întrebare (de la prima duce în intro). */

  function initQuiz(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var steps = QUIZ_STEPS.map(function (name) {
      return { name: name, el: util.qs(root, '[data-ns-quiz-step="' + name + '"]') };
    }).filter(function (step) { return !!step.el; });
    var backBtn = util.qs(root, '[data-ns-action="quiz-back"]');
    var progress = util.qs(root, '[data-ns-slot="quiz-progress"]');
    var index = 0; /* pe instanță, în closure — nu la nivel de modul */

    function focusStep() {
      var step = steps[index];
      if (!step) return;
      var title = util.qs(step.el, '[data-ns-stage-title]') || util.qs(step.el, 'legend');
      if (!title) return;
      if (!title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
      try { if (typeof title.focus === 'function') title.focus(); } catch (e) { /* DOM fals */ }
    }

    function render(focus) {
      steps.forEach(function (step, i) {
        if (i === index) step.el.removeAttribute('hidden');
        else step.el.setAttribute('hidden', '');
      });
      if (progress) util.setText(progress, util.fmt(ctx.text('quiz_progress'), { i: index + 1, n: steps.length }));
      if (backBtn) {
        if (index > 0) backBtn.removeAttribute('hidden');
        else backBtn.setAttribute('hidden', '');
      }
      if (focus) focusStep();
    }

    function mark(step, value) {
      util.qsa(step.el, '[data-ns-quiz]').forEach(function (btn) {
        btn.classList[btn.getAttribute('data-ns-value') === value ? 'add' : 'remove']('is-selected');
      });
    }

    function start() {
      index = 0;
      QUIZ_STEPS.forEach(function (name) { state.quiz[name] = null; });
      steps.forEach(function (step) { mark(step, null); });
      render(false);
      ctx.stages.show('quiz');
    }

    function pick(name, value) {
      if (!value) return false;
      var current = steps[index];
      if (!current || current.name !== name) return false; /* răspuns la o întrebare care nu e la rând */
      state.quiz[name] = value;
      mark(current, value);
      if (index < steps.length - 1) {
        index += 1;
        render(true);
        return true;
      }
      ctx.stages.show('capture'); /* sarcina 65 preia de aici */
      return true;
    }

    function back() {
      if (index === 0) {
        ctx.stages.show('intro');
        return;
      }
      index -= 1;
      state.quiz[steps[index].name] = null;
      mark(steps[index], null);
      render(true);
    }

    ctx.actions.register('quiz-pick', function (ev, el) {
      pick(el.getAttribute('data-ns-quiz'), el.getAttribute('data-ns-value'));
    });
    ctx.actions.register('quiz-back', function () { back(); });
    ctx.onReset(function () { index = 0; render(false); });

    render(false);

    return { start: start, back: back, pick: pick, step: function () { return index; }, steps: steps };
  }

  /* ── capture + review ── etapa de fotografiere (amândouă căile) și etapa de verificare.

     Calea cu INPUT DE FIȘIER e cea principală: 90% din trafic vine din Facebook/Instagram, unde
     `<input type="file" capture="environment">` deschide camera nativă a telefonului. Calea cu
     camera live e pentru restul (Safari, Chrome, Samsung Internet pe https).

     Pozele trăiesc DOAR în memorie: un `Blob` și un `blob:` object URL, revocate la „Reia
     pozele", la resetare și la teardown. Nimic în localStorage, nimic în pâlnie, nimic în pixeli. */

  /* Geometria cadrului, pe forma unghiului (viewBox-ul suprapunerii e 0 0 320 320). Stă aici, nu
     în CSS, ca formele să se poată schimba dintr-un singur loc. */
  var OVERLAY_SHAPES = {
    rounded: { x: 24, y: 52, width: 272, height: 216, rx: 28, ry: 28 },
    wide: { x: 12, y: 86, width: 296, height: 148, rx: 24, ry: 24 },
    close: { x: 68, y: 40, width: 184, height: 240, rx: 40, ry: 40 }
  };

  function initCapture(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var config = ctx.config || {};
    var els = {
      stage: util.qs(root, '[data-ns-stage="capture"]'),
      camera: util.qs(root, '[data-ns-camera]'),
      video: util.qs(root, '[data-ns-video]'),
      overlay: util.qs(root, '[data-ns-slot="overlay"]'),
      rect: util.qs(root, '[data-ns-overlay-rect]'),
      step: util.qs(root, '[data-ns-slot="capture-step"]'),
      instruction: util.qs(root, '[data-ns-slot="capture-instruction"]'),
      error: util.qs(root, '[data-ns-slot="capture-error"]'),
      dots: util.qsa(root, '[data-ns-dot]'),
      shutter: util.qs(root, '[data-ns-action="shutter"]'),
      take: util.qs(root, '[data-ns-action="take"]'),
      upload: util.qs(root, '[data-ns-action="upload"]'),
      input: util.qs(root, '[data-ns-file]'),
      thumbs: util.qs(root, '[data-ns-slot="thumbs"]')
    };
    var mode = 'file';   /* 'file' = input de fișier (calea principală) | 'camera' = flux live */
    var busy = false;    /* o singură poză în lucru: dublu-tap pe declanșator nu face două poze */
    var urls = [];       /* TOATE object URL-urile create, ca să nu scape niciunul nerevocat */
    /* SEMN DE GENERAȚIE pentru pozele în lucru: `stages.reset()` (butonul „Încearcă din nou",
       folosit și de sarcinile 66–68) NU schimbă etapa, deci o decodare pornită înainte s-ar
       termina liniștită și ar împinge poza scanării VECHI în scanarea nouă. „Reia pozele" la fel. */
    var run = 0;

    function angles() { return Array.isArray(config.angles) ? config.angles : []; }

    /* Câte poze cerem: setarea (1–3), dar niciodată mai multe decât unghiurile configurate. */
    function count() {
      var n = Math.round(Number(config.photoCount));
      if (!(n >= 1)) n = 1;
      if (n > 3) n = 3;
      var list = angles();
      if (list.length) n = Math.min(n, list.length);
      return n;
    }

    function show(el, on) {
      if (!el) return;
      if (on) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    }

    function setBusy(on) {
      [els.shutter, els.take].forEach(function (btn) {
        if (!btn) return;
        if (on) btn.setAttribute('disabled', '');
        else btn.removeAttribute('disabled');
      });
    }

    /* Pilula „1/2", instrucțiunea unghiului, forma cadrului și punctele de progres. Nimic nu
       apare/dispare între unghiuri (doar text și atribute), ca să nu sară pagina. */
    function render() {
      var total = count();
      var index = Math.min(state.photos.length, total - 1);
      var angle = angles()[index] || {};
      util.setText(els.step, util.fmt(ctx.text('capture_step'), { i: index + 1, n: total }));
      util.setText(els.instruction, angle.instruction || '');

      var shape = util.own(OVERLAY_SHAPES, angle.shape) ? angle.shape : 'rounded';
      if (els.overlay) els.overlay.setAttribute('data-ns-shape', shape);
      if (els.rect) {
        var geometry = util.own(OVERLAY_SHAPES, shape);
        for (var key in geometry) {
          if (Object.prototype.hasOwnProperty.call(geometry, key)) els.rect.setAttribute(key, String(geometry[key]));
        }
      }

      els.dots.forEach(function (dot, i) {
        if (i >= total) { dot.setAttribute('hidden', ''); return; }
        dot.removeAttribute('hidden');
        dot.classList.toggle('is-done', i < state.photos.length);
        dot.classList.toggle('is-active', i === index);
      });

      show(els.shutter, mode === 'camera');
      show(els.take, mode === 'file');
      if (els.camera) els.camera.setAttribute('data-ns-mode', mode);
    }

    function clearError() {
      if (!els.error) return;
      util.setText(els.error, '');
      els.error.setAttribute('hidden', '');
      if (els.stage) {
        els.stage.classList.remove('has-error');
        els.stage.classList.remove('has-camera-error');
      }
    }

    /* Orice eroare rămâne PE etapa de fotografiere (nu ducem clientul la ecranul de eroare
       general): mesajul e anunțat prin `role="alert"`, iar linkul de încărcare — care e oricum
       mereu vizibil — devine calea evidentă mai departe.
       ORDINEA CONTEAZĂ: întâi scoatem `hidden`, abia apoi punem textul. Un `role="alert"` citește
       ce APARE în el; textul pus într-un element încă ascuns poate trece nerostit.
       `fromCamera` separă CINE a greșit: o poză HEIC aleasă din galerie cât timp camera live merge
       nu are voie să stingă caseta și declanșatorul (CSS-ul leagă ascunderea de `has-camera-error`,
       iar `has-error` rămâne doar pentru stilul mesajului). */
    function showError(message, reason, fromCamera) {
      if (!els.error) return;
      els.error.removeAttribute('hidden');
      util.setText(els.error, message);
      if (els.stage) {
        els.stage.classList.add('has-error');
        if (fromCamera) els.stage.classList.add('has-camera-error');
      }
      if (reason) ctx.api.event('scan_failed', reason);
    }

    function pickMode() {
      var w = getWindow();
      var ua = (w && w.navigator && w.navigator.userAgent) || '';
      /* Într-un webview de aplicație, getUserMedia fie lipsește, fie pornește și rămâne neagră:
         mergem direct pe camera nativă a telefonului, prin input-ul de fișier. */
      if (ctx.images.isInAppBrowser(ua)) return 'file';
      if (!ctx.camera.available()) return 'file';
      return 'camera';
    }

    function enter() {
      busy = false;
      setBusy(false);
      clearError();
      mode = pickMode();
      render();
      if (mode !== 'camera') return;
      /* Etapa e DEJA vizibilă aici (`ns:stage` se emite la finalul lui `stages.show`), deci
         fluxul se atașează pe un `<video>` afișat — un `<video>` ascuns rămâne negru pe Chrome. */
      ctx.camera.start(els.video, onCameraError).then(function (res) {
        if (state.destroyed || state.stage !== 'capture') return;
        if (!res.ok && res.reason !== 'aborted') onCameraError(res);
      });
    }

    function onCameraError(res) {
      if (state.destroyed || state.stage !== 'capture') return;
      /* Camera live e moartă (refuzată, lipsă, ocupată): NU mai lăsăm declanșatorul rotund pe
         ecran. O apăsare pe el ar da o eroare de decodare pentru un flux care n-a existat
         niciodată. Trecem pe camera nativă a telefonului — același drum ca în browserele din
         aplicații — și așa rămânem, chiar dacă poza următoare reușește. */
      mode = 'file';
      render();
      showError(res.message, res.event, true);
    }

    function leave() {
      ctx.camera.stop();
      busy = false;
      setBusy(false);
    }

    function accept(out) {
      var index = state.photos.length;
      var url = createObjectUrl(out.blob);
      if (url) urls.push(url);
      state.photos.push({ blob: out.blob, url: url || '', angleIndex: index, width: out.width, height: out.height });
      if (state.photos.length >= count()) {
        ctx.camera.stop();
        ctx.stages.show('review');
        return;
      }
      render();
    }

    /* „Rezultatul ăsta e al scanării curente?" — după orice `await`. */
    function stale(myRun) {
      return myRun !== run || state.destroyed || state.stage !== 'capture';
    }

    function shoot() {
      if (busy || mode !== 'camera') return;
      busy = true;
      setBusy(true);
      clearError();
      var myRun = run;
      ctx.camera.capture(els.video).then(function (out) {
        busy = false;
        if (stale(myRun)) return;
        setBusy(false);
        if (!out.ok) { showError(out.message, 'camera_error'); return; }
        accept(out);
      });
    }

    /* `input.click()` TREBUIE chemat sincron, în chiar handlerul clicului: mutat după un `await`
       sau un `setTimeout`, iOS îl consideră neprovocat de utilizator și îl ignoră în tăcere. */
    function openFile(useCamera) {
      if (!els.input) return;
      if (useCamera) els.input.setAttribute('capture', 'environment');
      else els.input.removeAttribute('capture'); /* fără `capture` se deschide galeria */
      try { els.input.click(); } catch (e) { /* input lipsă */ }
    }

    function onFile(input) {
      if (!input) return;
      var files = input.files;
      /* Selecția multiplă e oprită din markup, dar dacă totuși vin mai multe, luăm prima. */
      var chosen = files && files.length ? files[0] : null;
      /* Golim input-ul IMEDIAT ce am luat fișierul: altfel realegerea aceluiași fișier (după o
         eroare, sau pentru al doilea unghi) nu mai declanșează niciun `change`. */
      try { input.value = ''; } catch (e) { /* unele browsere vechi refuză */ }
      if (!chosen || busy) return;
      busy = true;
      setBusy(true);
      clearError();
      var myRun = run;
      ctx.images.fromFile(chosen).then(function (out) {
        busy = false;
        /* Decodarea poate dura secunde pe un telefon slab: între timp scanarea poate fi resetată
           („Încearcă din nou") sau pozele reluate. Rezultatul rămas în urmă se aruncă — nimic de
           revocat, fiindcă object URL-ul poze se creează abia în `accept`. */
        if (stale(myRun)) return;
        setBusy(false);
        if (!out.ok) { showError(out.message, 'upload_invalid'); return; }
        accept(out);
      });
    }

    function revokeAll() {
      urls.splice(0).forEach(function (url) { revokeObjectUrl(url); });
    }

    function renderThumbs() {
      if (!els.thumbs) return;
      var html = '';
      for (var i = 0; i < state.photos.length; i++) {
        html += util.tpl('<img class="ns-thumb" src="{src}" alt="{alt}" loading="lazy">', {
          src: state.photos[i].url,
          alt: util.fmt(ctx.text('review_alt'), { i: i + 1 })
        });
      }
      els.thumbs.innerHTML = html;
    }

    function retake() {
      run += 1; /* o poză care încă se decodează aparține seriei vechi */
      revokeAll();
      state.photos.length = 0;
      if (els.thumbs) els.thumbs.innerHTML = '';
      busy = false;
      ctx.stages.show('capture'); /* `enter()` repornește camera pe calea cu flux live */
    }

    /* CÂRLIGUL SARCINII 66. Varianta curată: ascultă `ns:analyze` pe rădăcină (fără să
       reînregistrezi acțiunea, deci fără avertismentul de handler dublat). Dacă ai nevoie să
       înlocuiești chiar handlerul de clic, `ctx.actions.register('analyze', fn)` câștigă (ultimul
       înregistrat) și poți chema `ctx.review.analyze()` ca să refolosești pasul de aici. */
    function analyze() {
      if (!state.photos.length) return;
      ctx.emit('ns:analyze', { count: state.photos.length });
      ctx.stages.show('processing');
    }

    /* Ieșirea de pe etapă pe ORICE drum (verificare, eroare, întoarcere la întrebări, resetare,
       teardown) oprește track-urile: altfel rămâne becul camerei aprins pe telefon. */
    ctx.on(root, 'ns:stage', function (ev) {
      var detail = (ev && ev.detail) || {};
      if (detail.to === 'capture' && detail.from !== 'capture') enter();
      else if (detail.from === 'capture' && detail.to !== 'capture') leave();
      if (detail.to === 'review') renderThumbs();
    });

    /* PLECĂRI ȘI ÎNTOARCERI (M11). Pe telefon, clientul primește un apel, trece în altă aplicație
       sau trage bara de notificări. Atunci camera trebuie STINSĂ: altfel becul rămâne aprins, iar
       pe unele Android-uri fluxul nu se mai întoarce niciodată. La revenire — și numai dacă e tot
       pe etapa de fotografiere — etapa se reia CURAT: un `<video>` cu un flux mort rămâne negru,
       iar declanșatorul ar face poze goale. */
    function onAway() { leave(); }
    function onBack() {
      if (state.destroyed || state.stage !== 'capture') return;
      enter();
    }

    ctx.on(document, 'visibilitychange', function () {
      var w = getWindow();
      var hidden = !!(w && w.document && w.document.hidden);
      if (hidden) onAway();
      else onBack();
    });
    var captureWin = getWindow();
    if (captureWin) {
      ctx.on(captureWin, 'pagehide', onAway);
      /* Doar întoarcerea din bfcache: un `pageshow` obișnuit vine la încărcarea paginii, când
         `enter()` a rulat deja pe drumul normal. */
      ctx.on(captureWin, 'pageshow', function (ev) { if (ev && ev.persisted) onBack(); });
    }

    ctx.actions.register('shutter', function () { shoot(); });
    ctx.actions.register('take', function () { openFile(true); });
    ctx.actions.register('upload', function () { openFile(false); });
    ctx.actions.registerChange('data-ns-file', function (ev, el) { onFile(el); });
    ctx.actions.register('retake', function () { retake(); });
    ctx.actions.register('analyze', function () { analyze(); });

    ctx.onReset(function () {
      run += 1; /* scanare nouă: tot ce era în lucru pentru cea veche se aruncă */
      leave();
      revokeAll();
      if (els.thumbs) els.thumbs.innerHTML = '';
      clearError();
    });
    /* Și la teardown, ca rezervă: `cleanup()` rulează întâi cârligele de resetare, deci de obicei
       nu mai e nimic de făcut aici — dar dacă ordinea aia se schimbă vreodată, tot nu pierdem un
       flux pornit sau un object URL. */
    ctx.onTeardown(function () {
      ctx.camera.stop();
      revokeAll();
    });

    return {
      enter: enter,
      render: render,
      renderThumbs: renderThumbs,
      count: count,
      mode: function () { return mode; },
      shapes: OVERLAY_SHAPES,
      review: { analyze: analyze, retake: retake, render: renderThumbs }
    };
  }

  /* ── processing ── LISTA cu pașii analizei (cerința lui Robert, 20.09: „să aibă scris tot ce
     urmează să facă și să treacă cu bifă ce a făcut"), video opțional lazy, timeout 90 s (din
     `api`), `Lead` + `scan_started` trimise ÎNAINTE de încărcare, apoi drumul spre rezultat sau
     eroare. Tot ce ține de etapa de EROARE (inclusiv formularul „te anunțăm") stă tot aici: etapa
     de eroare e capătul firului pornit de aici.

     TEATRU CINSTIT, nu minciună. Pașii NU sunt raportați de Worker (n-avem flux de progres): ei
     sunt o prezentare a ceea ce face analiza, pe un program potrivit unei scanări adevărate de
     ~22 de secunde. Două reguli țin lucrurile oneste:
       · ULTIMUL pas nu se bifează niciodată singur — stă „în lucru" până vine chiar răspunsul,
         iar bara se apropie de 92 % și așteaptă acolo;
       · la un EȘEC totul îngheață pe loc: nu bifăm ceva ce nu s-a terminat.
     Când răspunsul vine devreme (mock-ul e instant), pașii rămași se bifează repede, fără să
     întârzie rezultatul cu mai mult de ~1,2 s. */

  var SCAN_MS = 22000;                      /* durata tipică a unei scanări adevărate */
  var STEP_MARKS = [8, 25, 45, 65, 82];     /* procentele la care se bifează pașii, pentru 6 pași */
  var TICK_MS = 100;                        /* cât de des recalculăm bara (un singur cronometru) */
  var BAR_MAX = 92;                         /* bara nu trece de-aici până nu sosește răspunsul */
  var EASE_MS = 6000;                       /* cât de „moale" se lipește bara de 92 % */
  var FAST_STEP_MS = 180;                   /* bifarea rapidă, când răspunsul a venit devreme */
  var FAST_TOTAL_MS = 1200;                 /* …dar niciodată mai mult de-atât în total */

  /* v1.4 — COADA. Când Worker-ul răspunde `wait:true`, aceleași poze pleacă din nou după
     `retry_after` secunde (ținut între 3 și 60, orice ar veni pe fir). Așteptarea ADUNATĂ a unei
     scanări nu trece de 180 s: după atât, omul primește drumul obișnuit de aglomerare. */
  var WAIT_MIN_S = 3;
  var WAIT_MAX_S = 60;
  var WAIT_MAX_TOTAL_S = 180;
  var WAIT_TICK_MS = 1000;
  var CLOCK_LOCALE = 'ro-RO';
  var CLOCK_ZONE = 'Europe/Bucharest';

  /* Pragurile pentru `n` treceri (adică `n + 1` pași). Cu 5 treceri ies exact procentele de mai
     sus; cu mai puține sau mai multe, aceeași curbă, întinsă pe câți pași a pus comerciantul. */
  function marksFor(n) {
    var out = [];
    var last = STEP_MARKS.length - 1;
    if (n <= 0) return out;
    if (n === 1) return [STEP_MARKS[Math.floor(last / 2)]];
    for (var i = 0; i < n; i++) {
      var at = i * last / (n - 1);
      var lo = Math.floor(at);
      var hi = Math.ceil(at);
      out.push(STEP_MARKS[lo] + (STEP_MARKS[hi] - STEP_MARKS[lo]) * (at - lo));
    }
    return out;
  }

  /* Motivul raportat în pâlnie, tradus în enumul Worker-ului (`api.reasons`): orice altceva ar fi
     lăsat afară de filtrul din `api.event`, deci pâlnia ar avea găuri fără să se vadă. */
  var FAIL_REASON = {
    model_error: 'model_error',
    overload: 'overload',
    rate_limited: 'rate_limited',
    timeout: 'timeout',
    network: 'network',
    server_error: 'server_error',
    bad_request: 'upload_invalid',   /* Worker-ul a refuzat pozele, nu s-a stricat el */
    consent_required: 'cancelled',   /* n-ar trebui să se întâmple: bifa e obligatorie la intro */
    not_configured: 'server_error'
  };

  /* Textul arătat clientului, pe chei din `config.texts` (`model_error` face excepție: acolo
     mesajul e scris de model, în română, despre CHIAR poza lui). */
  var FAIL_TEXT = {
    overload: 'err_overload',
    rate_limited: 'err_rate_limited',
    timeout: 'err_timeout',
    network: 'err_network',
    server_error: 'err_server',
    bad_request: 'err_bad_request',
    consent_required: 'err_consent',
    not_configured: 'err_not_configured'
  };

  /* Butonul de pe etapa de eroare duce ÎN MOD NORMAL la început (`retry`, înregistrat de `stages`).
     La o eroare de model, clientul n-are nevoie să reia întrebările — îi trebuie doar poze mai
     bune — așa că butonului i se schimbă acțiunea în `retry-photos`. Preferăm asta în locul unei
     a doua înregistrări pe numele `retry` (care ar scoate avertismentul de handler dublat): în DOM
     se vede negru pe alb unde duce butonul. */
  function setErrorRetry(ctx, toPhotos) {
    var stage = ctx.stages.get('error');
    if (!stage) return;
    var btn = util.qs(stage, '[data-ns-action="retry"]') || util.qs(stage, '[data-ns-action="retry-photos"]');
    if (btn) btn.setAttribute('data-ns-action', toPhotos ? 'retry-photos' : 'retry');
  }

  function createProcessing(ctx) {
    var state = ctx.state;
    var root = ctx.root;
    /* Singura regiune vie de pe etapă: anunță DOAR pasul care tocmai a devenit activ (o listă
       întreagă recitită la fiecare bifă ar fi de nesuportat la cititorul de ecran). */
    var msgEl = util.qs(root, '[data-ns-slot="processing-message"]');
    var stepsEl = util.qs(root, '[data-ns-slot="processing-steps"]');
    var sizerEl = util.qs(root, '[data-ns-slot="processing-sizer"]');
    var stripEl = util.qs(root, '[data-ns-slot="processing-photos"]');
    var fillEl = util.qs(root, '[data-ns-slot="processing-fill"]');
    var pctEl = util.qs(root, '[data-ns-slot="processing-percent"]');
    var barEl = util.qs(root, '[data-ns-progress]');
    var videoEl = util.qs(root, '[data-ns-processing-video]');
    var noteEl = util.qs(root, '[data-ns-slot="processing-note"]');
    var waitEl = util.qs(root, '[data-ns-slot="processing-wait"]');
    var waitInEl = util.qs(root, '[data-ns-slot="processing-wait-in"]');
    var waitTimer = 0;       /* numărătoarea până la reîncercare (v1.4) */
    var waited = 0;          /* câte secunde de așteptare a adunat scanarea asta */
    var queuedTracked = false;
    var ticker = 0;          /* cronometrul listei */
    var fast = 0;            /* cronometrul bifării rapide */
    var elapsed = 0;         /* cât ține așteptarea, numărat din cronometru (nu din ceas) */
    var ticked = 0;          /* câți pași sunt bifați */
    var steps = [];
    var marks = [];
    var busy = false;
    var overloadSending = false;
    /* SEMN DE GENERAȚIE (aceeași capcană ca la sarcina 65): `stages.reset()` NU schimbă etapa, deci
       garda `stage !== 'processing'` nu prinde o scanare care se termină DUPĂ „Reia". Fără el, un
       răspuns al scanării ABANDONATE ar fi scris tokenul în sesiune și ar fi randat rezultatul
       peste ecranul clientului. */
    var run_gen = 0;

    /* Pașii, din blocurile „Mesaj în timpul analizei". `detail` e OPȚIONAL: un bloc vechi (sau un
       config scris de mână, unde mesajul e doar un șir) rămâne valid. */
    function messages() {
      var list = (ctx.config.processing && ctx.config.processing.messages) || [];
      var out = [];
      for (var i = 0; i < list.length; i++) {
        var raw = list[i];
        var text = '';
        var detail = '';
        if (raw && typeof raw === 'object') {
          text = raw.text === null || raw.text === undefined ? '' : String(raw.text);
          detail = raw.detail === null || raw.detail === undefined ? '' : String(raw.detail);
        } else {
          text = raw === null || raw === undefined ? '' : String(raw);
        }
        if (text) out.push({ text: text, detail: detail });
      }
      return out;
    }

    function setAnalyzeBusy(on) {
      util.qsa(root, '[data-ns-action="analyze"]').forEach(function (btn) {
        btn.disabled = !!on;
        if (on) btn.setAttribute('disabled', '');
        else btn.removeAttribute('disabled');
      });
    }

    function stopTicker() {
      if (ticker) ctx.clearTimer(ticker);
      ticker = 0;
    }

    function stopFast() {
      if (fast) ctx.clearTimer(fast);
      fast = 0;
    }

    function stopWait() {
      if (waitTimer) ctx.clearTimer(waitTimer);
      waitTimer = 0;
    }

    /* Rândul „ești la rând" ia LOCUL rândului de liniștire („de obicei durează 20–30 de secunde" ar
       fi o minciună cât omul stă la coadă). Textul lui e static, din Liquid; aici doar îl arătăm. */
    function showWait(on) {
      if (waitEl) {
        var wasHidden = waitEl.hasAttribute('hidden');
        if (on) waitEl.removeAttribute('hidden');
        else waitEl.setAttribute('hidden', '');
        /* Pe un telefon scund rândul nou cade sub marginea ecranului: îl aducem la vedere O DATĂ, cu
           cea mai mică mișcare (`nearest`), ca omul să afle de ce durează. */
        if (on && wasHidden) {
          try {
            if (typeof waitEl.scrollIntoView === 'function') waitEl.scrollIntoView({ block: 'nearest', behavior: ctx.stages.reducedMotion() ? 'auto' : 'smooth' });
          } catch (e) { /* DOM fals */ }
        }
      }
      if (noteEl) {
        if (on && waitEl) noteEl.setAttribute('hidden', '');
        else noteEl.removeAttribute('hidden');
      }
      if (!on) util.setText(waitInEl, '');
    }

    function setCountdown(left) {
      util.setText(waitInEl, left > 0 ? util.fmt(ctx.text('processing_wait_in'), { s: left }) : '');
    }

    /* Un singur șablon de rând: bulină (cerc gol → inel care se rotește → bifă), titlul pasului și
       propoziția lui. Valorile vin din setări, deci trec prin `util.tpl` (care le escapează). */
    function renderSteps() {
      if (!stepsEl) return;
      var html = '';
      for (var i = 0; i < steps.length; i++) {
        html += util.tpl(
          '<li class="ns-proc-step" data-ns-step="{i}" data-ns-state="pending">'
            + '<span class="ns-proc-mark" aria-hidden="true">'
              + '<span class="ns-proc-ring"></span>'
              + '<svg class="ns-proc-check" viewBox="0 0 16 16" focusable="false" aria-hidden="true"><path d="M3.6 8.5l2.9 2.9 5.9-6.4"></path></svg>'
            + '</span>'
            + '<span class="ns-proc-body">'
              + '<span class="ns-proc-name">{name}</span>'
              + '<span class="ns-proc-detail"><span class="ns-proc-detail-in">{detail}</span></span>'
            + '</span>'
          + '</li>',
          { i: i, name: steps[i].text, detail: steps[i].detail }
        );
      }
      stepsEl.innerHTML = html;
      renderSizer();
    }

    /* CALAPODUL nevăzut. Explicația se vede doar la pasul în lucru, deci lista ar fi când mai
       înaltă, când mai scundă (o explicație ține un rând, alta trei; titlul îngroșat se poate rupe
       pe două rânduri). Calapodul stă în aceeași celulă cu lista și ține câte o COPIE a ei pentru
       fiecare pas posibil „în lucru", toate una peste alta: înălțimea e a celei mai înalte, deci
       cardul nu tresare. Copiile n-au `data-ns-step`: nu se bifează și nu se numără. */
    function renderSizer() {
      if (!sizerEl) return;
      var html = '';
      for (var v = 0; v < steps.length; v++) {
        html += '<div class="ns-proc-variant">';
        for (var i = 0; i < steps.length; i++) {
          html += util.tpl(
            '<div class="ns-proc-step" data-ns-state="{state}"><span class="ns-proc-mark"></span><span class="ns-proc-body">'
              + '<span class="ns-proc-name">{name}</span>'
              + '<span class="ns-proc-detail"><span class="ns-proc-detail-in">{detail}</span></span>'
            + '</span></div>',
            { state: i === v ? 'active' : 'pending', name: steps[i].text, detail: steps[i].detail }
          );
        }
        html += '</div>';
      }
      sizerEl.innerHTML = html;
    }

    function applyStates() {
      if (!stepsEl) return;
      util.qsa(stepsEl, '[data-ns-step]').forEach(function (el, i) {
        el.setAttribute('data-ns-state', i < ticked ? 'done' : (i === ticked ? 'active' : 'pending'));
      });
    }

    function announce(index) {
      util.setText(msgEl, steps[index] ? steps[index].text : '');
    }

    function setProgress(value) {
      var p = Math.max(0, Math.min(100, Math.round(value)));
      if (fillEl && fillEl.style) fillEl.style.width = p + '%';
      if (barEl) barEl.setAttribute('aria-valuenow', String(p));
      util.setText(pctEl, util.fmt(ctx.text('processing_percent'), { p: p }));
    }

    /* La „mișcare redusă" bara sare din prag în prag (cerința de accesibilitate); altfel crește
       liniar până la ultimul prag, apoi se lipește moale de 92 %. */
    function barPercent() {
      if (ctx.stages.reducedMotion()) {
        if (!marks.length) return 0;
        if (ticked >= marks.length) return BAR_MAX;
        return ticked > 0 ? marks[ticked - 1] : 0;
      }
      var top = marks.length ? marks[marks.length - 1] : STEP_MARKS[STEP_MARKS.length - 1];
      var until = SCAN_MS * top / 100;
      if (elapsed <= until) return elapsed / SCAN_MS * 100;
      return BAR_MAX - (BAR_MAX - top) * Math.exp(-(elapsed - until) / EASE_MS);
    }

    function tick() {
      ticker = 0;
      if (state.destroyed || state.stage !== 'processing') return;
      elapsed += TICK_MS;
      var reached = 0;
      for (var i = 0; i < marks.length; i++) {
        if (elapsed >= SCAN_MS * marks[i] / 100) reached = i + 1;
      }
      if (reached > ticked) {
        ticked = reached;
        applyStates();
        announce(ticked);
      }
      setProgress(barPercent());
      ticker = ctx.timer(tick, TICK_MS);
    }

    /* Miniaturile sunt CHIAR pozele omului: object URL-uri deja făcute de modulul de fotografiere,
       ținute în memorie. Nu se încarcă și nu pleacă nimic nou — doar le arătăm cât se așteaptă. */
    function renderPhotos(hasVideo) {
      if (!stripEl) return;
      stripEl.innerHTML = '';
      stripEl.setAttribute('hidden', '');
      if (hasVideo) return;
      var html = '';
      for (var i = 0; i < state.photos.length; i++) {
        var photo = state.photos[i];
        if (!photo || !photo.url) continue;
        html += util.tpl(
          '<span class="ns-proc-photo"><img src="{src}" alt="{alt}"><span class="ns-proc-sweep" aria-hidden="true"></span></span>',
          { src: photo.url, alt: util.fmt(ctx.text('review_alt'), { i: i + 1 }) }
        );
      }
      if (!html) return;
      stripEl.innerHTML = html;
      stripEl.removeAttribute('hidden');
    }

    /* La intrarea pe etapă. `src`-ul video-ului se pune ABIA aici: pus în markup, un fișier de
       câteva MB s-ar descărca la fiecare încărcare a paginii, pentru un ecran pe care mulți nu
       ajung niciodată. */
    function enter() {
      stopTicker();
      stopFast();
      stopWait();
      showWait(false);
      elapsed = 0;
      ticked = 0;
      steps = messages();
      marks = marksFor(steps.length - 1);
      renderSteps();
      applyStates();
      announce(0);
      setProgress(0);

      /* Prin aceeași poartă ca imaginile produselor: „vine din setări" nu înseamnă „e o adresă".
         `javascript:` scris acolo ar fi un link executabil, iar `//alt-site` ar duce clientul de
         pe magazin. Rămân doar căile de pe magazin și http(s). */
      var url = safeUrl((ctx.config.processing && ctx.config.processing.videoUrl) || '');
      var hasVideo = !!(videoEl && url);
      /* Clipul comerciantului ia locul benzii de miniaturi: două lucruri care se mișcă, unul peste
         altul, ar fi zgomot, nu liniște. */
      renderPhotos(hasVideo);
      if (steps.length > 1) ticker = ctx.timer(tick, TICK_MS);
      if (!hasVideo) return;
      videoEl.setAttribute('src', String(url));
      videoEl.removeAttribute('hidden');
      var started;
      try { started = videoEl.play(); } catch (e) { started = null; }
      /* autoplay blocat de browser = ecran fără video, nu o promisiune neprinsă în consolă */
      if (started && typeof started.then === 'function') started.then(null, function () { /* fără video */ });
    }

    function leave() {
      stopTicker();
      stopFast();
      stopWait();
      showWait(false);
      if (!videoEl) return;
      videoEl.removeAttribute('src');
      videoEl.setAttribute('hidden', '');
      try { if (typeof videoEl.load === 'function') videoEl.load(); } catch (e) { /* video fals */ }
    }

    /* Răspunsul a venit înaintea programului (mock-ul e instant, o scanare norocoasă durează 8 s).
       Bifăm repede pașii rămași — dar niciodată mai mult de ~1,2 s în total, ca omul să vadă
       lista terminându-se, nu să aștepte degeaba. La „mișcare redusă" nu întârziem deloc. */
    function fastForward(finish) {
      stopTicker();
      stopFast();
      var myGen = run_gen;
      function over() {
        ticked = steps.length;
        applyStates();
        setProgress(100);
        finish();
      }
      var left = steps.length - ticked;
      if (left <= 0 || ctx.stages.reducedMotion()) { over(); return; }
      var gap = Math.min(FAST_STEP_MS, Math.floor(FAST_TOTAL_MS / left));
      function step() {
        fast = 0;
        if (state.destroyed || myGen !== run_gen || state.stage !== 'processing') return;
        ticked += 1;
        applyStates();
        setProgress(ticked >= steps.length ? 100 : Math.max(barPercent(), BAR_MAX));
        if (ticked >= steps.length) { over(); return; }
        announce(ticked);
        fast = ctx.timer(step, gap);
      }
      fast = ctx.timer(step, gap);
    }

    function succeed(res) {
      var result = res.result;
      state.token = res.token || null;
      state.result = result;
      var completed = {
        severity: typeof result.severity === 'string' ? result.severity : '',
        score: Number(result.nail_health_score) || 0
      };
      /* v1.1 §3a: scanarea pornită din pagina „rezultat primit" — un boolean, fără nimic din link. */
      if (state.fromShared === true) completed.from_shared = true;
      ctx.track.event('NailScanCompleted', completed);
      ctx.result.remember(state.token);
      ctx.result.render(result, { token: state.token, fromToken: false });
      ctx.stages.show('result');
    }

    function route(res) {
      var kind = res.kind;
      /* Abandonul e al NOSTRU (resetare, teardown, plecare de pe etapă): clientul n-a pățit nimic
         și n-are ce vedea. Nu e nici un eșec de raportat în pâlnie. */
      if (kind === 'aborted') return;
      /* Reușita trece ÎNTÂI prin bifarea rapidă: lista se termină sub ochii omului, apoi apare
         rezultatul. Generația e verificată la fiecare pas al bifării (vezi `fastForward`). */
      if (kind === 'ok' && res.result) {
        var myGen = run_gen;
        fastForward(function () {
          if (state.destroyed || myGen !== run_gen || state.stage !== 'processing') return;
          succeed(res);
        });
        return;
      }

      /* Un eșec oprește TOT pe loc: nicio bifă în plus, fiindcă nimic nu s-a terminat. */
      stopTicker();
      stopFast();
      stopWait();
      if (kind === 'session_limit') { limited(res); return; }
      ctx.api.event('scan_failed', util.own(FAIL_REASON, kind) || 'server_error');
      if (kind === 'model_error') {
        /* Mesajul modelului e despre CHIAR poza clientului („unghiile au ojă", „e prea întuneric"):
           îl arătăm pe el, iar reluarea îl duce direct la fotografiere. */
        setErrorRetry(ctx, true);
        ctx.stages.error({ title: ctx.text('error_title'), message: res.message || ctx.text('err_decode') });
        return;
      }
      setErrorRetry(ctx, false);
      /* La aglomerare textul depinde de ce putem OFERI: cu Klaviyo configurat îi cerem adresa
         („te anunțăm"), fără el îi spunem doar să revină. Un text care promite un anunț, dar
         fără formular, e mai rău decât niciun text. */
      var emailReady = false;
      try { emailReady = !!(ctx.email && ctx.email.configured()); } catch (e) { emailReady = false; }
      var key = util.own(FAIL_TEXT, kind) || 'err_network';
      if (kind === 'overload' && !emailReady) key = 'err_overload_noform';
      ctx.stages.error({
        title: ctx.text('error_title'),
        message: ctx.text(key),
        overload: kind === 'overload'
      });
    }

    /* ── v1.4: limita pe browser ── */

    /* „mâine, după ora 03:00": pragul Worker-ului e miezul nopții UTC, dar omul trăiește pe ora
       României. Dacă pragul cade în ACEEAȘI zi calendaristică (cineva scanează la 1 noaptea), textul
       e cel cu „azi". O dată stricată sau un browser fără fusuri orare → nimic (niciodată
       „Invalid Date" pe ecran). */
    function whenText(iso, now) {
      try {
        var at = new Date(String(iso || ''));
        if (!iso || isNaN(at.getTime())) return '';
        var hour = at.toLocaleTimeString(CLOCK_LOCALE, { timeZone: CLOCK_ZONE, hour: '2-digit', minute: '2-digit', hour12: false });
        if (!/^\d{1,2}:\d{2}$/.test(hour)) return '';
        if (hour.length === 4) hour = '0' + hour;
        var dayOf = function (d) { return d.toLocaleDateString(CLOCK_LOCALE, { timeZone: CLOCK_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }); };
        var today = dayOf(now && typeof now.getTime === 'function' && !isNaN(now.getTime()) ? new Date(now.getTime()) : new Date());
        return util.fmt(ctx.text(dayOf(at) === today ? 'limit_retry_today' : 'limit_retry_tomorrow'), { ora: hour });
      } catch (e) { return ''; }
    }

    /* Ecranul calm. Nu e un eșec de raportat Worker-ului (el a refuzat, deci știe); spre pixeli
       pleacă doar FAPTUL. „Rezultatul tău e salvat mai jos" se spune numai când chiar există unul. */
    function limited(res) {
      var saved = null;
      try { saved = ctx.result.lastKnown(); } catch (e) { saved = null; }
      ctx.track.event('NailScanSessionLimit');
      var key = 'limit_text';
      if (res.limit === 'tries') key = 'limit_text_tries';
      else if (res.limit === 'ok' && saved) key = 'limit_text_ok';
      setErrorRetry(ctx, false);
      ctx.stages.error({
        title: ctx.text('limit_title') || ctx.text('error_title'),
        message: ctx.text(key) || ctx.text('limit_text'),
        limit: { when: whenText(res.retryAt, new Date()), hasResult: !!saved }
      });
    }

    ctx.actions.register('limit-result', function () {
      var saved = ctx.result.lastKnown();
      if (!saved) return;
      stripResultParam();
      ctx.stages.reset();
      ctx.result.load(saved, { focus: true });
    });

    /* ── v1.4: coada ── */

    function finishRun() {
      busy = false;
      setAnalyzeBusy(false);
    }

    /* O ÎNCERCARE: aceleași poze (tot numai din memorie), aceleași răspunsuri. Cronometrul de 90 s
       e al cererii (`api.scan`), deci repornește singur la fiecare încercare. */
    function attempt(myGen) {
      var blobs = [];
      for (var i = 0; i < state.photos.length; i++) blobs.push(state.photos[i].blob);

      ctx.api.scan({ consent: state.consent, quiz: state.quiz, auto: !quizEnabled(ctx.config), blobs: blobs }).then(function (res) {
        if (state.destroyed) { busy = false; return; }
        /* Scanarea asta nu mai e a nimănui: a intervenit un „Reia" (`stages.reset()`). Nici
           rezultatul, nici eșecul ei nu mai au voie să atingă ecranul — și nici LACĂTUL, care e
           deja al altcuiva (resetarea l-a eliberat, poate o scanare nouă l-a luat). */
        if (myGen !== run_gen) return;
        var out = res || {};
        /* Un rezultat sosit după ce clientul a plecat de pe etapă nu-l smulge înapoi. */
        if (state.stage !== 'processing') { finishRun(); return; }
        if (out.kind === 'wait') { queue(myGen, out); return; }
        finishRun();
        route(out);
      });
    }

    /* „Mai așteaptă": rămânem pe listă, cu bifele făcute; ultimul pas stă în lucru (nu se bifează
       niciodată singur), bara stă lângă 92 %. Lacătul de dublu-tap RĂMÂNE pus. */
    function queue(myGen, res) {
      var delay = Math.round(Number(res.retryAfter));
      if (!isFinite(delay)) delay = WAIT_MIN_S;
      delay = Math.max(WAIT_MIN_S, Math.min(WAIT_MAX_S, delay));
      if (waited + delay > WAIT_MAX_TOTAL_S) {
        finishRun();
        route({ kind: 'overload' });
        return;
      }
      waited += delay;
      if (!queuedTracked) {
        queuedTracked = true;
        /* Doar spre pixeli: enumul de motive al Worker-ului e închis, iar el știe deja că a pus omul la rând. */
        ctx.track.event('NailScanQueued');
        /* UN anunț politicos pentru cititorul de ecran; numărătoarea NU e într-o regiune vie. */
        util.setText(msgEl, ctx.text('processing_wait'));
      }
      showWait(true);
      var left = delay;
      setCountdown(left);
      function second() {
        waitTimer = 0;
        if (state.destroyed || myGen !== run_gen || state.stage !== 'processing') return;
        left -= 1;
        setCountdown(left);
        if (left > 0) { waitTimer = ctx.timer(second, WAIT_TICK_MS); return; }
        attempt(myGen);
      }
      stopWait();
      waitTimer = ctx.timer(second, WAIT_TICK_MS);
    }

    function run() {
      /* Lacăt SINCRON: pe telefon, două atingeri la câteva zeci de milisecunde una de alta ajung
         amândouă înainte de orice `await`. */
      if (busy || state.destroyed) return;
      busy = true;
      run_gen += 1;
      var myGen = run_gen;
      setAnalyzeBusy(true);
      /* Pe drumul obișnuit `ns:analyze` vine ÎNAINTE de `stages.show('processing')`, deci lista o
         pregătește `enter()`. Dar o scanare pornită când etapa e DEJA pe ecran (după `stages.reset()`,
         care nu schimbă etapa) n-ar mai trece prin `enter()` și ar moșteni bifele scanării vechi. */
      if (state.stage === 'processing') enter();

      /* Pâlnia ÎNAINTE de încărcare: dacă upload-ul pică (rețea proastă, poze mari), tot vrem să
         știm câți oameni au apăsat butonul. */
      ctx.track.event('Lead');
      ctx.api.event('scan_started');

      waited = 0;
      queuedTracked = false;
      attempt(myGen);
    }

    /* Reluarea după o eroare de model: păstrăm bifa ȘI răspunsurile (nu s-au schimbat), ștergem
       doar pozele — `stages.reset()` face curățenia grea (revocă object URL-urile, oprește camera,
       abandonează cererile), dar golește și răspunsurile, așa că le punem la loc. */
    ctx.actions.register('retry-photos', function () {
      var answers = { zone: state.quiz.zone, duration: state.quiz.duration, count: state.quiz.count };
      ctx.stages.reset();
      state.quiz.zone = answers.zone;
      state.quiz.duration = answers.duration;
      state.quiz.count = answers.count;
      setErrorRetry(ctx, false);
      ctx.stages.show('capture');
    });

    /* Formularul „te anunțăm când e liber" de pe etapa de eroare. Se limitează la FORMULARUL lui
       (pagina are trei formulare de email), iar confirmarea e aceeași indiferent ce răspunde
       integrarea: clientul n-are ce face cu „Klaviyo nu e configurat". */
    ctx.actions.registerSubmit('overload', function (ev, form) {
      /* Lacăt SINCRON, ca la scanare: „Enter" ținut apăsat sau un dublu-tap trimite trei
         `submit`-uri în aceeași clipă, iar butonul se dezactivează abia după validare — fără
         lacăt, omul s-ar abona de trei ori. Rămâne pus și după reușită (formularul e terminat). */
      if (overloadSending) return;
      var emailEl = util.qs(form, '[data-ns-email]');
      var consentEl = util.qs(form, '[data-ns-email-consent]');
      var statusEl = util.qs(form, '[data-ns-slot="overload-status"]');
      var button = util.qs(form, '[data-ns-action="email-submit"]');
      var value = String((emailEl && emailEl.value) || '').trim().toLowerCase();

      /* Greșelile de completare NU pun lacătul: clientul trebuie să poată corecta și retrimite. */
      if (!EMAIL_RE.test(value)) { util.setText(statusEl, ctx.text('email_invalid')); return; }
      if (!consentEl || consentEl.checked !== true) { util.setText(statusEl, ctx.text('email_consent_required')); return; }

      overloadSending = true;
      if (button) { button.disabled = true; button.setAttribute('disabled', ''); }

      /* CONFIRMAREA SE DĂ DOAR PE O REUȘITĂ ADEVĂRATĂ (runda finală de fix).
         Înainte, orice răspuns — inclusiv „Klaviyo nu e configurat" ori un 400 — scria „Gata,
         ți-am notat adresa". Omul pleca liniștit, convins că îl anunțăm, iar adresa lui nu ajunsese
         nicăieri. Acum e exact ca la formularul din rezultat: reușită → confirmare; orice altceva →
         `email_error`, lacătul se ridică și poate încerca din nou. */
      function finish(res) {
        if (state.destroyed) return;
        if (res && res.ok === true) {
          util.setText(statusEl, ctx.text('overload_success'));
          return;
        }
        overloadSending = false;
        util.setText(statusEl, ctx.text('email_error'));
        if (button) { button.disabled = false; button.removeAttribute('disabled'); }
      }

      var out = null;
      try { out = ctx.email.subscribe({ email: value, source: 'overload' }); } catch (e) { out = null; }
      if (out && typeof out.then === 'function') out.then(finish, function () { finish(null); });
      else finish(out);
    });

    /* Sarcina 65 emite `ns:analyze` ÎNAINTE de `stages.show('processing')`; ascultăm evenimentul,
       fără să reînregistrăm acțiunea `analyze` (varianta recomandată de raportul ei). */
    ctx.on(root, 'ns:analyze', function () { run(); });

    ctx.on(root, 'ns:stage', function (ev) {
      var detail = (ev && ev.detail) || {};
      if (detail.to === 'processing' && detail.from !== 'processing') enter();
      else if (detail.from === 'processing' && detail.to !== 'processing') leave();
    });

    /* „Reia" scoate din joc scanarea în zbor și deschide la loc formularul de anunțare. */
    ctx.onReset(function () {
      run_gen += 1;
      overloadSending = false;
      stopTicker();
      stopFast();
      stopWait();
      showWait(false);
      /* Scanarea abandonată nu-și mai eliberează singură lacătul (vezi `attempt`), iar una aflată
         la RÂND nici n-are o cerere în aer care s-o facă. */
      finishRun();
    });
    ctx.onTeardown(leave);

    return {
      run: run,
      enter: enter,
      leave: leave,
      messages: messages,
      steps: function () { return steps; },
      marks: function () { return marks; },
      busy: function () { return busy; },
      whenText: whenText,
      waitMaxTotal: WAIT_MAX_TOTAL_S,
      scanMs: SCAN_MS,
      tickMs: TICK_MS,
      reasons: FAIL_REASON
    };
  }

  /* ── result ── inel SVG cu scorul, cele două estimări de creștere, pozele cu repere + lupa,
     barele pe cele 7 criterii, rezumatul cu sfaturi și nota de specialist. Sloturile `plan`,
     `totals`, `cta`, `email`, `progress` și `actions` rămân GOALE (sarcinile 67/68): după randare
     se emite `ns:result`, ca ele să-și scrie partea lor.

     Rezultatul e intrare NEDE ÎNCREDERE chiar dacă Worker-ul l-a validat și reparat: fiecare șir
     care ajunge în markup trece prin `util.tpl` (care escapează valorile), iar textul simplu prin
     `util.setText`. */

  var RING_R = 70;
  var RING_BOX = 160;
  var RING_C = 2 * Math.PI * RING_R;
  var RING_DELAY_MS = 60;
  var ZOOMS = [2, 4, 6];
  var ADVICE_MAX = 5;
  var SESSION_TOKEN_KEY = 'ns:token';
  /* v1.4: ULTIMUL rezultat al filei, ținut și după „Scanează din nou" (care uită `ns:token`, ca o
     reîncărcare să nu readucă rezultatul vechi). Îl folosește doar ecranul de limită: omul care a
     făcut deja scanările zilei trebuie să aibă un drum înapoi la rezultatul lui. */
  var LAST_TOKEN_KEY = 'ns:last';
  var TOKEN_RE = /^[a-z0-9]{32}$/;
  var RESULT_SLOTS = ['hero', 'verdict', 'specialist-top', 'callouts', 'photos', 'bars', 'summary', 'specialist'];

  var SEVERITY_TEXT = { sanatos: 'stage_sanatos', usor: 'stage_usor', moderat: 'stage_moderat', avansat: 'stage_avansat' };
  var MARKER_SEVERITY_TEXT = { minor: 'sev_minor', moderat: 'sev_moderat', sever: 'sev_sever' };
  var ZONE_TEXT = { maini: 'quiz_zone_maini', picioare: 'quiz_zone_picioare' };
  var DIMENSIONS = ['culoare', 'grosime', 'suprafata', 'margini_fragilitate', 'aderenta', 'luciu', 'piele_cuticule'];
  var DIMENSION_TEXT = {
    culoare: 'dim_culoare', grosime: 'dim_grosime', suprafata: 'dim_suprafata',
    margini_fragilitate: 'dim_margini', aderenta: 'dim_aderenta', luciu: 'dim_luciu', piele_cuticule: 'dim_piele'
  };
  /* iconițele sunt clase bootstrap-icons (fontul e încărcat de layout-ul temei) */
  var DIMENSION_ICON = {
    culoare: 'bi-palette', grosime: 'bi-layers-half', suprafata: 'bi-grid-3x2-gap',
    margini_fragilitate: 'bi-scissors', aderenta: 'bi-link-45deg', luciu: 'bi-brightness-high', piele_cuticule: 'bi-droplet'
  };

  /* Presupunerea („Ce pare să fie", v1.1 §1) — vocabular ÎNCHIS, oglinda lui `IMPRESSIONS` din
     Worker (testul de paritate le compară, valoare cu valoare, în aceeași ordine). Pe ecran ajung
     DOAR textele comerciantului: setarea `impression_<valoare>` e titlul, `impression_<valoare>_text`
     e propoziția de sprijin. Orice altceva primit (câmp lipsă la un rezultat vechi, valoare
     inventată, nume de pe `Object.prototype`) devine `neclar`. */
  var IMPRESSIONS = ['ciuperca_probabila', 'ciuperca_posibila', 'fara_semne_ciuperca', 'alta_cauza_probabila', 'de_vazut_la_medic', 'neclar'];
  var IMPRESSION_UNCLEAR = 'neclar';
  var IMPRESSION_DOCTOR = 'de_vazut_la_medic';
  var IMPRESSION_SIGNS_MAX = 3;
  var IMPRESSION_KNOWN = {};
  IMPRESSIONS.forEach(function (value) { IMPRESSION_KNOWN[value] = true; });

  /* Valoarea NORMALIZATĂ a presupunerii: mereu una din `IMPRESSIONS`. */
  function impressionOf(result) {
    var raw = result && typeof result === 'object' ? result.impression : null;
    return typeof raw === 'string' && util.own(IMPRESSION_KNOWN, raw) === true ? raw : IMPRESSION_UNCLEAR;
  }

  /* „Are semne de ciupercă?" (2026-09-24) — oglinda lui `SIGNS_PRESENT` din Worker (testul de
     paritate). Răspunsul scurt din blocurile „Vestea bună / Vestea rea". Un rezultat fără câmp (salvat
     înainte, sau venit de la un Worker încă nepublicat) îl primește dedus din presupunere — exact
     regula Worker-ului; la „de văzut la medic" presupunerea nu spune nimic despre ciupercă → `neclar`. */
  var SIGNS_PRESENT = ['da', 'posibil', 'nu', 'neclar'];
  var SIGNS_UNCLEAR = 'neclar';
  var SIGNS_KNOWN = {};
  SIGNS_PRESENT.forEach(function (value) { SIGNS_KNOWN[value] = true; });
  var SIGNS_BY_IMPRESSION = {
    ciuperca_probabila: 'da', ciuperca_posibila: 'posibil', fara_semne_ciuperca: 'nu',
    alta_cauza_probabila: 'nu', de_vazut_la_medic: 'neclar', neclar: 'neclar'
  };

  function signsPresentOf(result, impression) {
    var raw = result && typeof result === 'object' ? result.signs_present : null;
    if (typeof raw === 'string' && util.own(SIGNS_KNOWN, raw) === true) return raw;
    return util.own(SIGNS_BY_IMPRESSION, impression) || SIGNS_UNCLEAR;
  }

  /* Ce veste bună și ce veste rea spunem (cheile textelor). DETERMINIST, din enumuri: nimic din textul
     liber al modelului nu ajunge aici. „De văzut la medic" bate tot: nicio liniștire lângă o trimitere
     la medic. La „nu" + `sanatos` vestea rea e că… nu prea avem una. */
  function newsKeys(result, impression, signs) {
    if (impression === IMPRESSION_DOCTOR) return { good: 'news_good_medic', bad: 'news_bad_medic', doctor: true };
    if (signs === 'da') return { good: 'news_good_da', bad: 'news_bad_da' };
    if (signs === 'posibil') return { good: 'news_good_posibil', bad: 'news_bad_posibil' };
    if (signs === 'nu') {
      var healthy = asText(result && result.severity) === 'sanatos';
      return { good: 'news_good_nu', bad: healthy ? 'news_bad_sanatos' : 'news_bad_nu', healthy: healthy };
    }
    return { good: 'news_good_neclar', bad: 'news_bad_neclar', unclear: true };
  }

  /* Cel mai bun și cel mai slab dintre cele 7 criterii (`null` = n-avem numere). La egalitate câștigă
     primul din ordinea fixă `DIMENSIONS` — același rezultat randat de două ori spune același lucru. */
  function dimensionExtremes(result) {
    var dims = result && result.dimensions && typeof result.dimensions === 'object' ? result.dimensions : null;
    if (!dims) return null;
    var best = null;
    var worst = null;
    for (var i = 0; i < DIMENSIONS.length; i++) {
      var value = wholeNumber(util.own(dims, DIMENSIONS[i]), 0, 100);
      if (value === null) continue;
      if (!best || value > best.value) best = { key: DIMENSIONS[i], value: value };
      if (!worst || value < worst.value) worst = { key: DIMENSIONS[i], value: value };
    }
    return best ? { best: best, worst: worst } : null;
  }

  /* Estimările de creștere, FIXE (v1.1 §5) — [min, max]: `weeks` = primele semne vizibile
     (săptămâni), `months` = unghie complet reînnoită (luni). Sursa: unghiile mâinii cresc
     ~3 mm pe lună, deci o unghie întreagă se schimbă în ~6 luni; ale piciorului cresc ~1–1,5 mm pe
     lună, deci 12–18 luni. O unghie mai afectată are mai mult de crescut „curat", de-aia intervalul
     urcă odată cu severitatea. Tabelul e copiat din specificația v1.1; `sanatos` lipsește dinadins. */
  function asText(value) {
    return value === null || value === undefined || typeof value === 'object' ? '' : String(value);
  }

  /* Scoate DOAR `r=` din interogare, prin `replaceState` (fără navigare): restul parametrilor
     paginii rămân cum erau, iar STAREA intrării din istoric se păstrează — altfel santinela
     ferestrei de ieșire (`{ns:'ns-exit'}`) ar fi ștearsă din intrarea pe care stăm.
     Folosit în două locuri: după o încărcare reușită prin `?r=TOKEN` (ca tokenul să nu mai fie în
     adresa paginii când pleacă evenimentele de pixel) și la „Testează din nou". */
  function stripResultParam() { return stripParams(['r']); }

  /* Același mecanism, pentru orice listă de parametri: `s=` (linkul primit, v1.1 §3a) pleacă din
     adresă abia când vizitatorul apasă „Fă-ți și tu scanarea". Fără niciun parametru de scos,
     istoricul NU e atins deloc. */
  function stripParams(names) {
    var w = getWindow();
    var loc = w && w.location;
    var hist = w && w.history;
    if (!loc || !hist || typeof hist.replaceState !== 'function') return false;
    var search = String(loc.search || '');
    var pairs = search.replace(/^\?/, '').split('&').filter(function (pair) { return pair !== ''; });
    var kept = pairs.filter(function (pair) { return names.indexOf(pair.split('=')[0]) < 0; });
    if (kept.length === pairs.length) return false;
    var next = String(loc.pathname || '') + (kept.length ? '?' + kept.join('&') : '') + String(loc.hash || '');
    try { hist.replaceState(hist.state === undefined ? null : hist.state, '', next || '.'); return true; } catch (e) { return false; }
  }

  /* Două texte care spun același lucru (fără să ne batem capul cu majuscule sau diacritice). */
  function sameWords(a, b) {
    function flat(v) {
      var s = String(v === null || v === undefined ? '' : v).toLowerCase();
      /* `normalize` lipsește pe browsere foarte vechi: acolo rămâne comparația simplă. */
      if (typeof s.normalize === 'function') s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
      return s.replace(/[^a-z0-9]+/g, ' ').trim();
    }
    var left = flat(a);
    return left !== '' && left === flat(b);
  }

  /* Un număr utilizabil, TĂIAT la capete (pentru bare și scor: 320 înseamnă „maxim", nu „stricat").
     `null` = nu e număr deloc. */
  function clampNumber(value, min, max) {
    var n;
    if (typeof value === 'number') n = value;
    else if (typeof value === 'string' && value.trim() !== '') n = Number(value);
    else return null;
    if (!isFinite(n)) return null;
    return Math.min(max, Math.max(min, n));
  }

  function wholeNumber(value, min, max) {
    var n = clampNumber(value, min, max);
    return n === null ? null : Math.round(n);
  }

  /* Un număr care trebuie să fie CHIAR în interval (pentru repere: un `x_pct` de 140 e un reper
     stricat, nu unul lipit de marginea din dreapta — îl sărim). */
  function exactNumber(value, min, max) {
    var n;
    if (typeof value === 'number') n = value;
    else if (typeof value === 'string' && value.trim() !== '') n = Number(value);
    else return null;
    if (!isFinite(n) || n < min || n > max) return null;
    return n;
  }

  /* Numere pentru markup: fără cozi de virgulă flotantă în atribute. */
  function num(value) {
    return String(Math.round(Number(value) * 100) / 100);
  }

  /* Un object URL e al nostru, dar tot îl trecem prin filtru înainte de `url("…")`: o ghilimea sau
     o paranteză scăpată acolo ar deschide o declarație CSS nouă. */
  function cssUrl(value) {
    return String(value === null || value === undefined ? '' : value).replace(/[^A-Za-z0-9:/._~%?#=&+-]/g, function (ch) {
      var code = ch.charCodeAt(0);
      return '%' + (code < 16 ? '0' : '') + code.toString(16).toUpperCase();
    });
  }

  /* „cod=Nume" pe linii, dintr-o setare de tip textarea: numele românești ale unghiilor și ale
     problemelor stau în setări, nu în cod (regula „niciun text vizibil în JS"). */
  function parseNameMap(text) {
    var out = {};
    var lines = String(text === null || text === undefined ? '' : text).split('\n');
    for (var i = 0; i < lines.length; i++) {
      var at = lines[i].indexOf('=');
      if (at < 1) continue;
      var key = lines[i].slice(0, at).trim();
      var value = lines[i].slice(at + 1).trim();
      if (key && value) out[key] = value;
    }
    return out;
  }

  /* ── inelul cu scor și barele, ca funcții de MODUL ──
     Două ecrane le desenează: rezultatul proprietarului și pagina „rezultat primit" (v1.1 §3a).
     Un singur cod, ca inelul să arate la fel în amândouă; ce diferă vine prin `opts`:
       `labelKey` — textul de sub scor („Scorul aspectului unghiilor" / „Scorul persoanei care…");
       `affected` — pastila „N unghii cu modificări" e DOAR a proprietarului (nu călătorește în link). */

  function dashOffset(score) {
    var n = clampNumber(score, 0, 100);
    if (n === null) n = 0;
    return RING_C * (1 - n / 100);
  }

  function renderScoreHero(ctx, el, result, opts) {
    if (!el) return;
    var o = opts || {};
    var state = ctx.state;
    var html = '';
    var score = wholeNumber(result.nail_health_score, 0, 100);
    var animate = !ctx.stages.reducedMotion();
    var target = dashOffset(score === null ? 0 : score);

    if (score !== null) {
      html += util.tpl(
        '<div class="ns-ring">' +
          '<svg viewBox="0 0 {box} {box}" aria-hidden="true" focusable="false">' +
            '<circle class="ns-ring-track" cx="{mid}" cy="{mid}" r="{r}"></circle>' +
            '<circle class="ns-ring-value" data-ns-ring cx="{mid}" cy="{mid}" r="{r}" stroke-dasharray="{dash}" stroke-dashoffset="{offset}"></circle>' +
          '</svg>' +
          '<strong class="ns-score" data-ns-score aria-label="{aria}">{score}</strong>' +
        '</div>' +
        '<p class="ns-score-label">{label}</p>',
        {
          box: RING_BOX, mid: RING_BOX / 2, r: RING_R,
          dash: num(RING_C), offset: num(animate ? RING_C : target),
          aria: util.fmt(ctx.text('score_aria'), { n: score }),
          score: score, label: ctx.text(o.labelKey || 'score_label')
        }
      );
    }

    /* `severity` de pe lista albă e și singura sursă a atributului: în `data-ns-severity` nu
       ajunge niciodată valoarea brută a modelului. */
    var severity = asText(result.severity);
    var severityKey = util.own(SEVERITY_TEXT, severity);
    if (severityKey) {
      html += util.tpl('<p class="ns-badge" data-ns-severity="{sev}">{label}</p>', { sev: severity, label: ctx.text(severityKey) });
    }

    var pills = '';
    var zoneKey = util.own(ZONE_TEXT, asText(result.zone));
    if (zoneKey) pills += util.tpl('<span class="ns-pill">{t}</span>', { t: ctx.text(zoneKey) });
    var affected = o.affected === false ? null : wholeNumber(result.affected_nails_count, 0, 99);
    if (affected !== null) {
      var key = affected === 0 ? 'affected_none' : (affected === 1 ? 'affected_one' : 'affected_many');
      pills += util.tpl('<span class="ns-pill">{t}</span>', { t: util.fmt(ctx.text(key), { n: affected }) });
    }
    if (pills) html += '<div class="ns-pills">' + pills + '</div>';

    el.innerHTML = html;

    /* Animația pornește dintr-un al doilea cadru, ca tranziția CSS să aibă de la ce pleca.
       Inelul e desenat de la început la dimensiunea finală, deci nu mișcă layout-ul. */
    if (!animate || score === null) return;
    var ring = util.qs(el, '[data-ns-ring]');
    if (!ring) return;
    ctx.timer(function () {
      if (state.destroyed) return;
      ring.setAttribute('stroke-dashoffset', num(target));
    }, RING_DELAY_MS);
  }

  function renderDimensionBars(ctx, el, result, titleKey) {
    if (!el) return;
    var dims = result && result.dimensions && typeof result.dimensions === 'object' ? result.dimensions : {};
    var html = util.tpl('<h3 class="ns-sub">{t}</h3>', { t: ctx.text(titleKey || 'bars_title') });
    for (var i = 0; i < DIMENSIONS.length; i++) {
      var key = DIMENSIONS[i];
      var value = wholeNumber(util.own(dims, key), 0, 100);
      var band = value === null ? '' : (value < 40 ? ' is-low' : (value < 70 ? ' is-mid' : ' is-high'));
      html += util.tpl(
        '<div class="ns-bar" data-ns-dim="{key}">' +
          '<p class="ns-bar-head"><span class="ns-bar-label"><i class="bi {icon}" aria-hidden="true"></i>{label}</span>' +
          '<span class="ns-bar-value">{value}</span></p>' +
          '<div class="ns-bar-track"><span class="ns-bar-fill{band}" style="width:{w}%"></span></div>' +
        '</div>',
        { key: key, icon: DIMENSION_ICON[key], label: ctx.text(DIMENSION_TEXT[key]), value: value === null ? '' : value, band: band, w: value === null ? 0 : value }
      );
    }
    el.innerHTML = html;
  }

  /* ── linkul „rezultat primit" (v1.1 §3a, hotărârea R2) ── AUTONOM: tot ce vede cine îl deschide
     stă în el, deci nu există nici cerere la Worker, nici rând în KV, nici expirare:

         ?s=v1.<scor>.<sev>.<zonă>.<d1>-<d2>-<d3>-<d4>-<d5>-<d6>-<d7>

     scor și criterii = întregi 0–100, `sev` = indexul din `SHARE_SEVERITIES`, `zonă` = indexul din
     `SHARE_ZONES`, criteriile în ordinea lui `DIMENSIONS`. În link nu încape NIMIC altceva: nici
     token, nici presupunerea (`impression`), nici vreun text liber. Validarea e un singur regex,
     care ține și intervalele (`100|[1-9]?\d` = 0–100, fără zerouri în față); orice abatere =
     „link nevalabil". Un link falsificat poate arăta doar alt scor sub textele comerciantului. */
  var SHARE_SEVERITIES = ['sanatos', 'usor', 'moderat', 'avansat'];
  var SHARE_ZONES = ['maini', 'picioare'];
  var SHARE_RE = /^v1\.(100|[1-9]?\d)\.([0-3])\.([01])\.((?:100|[1-9]?\d)(?:-(?:100|[1-9]?\d)){6})$/;
  var SHARE_GOOD = { sanatos: 1, usor: 1 };

  function encodeShared(result) {
    if (!result || typeof result !== 'object') return null;
    var score = wholeNumber(result.nail_health_score, 0, 100);
    var severity = SHARE_SEVERITIES.indexOf(asText(result.severity));
    var zone = SHARE_ZONES.indexOf(asText(result.zone));
    if (score === null || severity < 0 || zone < 0) return null;
    var dims = result.dimensions && typeof result.dimensions === 'object' ? result.dimensions : null;
    if (!dims) return null;
    var values = [];
    for (var i = 0; i < DIMENSIONS.length; i++) {
      var value = wholeNumber(util.own(dims, DIMENSIONS[i]), 0, 100);
      if (value === null) return null;
      values.push(value);
    }
    var code = 'v1.' + score + '.' + severity + '.' + zone + '.' + values.join('-');
    /* Plasă de siguranță: ce scriem trebuie să fie exact ce știm să citim. */
    return SHARE_RE.test(code) ? code : null;
  }

  /* `{ present, data }`: `present` = adresa are un `s=` (chiar gol sau stricat — atunci omul a
     primit un link și îi datorăm pagina „link nevalabil", nu scanerul gol); `data` = cele 4 câmpuri,
     cu numele din contractul Worker-ului, ca aceleași funcții de randare să le citească. */
  function parseShared(search) {
    var found = /[?&]s=([^&#]*)/.exec(String(search === null || search === undefined ? '' : search));
    if (!found) return { present: false, data: null };
    var raw = found[1];
    try { raw = decodeURIComponent(raw); } catch (e) { return { present: true, data: null }; }
    var m = SHARE_RE.exec(raw);
    if (!m) return { present: true, data: null };
    var values = m[4].split('-');
    var dimensions = {};
    for (var i = 0; i < DIMENSIONS.length; i++) dimensions[DIMENSIONS[i]] = Number(values[i]);
    return {
      present: true,
      data: {
        nail_health_score: Number(m[1]),
        severity: SHARE_SEVERITIES[Number(m[2])],
        zone: SHARE_ZONES[Number(m[3])],
        dimensions: dimensions
      }
    };
  }

  function createResult(ctx) {
    var state = ctx.state;
    var root = ctx.root;
    var store = util.safeStorage('session');
    var lastToken = '';   /* rezerva din memorie a lui `ns:last` (v1.4) */
    var slots = {};
    util.qsa(root, '[data-ns-slot]').forEach(function (el) {
      var name = el.getAttribute('data-ns-slot');
      if (!slots[name]) slots[name] = el;
    });
    var modal = util.qs(root, '[data-ns-modal="magnifier"]');
    var titleEl = util.qs(root, '[data-ns-slot="magnifier-title"]');
    var surfaceEl = util.qs(root, '[data-ns-slot="magnifier"]');
    var zoomEl = util.qs(root, '[data-ns-slot="magnifier-zoom"]');
    var detailEl = util.qs(root, '[data-ns-slot="magnifier-detail"]');

    var markers = [];
    var current = null;
    var zoom = ZOOMS[0];
    var lastFocus = null;
    var maps = null;

    function nameMaps() {
      if (!maps) maps = { nails: parseNameMap(ctx.text('nail_names')), issues: parseNameMap(ctx.text('issue_names')) };
      return maps;
    }

    /* ── a. inelul cu scorul ── (desenul e la nivel de modul: `renderScoreHero`) */

    function renderHero(result) {
      renderScoreHero(ctx, slots.hero, result, { labelKey: 'score_label', affected: true });
    }

    /* ── a2. cardul „Ce pare să fie" (v1.1 §1) ── primul lucru citit după scor.
       Excepția declarată de la regula „non-diagnostic": o presupunere CONCRETĂ, dar dintr-un
       vocabular închis și cu textele comerciantului. Nota „nu este un diagnostic" e condiția
       excepției, deci nu se poate goli: setare goală → implicitul din schemă, pe care Liquid-ul îl
       duce în config sub a doua cheie (`impression_disclaimer_default`). Fără NICIUN text de notă
       (config stricat) cardul nu se randează deloc — mai bine fără presupunere decât fără notă. */

    function verdictSigns(result, impression) {
      /* „Nu ne putem pronunța" + „pe ce ne bazăm" s-ar contrazice; Worker-ul garantează oricum []. */
      if (impression === IMPRESSION_UNCLEAR) return [];
      var raw = result && Array.isArray(result.impression_signs) ? result.impression_signs : [];
      var names = nameMaps().issues;
      var seen = {};
      var out = [];
      for (var i = 0; i < raw.length && out.length < IMPRESSION_SIGNS_MAX; i++) {
        if (typeof raw[i] !== 'string') continue;
        /* Aceeași regulă ca în lista pe unghii: un cod fără nume în setări nu se afișează. */
        var label = util.own(names, raw[i]);
        if (!label || util.own(seen, raw[i])) continue;
        seen[raw[i]] = true;
        out.push(label);
      }
      return out;
    }

    function renderVerdict(result, impression) {
      var el = slots.verdict;
      if (!el) return;
      var note = ctx.text('impression_disclaimer').trim() || ctx.text('impression_disclaimer_default').trim();
      if (!note) { el.innerHTML = ''; return; }

      var html = '';
      var eyebrow = ctx.text('impression_title').trim();
      if (eyebrow) html += util.tpl('<p class="ns-eyebrow ns-verdict-eyebrow">{t}</p>', { t: eyebrow });
      var title = ctx.text('impression_' + impression).trim();
      if (title) html += util.tpl('<h3 class="ns-verdict-title">{t}</h3>', { t: title });
      var signsPresent = signsPresentOf(result, impression);
      var answer = ctx.text('signs_' + signsPresent).trim();
      if (answer) {
        html += util.tpl('<p class="ns-verdict-answer ns-verdict-answer--{v}"><span class="ns-verdict-answer-label">{l}</span> <strong>{a}</strong></p>',
          { v: signsPresent, l: ctx.text('signs_label').trim(), a: answer });
      }
      /* Vestea bună + vestea rea țin locul propoziției de sprijin. Golite amândouă din editor → revine
         propoziția de până acum, ca un card fără explicație să nu existe niciodată. */
      var news = newsHtml(result, impression, signsPresent);
      if (news) html += news;
      else {
        var support = ctx.text('impression_' + impression + '_text').trim();
        if (support) html += util.tpl('<p class="ns-verdict-text">{t}</p>', { t: support });
      }

      var signs = verdictSigns(result, impression);
      if (signs.length) {
        var pills = '';
        for (var i = 0; i < signs.length; i++) pills += util.tpl('<li class="ns-pill ns-verdict-pill">{t}</li>', { t: signs[i] });
        html += '<div class="ns-verdict-signs">' +
          util.tpl('<p class="ns-verdict-signs-label">{t}</p>', { t: ctx.text('impression_signs_label') }) +
          '<ul class="ns-verdict-pills">' + pills + '</ul></div>';
      }
      html += util.tpl('<p class="ns-verdict-note">{t}</p>', { t: note });
      html += moreHtml();

      /* `impression` e deja de pe lista albă: în atribut și în clasă nu ajunge valoarea brută.
         Accentul chihlimbar e DOAR la „de văzut la medic"; restul cardurilor sunt neutre. */
      el.innerHTML = util.tpl('<div class="ns-verdict-card{care}" data-ns-impression="{value}" data-ns-signs="{signs}">', {
        care: impression === IMPRESSION_DOCTOR ? ' ns-verdict-card--care' : '', value: impression, signs: signsPresent
      }) + html + '</div>';
    }

    /* 05.10.2026 (Robert) — ordinea nouă (`config.layout === 'v2'`): la baza cardului, un îndemn îngroșat
       cu săgeți animate, „Vezi rezultatul complet mai jos", ca omul să coboare la raport. E un buton:
       derulează la primul bloc umplut al raportului (poze → criterii → „Pe scurt"). Săgețile stau pe loc
       cu `prefers-reduced-motion`. Pe ordinea veche nu apare. */
    var MORE_ARROWS = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 5l6 6 6-6"></path><path d="M6 12l6 6 6-6"></path></svg>';

    function moreHtml() {
      if (!ctx.config || ctx.config.layout !== 'v2') return '';
      var text = ctx.text('verdict_more').trim();
      if (!text) return '';
      return util.tpl('<button type="button" class="ns-verdict-more" data-ns-action="verdict-more"><span class="ns-verdict-more-text">{t}</span>', { t: text })
        + '<span class="ns-verdict-more-arrows">' + MORE_ARROWS + '</span></button>';
    }

    function scrollToReport() {
      if (state.destroyed) return;
      var order = ['photos', 'bars', 'summary'];
      var target = null;
      for (var i = 0; i < order.length && !target; i++) {
        var el = slots[order[i]];
        if (el && el.innerHTML && String(el.innerHTML).trim()) target = el;
      }
      if (!target) return;
      try {
        if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'start', behavior: ctx.stages.reducedMotion() ? 'auto' : 'smooth' });
      } catch (e) { try { target.scrollIntoView(); } catch (err) { /* DOM fals */ } }
    }
    ctx.actions.register('verdict-more', scrollToReport);

    /* Un bloc de veste: eticheta („Vestea bună este…"), propoziția și, dacă avem numere, criteriul cel
       mai bun / cel mai slab. Iconițele sunt inline (fontul de iconițe vine de pe un CDN care în
       browserele din aplicații poate lipsi). */
    var NEWS_ICON = {
      good: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12.5l4.2 4.2L19 7"></path></svg>',
      bad: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 7v6.5M12 17v.3"></path></svg>'
    };

    function newsItem(kind, labelKey, textKey, extra) {
      var text = ctx.text(textKey).trim();
      if (!text) return '';
      var label = ctx.text(labelKey).trim();
      return util.tpl('<div class="ns-news-item ns-news-item--{k}" data-ns-news="{k}"><span class="ns-news-icon">', { k: kind }) + NEWS_ICON[kind] + '</span>'
        + '<div class="ns-news-body">'
        + (label ? util.tpl('<p class="ns-news-label">{t}</p>', { t: label }) : '')
        + util.tpl('<p class="ns-news-text">{t}</p>', { t: text })
        + (extra ? util.tpl('<p class="ns-news-extra">{t}</p>', { t: extra }) : '')
        + '</div></div>';
    }

    function extremeLine(key, point) {
      if (!point) return '';
      var name = ctx.text(util.own(DIMENSION_TEXT, point.key) || '').trim();
      if (!name) return '';
      var template = ctx.text(key).trim();
      return template ? util.fmt(template, { criteriu: name, n: point.value }) : '';
    }

    function newsHtml(result, impression, signs) {
      var keys = newsKeys(result, impression, signs);
      var ext = keys.doctor || keys.unclear ? null : dimensionExtremes(result);
      /* Criteriul bun doar dacă e chiar bun (≥ 70); cel slab doar dacă chiar cere grijă (< 70) și nu
         tocmai am spus că nu prea avem o veste rea. */
      var bestLine = ext && ext.best.value >= 70 ? extremeLine('news_best', ext.best) : '';
      var worstLine = ext && !keys.healthy && ext.worst.value < 70 ? extremeLine('news_worst', ext.worst) : '';
      var good = newsItem('good', 'news_good_label', keys.good, bestLine);
      var bad = newsItem('bad', 'news_bad_label', keys.bad, worstLine);
      if (!good && !bad) return '';
      return '<div class="ns-news">' + good + bad + '</div>';
    }

    /* ── b. termenul promis ──
       29.09.2026 (Robert): intervalele vechi („Primele semne vizibile 12–16 săptămâni", „Unghie complet
       reînnoită 12–18 luni") descurajau exact oamenii cărora le trebuia planul. Acum e O SINGURĂ
       promisiune, la orice rezultat cu modificări: „Rezultate vizibile după primele 14 zile" (textul
       e setarea `callout_visible`; golită → caseta nu apare). Numerele modelului (`estimated_*`) tot
       nu se afișează. `sanatos` n-are ce „reînnoi": o singură casetă pozitivă. */

    function renderCallouts(result, impression) {
      var el = slots.callouts;
      if (!el) return;
      /* „De văzut la medic. Nu amâna." nu poate fi urmat nici de „Unghiile tale arată bine", nici de
         o promisiune de termen („așteaptă și se reface"). Worker-ul păstrează ANUME perechea
         `sanatos` + `de_vazut_la_medic` (o dungă închisă stă de obicei pe o unghie cu scor mare). */
      if (impression === IMPRESSION_DOCTOR) { el.innerHTML = ''; return; }
      var severity = asText(result.severity);
      if (severity === 'sanatos') {
        var good = ctx.text('callout_healthy');
        el.innerHTML = good ? util.tpl('<p class="ns-callout ns-callout--healthy">{t}</p>', { t: good }) : '';
        return;
      }
      /* Severitatea e aleasă de model: doar cele cunoscute primesc promisiunea. */
      var promise = ctx.text('callout_visible').trim();
      if (!promise || !util.own(SEVERITY_TEXT, severity)) { el.innerHTML = ''; return; }
      el.innerHTML = util.tpl('<p class="ns-callout ns-callout--promise"><span class="ns-callout-icon" aria-hidden="true"></span><strong>{t}</strong></p>', { t: promise });
    }

    /* ── c. pozele cu repere + lista pe unghii ── */

    function markerList(result) {
      var raw = result && Array.isArray(result.markers) ? result.markers : [];
      var out = [];
      var names = nameMaps();
      for (var i = 0; i < raw.length; i++) {
        var m = raw[i];
        if (!m || typeof m !== 'object') continue;
        var image = exactNumber(m.image_idx, 0, 99);
        var marker = {
          index: out.length + 1,
          nail: asText(m.nail),
          issue: asText(m.issue),
          severity: asText(m.severity),
          label: asText(m.label).trim(),
          detail: asText(m.detail).trim(),
          image: image !== null && Math.floor(image) === image ? image : null,
          x: exactNumber(m.x_pct, 0, 100),
          y: exactNumber(m.y_pct, 0, 100)
        };
        /* Un reper fără NIMIC de spus (obiect gol, câmpuri necunoscute) ar fi randat un rând gol
           în lista pe unghii — mai rău decât să lipsească. Coordonatele singure nu sunt o
           informație: un punct fără etichetă n-ar avea ce arăta în lupă. */
        if (!marker.label && !marker.detail && !util.own(names.nails, marker.nail) && !util.own(names.issues, marker.issue)) continue;
        out.push(marker);
      }
      return out;
    }

    function pinnable(marker, photoCount) {
      return marker.x !== null && marker.y !== null && marker.image !== null && marker.image < photoCount;
    }

    function renderPhotos(result) {
      var el = slots.photos;
      if (!el) return;
      markers = markerList(result);
      if (!markers.length) { el.innerHTML = ''; return; }

      var photos = state.photos || [];
      /* EXACT `true`: un config incomplet (cheie lipsă, `0`, `"nu"`) nu are voie să pornească o
         funcție pe care comerciantul n-a cerut-o. Liquid-ul scrie mereu un boolean. */
      var withPins = ctx.config.showMarkers === true && photos.length > 0;
      var html = util.tpl('<h3 class="ns-sub">{t}</h3>', { t: ctx.text('photos_title') });
      var pinned = 0;

      if (withPins) {
        for (var p = 0; p < photos.length; p++) {
          var pins = '';
          for (var i = 0; i < markers.length; i++) {
            var m = markers[i];
            if (m.image !== p || !pinnable(m, photos.length)) continue;
            /* Sufixul de clasă vine DOAR de pe lista albă, nu din valoarea brută a modelului. */
            var band = util.own(MARKER_SEVERITY_TEXT, m.severity) ? ' is-' + m.severity : '';
            pins += util.tpl(
              '<button type="button" class="ns-pin{band}" data-ns-action="magnifier-open" data-ns-marker="{id}" style="left:{x}%;top:{y}%" aria-label="{aria}">{no}</button>',
              { band: band, id: i, x: num(m.x), y: num(m.y), aria: util.fmt(ctx.text('marker_aria'), { i: m.index, label: m.label }), no: m.index }
            );
            pinned += 1;
          }
          if (!pins) continue;
          html += util.tpl('<div class="ns-photo" data-ns-photo="{i}"><img src="{src}" alt="" decoding="async">', { i: p, src: photos[p].url }) + pins + '</div>';
        }
      }

      if (pinned) html += util.tpl('<p class="ns-tips">{t}</p>', { t: ctx.text('photos_hint') });
      else if (!photos.length) html += util.tpl('<p class="ns-note">{t}</p>', { t: ctx.text('photos_missing') });

      /* Lista pe unghii e MEREU acolo: e singurul lucru care supraviețuiește unei reîncărcări sau
         unui link din email, și singurul care rămâne dacă reperele n-au coordonate bune. */
      html += renderMarkerRows();
      el.innerHTML = html;
    }

    function renderMarkerRows() {
      var names = nameMaps();
      var rows = '';
      for (var i = 0; i < markers.length; i++) {
        var m = markers[i];
        var head = '';
        var nailName = util.own(names.nails, m.nail);
        if (nailName) head += util.tpl('<strong class="ns-marker-nail">{t}</strong>', { t: nailName });
        /* Categoria („Margine neregulată") se sare când modelul a scris exact același lucru în
           eticheta lui: două rânduri identice unul sub altul arată a bug, nu a informație. */
        var category = util.own(names.issues, m.issue);
        if (category && !sameWords(category, m.label)) head += util.tpl('<span class="ns-marker-issue">{t}</span>', { t: category });
        var severityKey = util.own(MARKER_SEVERITY_TEXT, m.severity);
        if (severityKey) head += util.tpl('<span class="ns-sev is-{s}">{t}</span>', { s: m.severity, t: ctx.text(severityKey) });

        rows += util.tpl('<li class="ns-marker"><span class="ns-marker-no">{no}</span><div class="ns-marker-body"><p class="ns-marker-head">', { no: m.index })
          + head + '</p>'
          + (m.label ? util.tpl('<p class="ns-marker-label">{t}</p>', { t: m.label }) : '')
          + (m.detail ? util.tpl('<p class="ns-marker-detail">{t}</p>', { t: m.detail }) : '')
          + '</div></li>';
      }
      return rows ? '<ol class="ns-marker-list">' + rows + '</ol>' : '';
    }

    /* ── lupa ── */

    /* Poziția fundalului, în procente, ca punctul (x,y) din poză să cadă în CENTRUL ferestrei:
       `(p/100*zoom - 0.5) / (zoom - 1) * 100`, tăiată la 0–100 (la marginile pozei nu mai există
       ce alinia, deci fundalul se lipește de margine). */
    function bgPosition(percent, zoomLevel) {
      var p = clampNumber(percent, 0, 100);
      if (p === null) p = 50;
      var z = Number(zoomLevel);
      if (!isFinite(z) || z <= 1) return 50;
      return Math.min(100, Math.max(0, ((p / 100 * z - 0.5) / (z - 1)) * 100));
    }

    function paint() {
      if (!surfaceEl || !current) return;
      var photo = (state.photos || [])[current.image];
      surfaceEl.setAttribute('style',
        'background-image:url("' + cssUrl(photo && photo.url) + '");' +
        'background-size:' + num(zoom * 100) + '%;' +
        'background-position:' + num(bgPosition(current.x, zoom)) + '% ' + num(bgPosition(current.y, zoom)) + '%');
    }

    /* Butoanele de mărire se construiesc O SINGURĂ DATĂ, la deschidere. Dacă le-am redesena la
       fiecare apăsare, butonul apăsat ar fi scos din pagină chiar sub degetul clientului, iar
       focusul ar cădea pe `body` — un utilizator de tastatură și-ar pierde locul în fereastră. */
    function renderZooms() {
      if (!zoomEl) return;
      var html = util.tpl('<span class="ns-zoom-label">{t}</span>', { t: ctx.text('magnifier_zoom') });
      for (var i = 0; i < ZOOMS.length; i++) {
        html += util.tpl('<button type="button" class="ns-zoom" data-ns-action="magnifier-zoom" data-ns-zoom="{z}" aria-pressed="false">{z}&#215;</button>', { z: ZOOMS[i] });
      }
      zoomEl.innerHTML = html;
      syncZooms();
    }

    function syncZooms() {
      util.qsa(zoomEl, '[data-ns-zoom]').forEach(function (btn) {
        btn.setAttribute('aria-pressed', Number(btn.getAttribute('data-ns-zoom')) === zoom ? 'true' : 'false');
      });
    }

    function focusables() {
      return util.qsa(modal, 'button').filter(function (el) { return !isDisabled(el) && !el.hasAttribute('hidden'); });
    }

    function openMagnifier(id, from) {
      var m = markers[id];
      if (!modal || !m) return;
      var photo = (state.photos || [])[m.image];
      if (!photo || !photo.url) return;
      current = m;
      zoom = ZOOMS[0];
      lastFocus = from || null;
      util.setText(titleEl, m.label);
      util.setText(detailEl, m.detail);
      renderZooms();
      paint();
      modal.removeAttribute('hidden');
      ctx.lockScroll('magnifier', true);
      var list = focusables();
      if (list.length) list[0].focus();
    }

    /* `restoreFocus` doar pe drumul CERUT de client (butonul, Escape, fundalul): acolo focusul
       trebuie să se întoarcă pe punctul de unde a plecat. Pe drumul de curățare (`clear()`, adică
       resetare, randare nouă sau teardown) nu: `cleanup()` rulează cârligele de resetare ÎNAINTE
       de `destroyed`, iar editorul de teme aruncă nodul la fiecare modificare — am fi mutat
       focusul pe un punct care tocmai dispare din pagină. */
    function closeMagnifier(restoreFocus) {
      if (!modal || modal.hasAttribute('hidden')) return;
      modal.setAttribute('hidden', '');
      ctx.lockScroll('magnifier', false);
      current = null;
      var back = lastFocus;
      lastFocus = null;
      if (restoreFocus === true && back && typeof back.focus === 'function') back.focus();
    }

    ctx.actions.register('magnifier-open', function (ev, el) {
      openMagnifier(Number(el.getAttribute('data-ns-marker')), el);
    });
    ctx.actions.register('magnifier-close', function () { closeMagnifier(true); });
    ctx.actions.register('magnifier-zoom', function (ev, el) {
      var next = Number(el.getAttribute('data-ns-zoom'));
      if (ZOOMS.indexOf(next) < 0) return;
      zoom = next;
      syncZooms();
      paint();
    });

    /* Clic pe fundal (nu pe card) închide fereastra — tiparul așteptat pe mobil. */
    if (modal) {
      ctx.on(modal, 'click', function (ev) {
        if (ev && ev.target === modal) closeMagnifier(true);
      });
    }

    ctx.on(document, 'keydown', function (ev) {
      if (!modal || modal.hasAttribute('hidden')) return;
      var key = ev && ev.key;
      if (key === 'Escape' || key === 'Esc') {
        if (typeof ev.preventDefault === 'function') ev.preventDefault();
        closeMagnifier(true);
        return;
      }
      if (key !== 'Tab') return;
      var list = focusables();
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      var inside = ev.target && typeof modal.contains === 'function' ? modal.contains(ev.target) : true;
      if (ev.shiftKey && ev.target === first) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && ev.target === last) { ev.preventDefault(); first.focus(); }
      else if (!inside) { ev.preventDefault(); first.focus(); }
    });

    /* ── d. barele pe cele 7 criterii ── */

    function renderBars(result) {
      renderDimensionBars(ctx, slots.bars, result, 'bars_title');
    }

    /* ── e. rezumatul și sfaturile ── */

    function renderSummary(result) {
      var el = slots.summary;
      if (!el) return;
      var html = '';
      var summary = asText(result.summary).trim();
      if (summary) {
        html += util.tpl('<h3 class="ns-sub">{t}</h3><p class="ns-summary-text">{s}</p>', { t: ctx.text('summary_title'), s: summary });
      }
      var advice = Array.isArray(result.care_advice) ? result.care_advice : [];
      var items = '';
      var used = 0;
      for (var i = 0; i < advice.length && used < ADVICE_MAX; i++) {
        var line = asText(advice[i]).trim();
        if (!line) continue;
        items += util.tpl('<li>{t}</li>', { t: line });
        used += 1;
      }
      if (items) html += util.tpl('<h3 class="ns-sub">{t}</h3>', { t: ctx.text('advice_title') }) + '<ul class="ns-advice">' + items + '</ul>';
      el.innerHTML = html;
    }

    /* ── nota de specialist ──
       Trimiterea la medic NU are voie să stea sub poarta de email: când steagul e pus, nota urcă
       MEREU sub verdict (slotul `specialist-top`, în afara `[data-ns-gated]`), orice presupunere ar
       însoți-o — Worker-ul nu leagă presupunerea de steag, iar promptul îi spune modelului că „nota
       către specialist apare oricum". Accentul chihlimbar al cardului rămâne doar la „de văzut la
       medic". Slotul de jos e rezerva pentru un markup fără `specialist-top`; nota apare o dată. */

    function renderSpecialist(result) {
      var top = slots['specialist-top'] || null;
      var el = top || slots.specialist;
      if (top && slots.specialist) slots.specialist.innerHTML = '';
      if (!el) return;
      var flags = result && result.flags && typeof result.flags === 'object' ? result.flags : {};
      if (flags.see_specialist !== true) { el.innerHTML = ''; return; }
      var html = util.tpl('<p class="ns-specialist-title"><i class="bi bi-heart-pulse" aria-hidden="true"></i>{t}</p>', { t: ctx.text('specialist_title') });
      html += util.tpl('<p>{t}</p>', { t: ctx.text('specialist_text') });
      var reason = asText(result.see_specialist_reason).trim();
      if (reason) html += util.tpl('<p class="ns-specialist-reason">{t}</p>', { t: reason });
      el.innerHTML = html;
    }

    /* ── randarea completă ── */

    function render(data, opts) {
      if (state.destroyed) return;
      var o = opts || {};
      var result = data && typeof data === 'object' ? data : {};
      closeMagnifier();
      var impression = impressionOf(result);
      renderHero(result);
      renderVerdict(result, impression);
      renderCallouts(result, impression);
      renderPhotos(result);
      renderBars(result);
      renderSummary(result);
      renderSpecialist(result);
      /* ABIA acum: sarcinile 67/68 se așteaptă să găsească rezultatul deja pe ecran. */
      /* `fromToken` = rezultat refăcut (link SAU sesiune), nu o scanare nouă; `fromLink` = tokenul a
         venit chiar din `?r=` — adică din emailul nostru, deci omul și-a dat deja adresa (v1.1 §4:
         sosește cu poarta deschisă). O reîncărcare de pagină are `fromToken`, dar NU `fromLink`:
         altfel „reîncarcă pagina" ar fi o scurtătură pe lângă poartă. */
      ctx.emit('ns:result', { result: data, token: o.token === undefined ? null : o.token, fromToken: !!o.fromToken, fromLink: !!o.fromLink });
    }

    function clear() {
      closeMagnifier();
      markers = [];
      for (var i = 0; i < RESULT_SLOTS.length; i++) {
        if (slots[RESULT_SLOTS[i]]) slots[RESULT_SLOTS[i]].innerHTML = '';
      }
    }

    /* ── tokenul: `?r=TOKEN` din email și continuitatea sesiunii ── */

    function remember(token) {
      /* Modul „vizitator" nu scrie NIMIC în stocare (v1.1 §3a): rezultatul de pe ecran nu e al lui. */
      if (state.viewer) return false;
      if (!token || !TOKEN_RE.test(String(token))) return false;
      lastToken = String(token);
      store.set(LAST_TOKEN_KEY, lastToken);
      return store.set(SESSION_TOKEN_KEY, String(token));
    }
    function remembered() {
      var value = store.get(SESSION_TOKEN_KEY);
      return value && TOKEN_RE.test(String(value)) ? String(value) : null;
    }
    function forget() { store.remove(SESSION_TOKEN_KEY); }
    /* Ultimul rezultat cunoscut: cel din sesiune, altfel cel de dinaintea lui „Scanează din nou"
       (cu rezerva din memorie, când stocarea e blocată). */
    function lastKnown() {
      var current = remembered();
      if (current) return current;
      var value = store.get(LAST_TOKEN_KEY);
      if (value && TOKEN_RE.test(String(value))) return String(value);
      return lastToken && TOKEN_RE.test(lastToken) ? lastToken : null;
    }
    function forgetLast() {
      lastToken = '';
      store.remove(LAST_TOKEN_KEY);
    }

    function tokenFromUrl() {
      var w = getWindow();
      var search = (w && w.location && w.location.search) || '';
      var found = /[?&]r=([^&]*)/.exec(String(search));
      return found && TOKEN_RE.test(found[1]) ? found[1] : null;
    }

    function load(token, options) {
      var fromLink = !!(options && options.fromLink === true);
      clear();
      if (slots.hero) slots.hero.innerHTML = util.tpl('<p class="ns-loading">{t}</p>', { t: ctx.text('result_loading') });
      /* Fără focus și fără derulare: suntem la ÎNCĂRCAREA paginii, nu într-un pas al clientului.
         Excepția (v1.4): „Vezi rezultatul tău" de pe ecranul de limită E un pas al clientului. */
      if (options && options.focus === true) ctx.stages.show('result');
      else ctx.stages.show('result', { focus: false, scroll: false });

      ctx.api.result(token).then(function (res) {
        if (state.destroyed || state.stage !== 'result') return;
        if (res && res.kind === 'aborted') return;
        if (res && res.kind === 'ok' && res.result) {
          state.token = res.token || token;
          state.result = res.result;
          remember(state.token);
          /* `?r=TOKEN` iese din bara de adrese ACUM, înainte de randare: de-aici înainte pagina
             mai trimite evenimente de pixel, iar fiecare ar căra tokenul în adresa paginii — un
             token care deschide rezultatul oricui. Sesiunea îl ține mai departe, deci reîncărcarea
             merge la fel. */
          stripResultParam();
          /* Pozele nu supraviețuiesc unei reîncărcări (există doar ca `blob:` în memorie), deci
             aici rămâne lista pe unghii + nota care spune de ce. */
          render(res.result, { token: state.token, fromToken: true, fromLink: fromLink });
          return;
        }
        clear();
        setErrorRetry(ctx, false);
        /* NUMAI un 404 înseamnă „rezultatul nu mai există": atunci, și doar atunci, aruncăm
           tokenul din sesiune. O rețea căzută sau un server împiedicat nu spun nimic despre
           rezultat — „Nu am găsit rezultatul. Poate a expirat" ar fi o minciună, iar `forget()` i-ar
           lua omului singura cale de a-l mai deschide. */
        var kind = (res && res.kind) || 'server_error';
        if (kind === 'not_found') {
          forget();
          forgetLast();
          ctx.stages.error({ title: ctx.text('error_title'), message: ctx.text('result_not_found'), focus: false, scroll: false });
          return;
        }
        /* Tot la ÎNCĂRCAREA paginii suntem (v1.1 §2): nici restaurarea eșuată nu derulează și nu
           ia focusul — `error()` dă opțiunile mai departe lui `show()`. */
        ctx.stages.error({
          title: ctx.text('error_title'),
          message: ctx.text(util.own(FAIL_TEXT, kind) || 'err_network'),
          focus: false,
          scroll: false
        });
      });
    }

    /* Rulează la pornire, DUPĂ ce `intro` e pe ecran: dacă n-avem ce încărca, nu se schimbă nimic. */
    function boot() {
      if (state.destroyed || state.configBroken || !ctx.workerUrl) return false;
      var linked = tokenFromUrl();
      var token = linked || remembered();
      if (!token) return false;
      load(token, { fromLink: !!linked });
      return true;
    }

    /* Resetarea golește ECRANUL, dar NU uită tokenul din sesiune: `stages.reset()` rulează și la
       `shopify:section:unload` (editorul de teme aruncă nodul la fiecare modificare), iar acolo
       ștergerea ar rupe tocmai continuitatea pe care o vrem. Tokenul se înlocuiește singur la
       următoarea scanare reușită și se șterge când Worker-ul nu-l mai are (404). */
    ctx.onReset(clear);

    return {
      render: render,
      clear: clear,
      boot: boot,
      load: load,
      remember: remember,
      remembered: remembered,
      lastKnown: lastKnown,
      forget: forget,
      tokenFromUrl: tokenFromUrl,
      dashOffset: dashOffset,
      bgPosition: bgPosition,
      markerList: markerList,
      openMagnifier: openMagnifier,
      closeMagnifier: closeMagnifier,
      zoom: function () { return zoom; },
      ring: { r: RING_R, box: RING_BOX, circumference: RING_C },
      zooms: ZOOMS,
      dimensions: DIMENSIONS,
      impressions: IMPRESSIONS,
      impressionOf: impressionOf
    };
  }

  /* ── plan + cta ── (sarcina 67, refăcută pe 29.09.2026) planul determinist, prețurile EXACTE ale
     treptelor, suplimentele bifabile și butonul „Comandă cu plata la livrare".

     Două module mici în locul unuia mare:
       `ctx.plan` — maparea severitate → plan din SETĂRI (din v1.1 §8 și planul „îngrijire", ales
                    după presupunere — vezi `pick`), suplimentele după steaguri (NU intră în total
                    până nu le bifează omul), cardurile, totalurile, prețul redus după email și
                    adresa de trimitere spre formularul de comandă;
       `ctx.cta`  — butonul (în flux + bara lipită), un LINK spre pagina produsului principal.
     `cta` e cel care ascultă `ns:result` DUPĂ plan, ca adresa butonului să fie scrisă din planul
     deja construit — ordinea e pironită de un test.

     DE CE NU MAI E COȘ (29.09): clienții Delfino cumpără prin formularul Releasit (plata la
     livrare), care stă pe pagina produsului și GOLEȘTE coșul Shopify la fiecare încărcare a unei
     pagini de produs. „Adaugă planul în coș" ducea omul la finalizarea Shopify, cu prețul întreg
     (6 uleiuri = 419,94 în loc de 296,94) — și nimeni n-a apăsat. Acum butonul duce omul la
     formular cu treapta, suplimentele și codul deja alese (scan-handoff.js, pe pagina produsului).

     PREȚURILE sunt cele de pe pagina produsului și din formularul Releasit, pentru ACEEAȘI
     cantitate: `tierTotals` (aritmetica lui DLProduct.bundleTotals), cu reducerea treptei din
     setările blocului de plan. Testul nail-scan-pricing le leagă de tools/phase2/releasit-offers.json.

     AI-ul nu alege NICIODATĂ produse: `result.severity` (și, pentru „îngrijire",
     `result.impression`) e doar o cheie într-o hartă scrisă de comerciant, citită cu `util.own`
     (un `constructor` venit de la model nu are voie să scoată ceva de pe `Object.prototype`). */

  /* Codurile de reducere Shopify: litere, cifre, liniuță, subliniere. Orice altceva (spațiu,
     ghilimea, bară) ar rupe adresa de trimitere sau ar fi un cod inexistent — îl tratăm ca pe
     „fără cod", nu-l trimitem și nu-l arătăm. */
  var DISCOUNT_CODE_RE = /^[A-Za-z0-9_-]{1,40}$/;
  var PLAN_SEVERITIES = { sanatos: 1, usor: 1, moderat: 1, avansat: 1 };
  /* Planul „îngrijire" (v1.1 §8): unui om FĂRĂ semne de ciupercă nu-i recomandăm un ulei „fungic".
     Cheia NU e o severitate (lista de mai sus rămâne oglinda Worker-ului): e blocul pe care
     comerciantul îl scrie pentru presupunerile de mai jos. Tot el alege produsele. */
  var PLAN_CARE = 'ingrijire';
  var CARE_IMPRESSIONS = { fara_semne_ciuperca: 1, alta_cauza_probabila: 1 };
  var ADDON_FLAGS = { always: 1, needs_foot_care: 1, needs_regeneration: 1 };
  var MAX_QTY = 99;
  /* `Number.MAX_SAFE_INTEGER` scris ca literal: restul fișierului e ES5 (fără `let`, fără săgeți). */
  var MAX_VARIANT_ID = 9007199254740991;
  var VARIANT_ID_RE = /^[0-9]{1,17}$/;
  /* Suplimentele pe care formularul de comandă le poate bifa singur (bife „tick-up" în Releasit).
     Cheia pleacă în adresa de trimitere (`ns_addons=`) și e căutată în textul bifei, pe pagina
     produsului — scan-handoff.js are ACEEAȘI listă. Produsul se recunoaște după titlu. */
  var HANDOFF_ADDON_KEYS = ['keranail', 'keratalp'];
  /* Ancora de la capătul adresei: slotul în care coboară formularul Releasit (product-cod-slot). */
  var HANDOFF_ANCHOR = 'dl-cod-form-slot';
  /* Unde lasă scanerul adresa Worker-ului pentru pagina produsului (`form_opened`, `code_applied`). */
  var WORKER_KEY = 'ns:worker';
  var WORKER_URL_RE = /^(https:\/\/[A-Za-z0-9.-]+(:\d{1,5})?|http:\/\/(localhost|127\.0\.0\.1)(:\d{1,5})?)(\/[A-Za-z0-9._~\/-]*)?$/;
  /* Scaner v2 (05.10.2026): eticheta paginii — `v1` (/pages/scanare-unghii, neschimbată) sau `v2`
     (pagina cu cardul de apel). Vine din config (`variant`, scrisă de Liquid din setarea
     `scan_variant`) și pleacă în `POST /plan` (`plan.variant`), pe comandă („Scaner versiune") și în
     evenimentul Klaviyo. Lipsă sau necunoscută → gol: cheia se OMITE, iar Merlo citește lipsa ca v1
     (așa nu se schimbă niciun mesaj al paginilor fără setare). */
  var SCAN_VARIANTS = { v1: 1, v2: 1 };

  function scanVariant(config) {
    var raw = asText(config && config.variant).trim().toLowerCase();
    return util.own(SCAN_VARIANTS, raw) === 1 ? raw : '';
  }

  function validCode(value) {
    var code = asText(value).trim();
    return DISCOUNT_CODE_RE.test(code) ? code : '';
  }

  /* Un id de variantă Shopify e un întreg pozitiv. TĂIEREA la interval (ce făceam înainte) e
     greșită aici: `0` și `-5` deveneau `1`, adică o variantă străină trimisă mai departe. */
  function variantId(value) {
    var n = value;
    if (typeof n === 'string') {
      /* Un id scris ca șir e un șir de CIFRE, atât — fără `trim`, fără `1e3`, fără `0x10`:
         `Number()` le-ar înghiți pe toate și ar scoate un id care nu e al nimănui. */
      if (!VARIANT_ID_RE.test(n)) return null;
      n = Number(n);
    }
    if (typeof n !== 'number' || !isFinite(n)) return null;
    if (Math.floor(n) !== n) return null;
    return n >= 1 && n <= MAX_VARIANT_ID ? n : null;
  }

  /* O adresă care ajunge într-un `href` sau într-un `src` vine din setările magazinului, dar
     „vine din setări" nu e același lucru cu „e o adresă": `javascript:` scris acolo ar fi un link
     executabil. Acceptăm doar căi relative de pe magazin și http(s). */
  function safeUrl(value) {
    var url = asText(value).trim();
    if (!url) return '';
    if (/^\//.test(url) && !/^\/\//.test(url)) return url;
    return /^https?:\/\//i.test(url) ? url : '';
  }

  /* Prețul unei trepte de cantitate — ACEEAȘI aritmetică ca `DLProduct.bundleTotals`
     (theme/assets/product.js), ca Liquid-ul din main-product.liquid și ca formularul Releasit
     (verificat pe toate ofertele): reducerea procentuală a treptei, în bani, se rotunjește în JOS și
     se scade din prețul întreg; apoi reducerea fixă a treptei (ofertele Releasit „fixed", ex. 6
     uleiuri −123,00 lei), niciodată sub 0. `was` = prețul vechi (sau, fără el, prețul întreg) ×
     cantitate — exact ce arată tăiat rândul treptei de pe pagina produsului. */
  function tierTotals(priceCents, compareCents, qty, pct, offCents) {
    var price = Math.max(0, Math.floor(Number(priceCents) || 0));
    var quantity = Math.max(1, Math.floor(Number(qty) || 1));
    var discount = Math.min(100, Math.max(0, Math.floor(Number(pct) || 0)));
    var compare = Math.max(price, Math.floor(Number(compareCents) || 0));
    var full = price * quantity;
    var total = full - Math.floor((full * discount) / 100);
    total = Math.max(0, total - Math.max(0, Math.floor(Number(offCents) || 0)));
    return { total: total, full: full, was: compare * quantity };
  }

  /* Reducerea unui cod procentual pe o sumă: ca Releasit, în bani, rotunjită în JOS. */
  function percentOff(amountCents, pct) {
    var amount = Math.max(0, Math.floor(Number(amountCents) || 0));
    var p = Math.min(100, Math.max(0, Math.floor(Number(pct) || 0)));
    return Math.floor((amount * p) / 100);
  }

  /* Cheia de trimitere a unui produs (`keranail` / `keratalp`), după titlu; gol = nu are. */
  function addonKeyOf(title) {
    var text = asText(title).toLowerCase();
    for (var i = 0; i < HANDOFF_ADDON_KEYS.length; i++) {
      if (text.indexOf(HANDOFF_ADDON_KEYS[i]) > -1) return HANDOFF_ADDON_KEYS[i];
    }
    return '';
  }

  function createPlan(ctx) {
    var root = ctx.root;
    var config = ctx.config || {};
    var slots = {
      plan: util.qs(root, '[data-ns-slot="plan"]'),
      totals: util.qs(root, '[data-ns-slot="totals"]')
    };
    var entries = [];   /* itemii randați acum, în ordinea de pe ecran */
    var picked = {};    /* suplimentele BIFATE de om, după poziția lor în `entries` — implicit niciunul */
    var current = null; /* blocul de plan folosit (pentru codul lui) */
    var currentKey = ''; /* cheia lui: o severitate sau `ingrijire`; gol = niciun plan */
    var bonus = null;   /* {code, pct} — sarcina 68 îl dezvăluie după email */

    function planFor(severity) {
      var key = asText(severity);
      if (!util.own(PLAN_SEVERITIES, key)) return null;
      var block = util.own(config.plans, key);
      return block && typeof block === 'object' ? block : null;
    }

    /* Un bloc „are ce vinde" când măcar un produs din el se poate comanda: variantă reală și
       `available` diferit de `false` — exact regula din `normalize`. */
    function sellable(block) {
      var items = block && Array.isArray(block.items) ? block.items : [];
      for (var i = 0; i < items.length; i++) {
        var one = normalize(items[i], { step: null, badge: '', kind: 'plan' });
        if (one && one.available) return true;
      }
      return false;
    }

    /* Ce plan primește rezultatul ăsta: `{ key, block }` sau `null`.
       Întâi severitatea trebuie să fie una CUNOSCUTĂ: un rezultat cu o severitate inventată e un
       rezultat stricat și nu primește niciun plan — regula de până acum. Apoi, DOAR dacă
       presupunerea e una dintre cele două „fără ciupercă" și blocul „îngrijire" are măcar un produs
       care se poate cumpăra, acela e planul, indiferent de severitate. Un bloc „îngrijire" gol
       (produsul legat nu se găsește în magazin) sau cu totul epuizat lasă totul pe drumul vechi:
       planul severității, dacă e configurat. */
    function pick(result) {
      var severity = asText(result && result.severity);
      if (!util.own(PLAN_SEVERITIES, severity)) return null;
      if (util.own(CARE_IMPRESSIONS, impressionOf(result)) === 1) {
        var care = util.own(config.plans, PLAN_CARE);
        if (care && typeof care === 'object' && sellable(care)) return { key: PLAN_CARE, block: care };
      }
      var bySeverity = planFor(severity);
      return bySeverity ? { key: severity, block: bySeverity } : null;
    }

    /* Suplimentele: `always` mereu, celelalte două doar când steagul din rezultat e EXACT `true`.
       Steagul e ales de model, deci și el trece prin lista albă + `util.own`. */
    function addonsFor(result) {
      var list = Array.isArray(config.addons) ? config.addons : [];
      var flags = result && result.flags && typeof result.flags === 'object' ? result.flags : {};
      var out = [];
      for (var i = 0; i < list.length; i++) {
        var entry = list[i];
        if (!entry || typeof entry !== 'object') continue;
        var flag = asText(entry.flag);
        if (!util.own(ADDON_FLAGS, flag)) continue;
        if (flag !== 'always' && util.own(flags, flag) !== true) continue;
        out.push(entry);
      }
      return out;
    }

    function normalize(raw, extra) {
      if (!raw || typeof raw !== 'object') return null;
      var id = variantId(raw.variantId);
      var price = wholeNumber(raw.price, 0, 100000000);
      var compare = wholeNumber(raw.compareAt, 0, 100000000);
      var qty = wholeNumber(raw.qty, 1, MAX_QTY);
      var tierPct = wholeNumber(raw.tierPct, 0, 100);
      var tierOff = wholeNumber(raw.tierOff, 0, 100000000);
      var one = {
        id: id,
        qty: qty === null ? 1 : qty,
        title: asText(raw.title).trim(),
        price: price === null ? 0 : price,
        compareAt: compare,
        tierPct: tierPct === null ? 0 : tierPct,
        tierOff: tierOff === null ? 0 : tierOff,
        image: safeUrl(raw.image),
        url: safeUrl(raw.url),
        when: asText(raw.when).trim(),
        why: asText(raw.why).trim(),
        tip: asText(raw.tip).trim(),
        /* Fără variantă nu există nimic de comandat, oricât ar zice setarea. */
        available: id !== null && raw.available !== false,
        step: extra.step,
        badge: extra.badge,
        kind: extra.kind === 'addon' ? 'addon' : 'plan',
        key: addonKeyOf(raw.title)
      };
      var line = tierTotals(one.price, one.compareAt, one.qty, one.tierPct, one.tierOff);
      one.lineTotal = line.total;
      one.lineWas = line.was;
      return one;
    }

    function build(result) {
      var out = [];
      var picked0 = pick(result);
      var block = picked0 ? picked0.block : null;
      current = block;
      currentKey = picked0 ? picked0.key : '';
      /* Fără bloc de plan pentru severitatea asta (severitate necunoscută, sau comerciantul n-a
         configurat-o) NU există plan — iar suplimentele sunt un ADAOS la un plan, nu un plan.
         Un singur card „Set unghiere" sub titlul „Planul tău de îngrijire" ar arăta a defect. */
      if (!block) return out;
      var inPlan = {}; /* variantele puse deja de plan */
      var planItems = Array.isArray(block.items) ? block.items : [];
      for (var i = 0; i < planItems.length; i++) {
        var one = normalize(planItems[i], { step: out.length + 1, badge: '', kind: 'plan' });
        if (!one) continue;
        out.push(one);
        if (one.id !== null) inPlan['v' + one.id] = true;
      }
      /* 29.09 (Robert): upsell-urile KeraNail/KeraTalp există DOAR pe formularul Releasit al
         uleiului fungic. Planul „îngrijire" duce la pagina KeraNail, unde formularul n-are ce să
         bifeze — un supliment bifat acolo s-ar pierde tăcut. Deci la „îngrijire": fără suplimente. */
      var addons = currentKey === PLAN_CARE ? [] : addonsFor(result);
      for (var a = 0; a < addons.length; a++) {
        var items = Array.isArray(addons[a].items) ? addons[a].items : [];
        for (var k = 0; k < items.length; k++) {
          var extra = normalize(items[k], { step: null, badge: asText(addons[a].badge).trim(), kind: 'addon' });
          if (!extra) continue;
          /* Un supliment care e DEJA produsul planului nu apare a doua oară (v1.1 §8): KeraNail e și
             produsul planului „îngrijire", și suplimentul de la `needs_regeneration`. Două carduri cu
             același produs — al doilea cu textul „după ulei", într-un plan fără ulei — ar arăta a defect. */
          if (extra.id !== null && inPlan['v' + extra.id] === true) continue;
          out.push(extra);
        }
      }
      return out;
    }

    function isAddon(one) { return !!one && one.kind === 'addon'; }

    /* Produsul PRINCIPAL: primul produs disponibil al planului (nu un supliment). Pe pagina lui
       duce butonul de comandă; fără el nu există comandă (doar blocul „comandă cu un om"). */
    function mainItem() {
      for (var i = 0; i < entries.length; i++) {
        if (!isAddon(entries[i]) && entries[i].available) return entries[i];
      }
      return null;
    }

    /* Tot ce se VEDE disponibil (plan + suplimente), bifat sau nu. */
    function available() {
      return entries.filter(function (one) { return one.available; });
    }

    /* Ce intră în COMANDĂ: produsele disponibile ale planului + suplimentele disponibile BIFATE. Din
       lista asta se fac totalul, mesajul de WhatsApp și adresa de trimitere. */
    function chosen() {
      if (!mainItem()) return [];
      var out = [];
      for (var i = 0; i < entries.length; i++) {
        var one = entries[i];
        if (!one.available) continue;
        if (isAddon(one) && picked[i] !== true) continue;
        out.push(one);
      }
      return out;
    }

    /* Un singur cod poate călători spre formular: câștigă procentul mai mare, iar la egalitate
       codul planului (e al comerciantului, bonusul e un plus). */
    function effective() {
      var fromPlan = null;
      var planCode = validCode(current && current.discountCode);
      var planPct = wholeNumber(current && current.discountPct, 0, 100) || 0;
      if (planCode && planPct > 0) fromPlan = { code: planCode, pct: planPct };
      if (!bonus) return fromPlan;
      if (!fromPlan) return bonus;
      return bonus.pct > fromPlan.pct ? bonus : fromPlan;
    }

    /* `subtotal` = prețul de pe SITE (treptele + suplimentele bifate); `total` = ce plătește omul
       după codul bonus, cu reducerea rotunjită în jos, ca în formular. Fără cod, sunt egale. */
    function totals() {
      var list = chosen();
      var subtotal = 0;
      for (var i = 0; i < list.length; i++) subtotal += list[i].lineTotal;
      var deal = effective();
      var discount = deal ? percentOff(subtotal, deal.pct) : 0;
      var total = subtotal - discount;
      var threshold = wholeNumber(config.freeShippingThreshold, 0, 100000000) || 0;
      return {
        subtotal: subtotal,
        discount: discount,
        discountCode: deal ? deal.code : '',
        discountPct: deal ? deal.pct : 0,
        total: total,
        /* Pragul de transport se arată DOAR dacă magazinul chiar are unul (implicit 0 → nimic:
           n-avem voie să promitem transport pe care Delfino nu-l oferă). */
        shipping: threshold > 0
          ? { show: true, threshold: threshold, free: total >= threshold, missing: Math.max(0, threshold - total) }
          : { show: false, threshold: 0, free: false, missing: 0 }
      };
    }

    /* Adresa butonului „Comandă cu plata la livrare": pagina produsului principal, cu
         ns=<token> (pentru pâlnie) · ns_qty=<cantitatea planului> · ns_addons=<cheile bifate> ·
         ns_code=<codul care dă prețul redus, doar dacă există>
       și ancora formularului. Gol = n-avem unde trimite (fără produs principal sau fără adresă). */
    function handoffUrl(token) {
      var main = mainItem();
      if (!main || !main.url) return '';
      var params = [];
      if (token && TOKEN_RE.test(String(token))) params.push('ns=' + token);
      params.push('ns_qty=' + main.qty);
      var keys = [];
      var list = chosen();
      for (var i = 0; i < list.length; i++) {
        if (list[i] === main || !list[i].key || keys.indexOf(list[i].key) > -1) continue;
        keys.push(list[i].key);
      }
      if (keys.length) params.push('ns_addons=' + keys.join(','));
      var deal = effective();
      if (deal) params.push('ns_code=' + encodeURIComponent(deal.code));
      var base = main.url.split('#')[0];
      return base + (base.indexOf('?') > -1 ? '&' : '?') + params.join('&') + '#' + HANDOFF_ANCHOR;
    }

    /* ── cardurile ── */

    function moneyText(cents) { return plainText(ctx.money(cents)); }

    function priceHtml(one) {
      var price = util.tpl('<strong>{t}</strong>', { t: ctx.money(one.lineTotal) });
      /* Prețul tăiat e cel de pe rândul treptei de pe pagina produsului: prețul vechi (sau întreg) ×
         cantitate — și doar când e MAI MARE: altfel arată a greșeală, nu a reducere. */
      if (one.lineWas > one.lineTotal) price += util.tpl('<s>{t}</s>', { t: ctx.money(one.lineWas) });
      if (one.qty > 1) price += util.tpl('<span class="ns-plan-qty">{t}</span>', { t: util.fmt(ctx.text('plan_qty'), { n: one.qty }) });
      return '<p class="ns-plan-price">' + price + '</p>';
    }

    /* Cardul are DOUĂ zone: antetul (poza + insignele + titlul + prețul) pe un rând, iar explicațiile
       („când", „de ce", sfatul) pe toată lățimea dedesubt. La 360 px coloana de lângă o poză de
       88 px are 164 px: un titlu de produs se rupe pe trei rânduri, iar „de ce" pe șase. Așa,
       textul lung are 266 px.
       Suplimentele (29.09, ca bifele din formularul Releasit): insigna AURIE „Ai nevoie și de asta",
       lângă ea ce face produsul (insigna blocului) și, sub antet, o BIFĂ mare „Adaugă la comandă".
       Nebifate, nu intră nici în total, nici în comandă. */
    function card(one, index, orderable) {
      var addon = isAddon(one);
      var head = '';
      if (addon) {
        var badges = '';
        var need = ctx.text('addon_need_badge').trim();
        if (need) badges += util.tpl('<span class="ns-badge ns-badge--need">{t}</span>', { t: need });
        var what = one.badge || ctx.text('plan_addon').trim();
        if (what) badges += util.tpl('<span class="ns-badge ns-badge--what">{t}</span>', { t: what });
        if (badges) head += '<p class="ns-addon-badges">' + badges + '</p>';
      } else {
        var eyebrow = one.step !== null ? util.fmt(ctx.text('plan_step'), { i: one.step }) : (one.badge || ctx.text('plan_addon'));
        if (eyebrow) head += util.tpl('<p class="ns-plan-eyebrow">{t}</p>', { t: eyebrow });
      }

      var title = one.title || '';
      head += one.url
        ? util.tpl('<p class="ns-plan-title"><a class="ns-plan-link" href="{href}">{t}</a></p>', { href: one.url, t: title })
        : util.tpl('<p class="ns-plan-title">{t}</p>', { t: title });
      head += priceHtml(one);

      var pickRow = '';
      if (addon && one.available && orderable) {
        var on = picked[index] === true;
        pickRow = util.tpl('<label class="ns-addon-pick"><input type="checkbox" class="ns-check ns-addon-check" data-ns-addon="{i}"', { i: index })
          + (on ? ' checked' : '') + '>'
          + util.tpl('<span class="ns-addon-pick-text">{t}</span></label>', { t: util.fmt(ctx.text('addon_pick_label'), { suma: moneyText(one.lineTotal) }) });
      }

      var body = '';
      if (one.when) body += util.tpl('<p class="ns-plan-when">{t}</p>', { t: one.when });
      if (one.why) body += util.tpl('<p class="ns-plan-why">{t}</p>', { t: one.why });
      if (one.tip) body += util.tpl('<p class="ns-plan-tip"><i class="bi bi-lightbulb" aria-hidden="true"></i>{t}</p>', { t: one.tip });
      if (!one.available) body += util.tpl('<p class="ns-plan-off">{t}</p>', { t: ctx.text('item_unavailable') });

      /* Imaginea are dimensiuni ca să nu miște layout-ul și `lazy` fiindcă planul e jos de tot pe
         ecranul de rezultat. `alt` = titlul produsului: e singurul lucru util de citit acolo. */
      var media = one.image
        ? util.tpl('<img class="ns-plan-img" src="{src}" alt="{alt}" width="88" height="88" loading="lazy" decoding="async">', { src: one.image, alt: title })
        : '';
      var classes = (one.available ? '' : ' is-off') + (addon ? ' ns-addon-card' : '') + (addon && picked[index] === true ? ' is-picked' : '');
      return util.tpl('<div class="ns-plan-card{c}" data-ns-item="{id}" data-ns-kind="{k}">', { c: classes, id: one.id === null ? '' : one.id, k: one.kind })
        + '<div class="ns-plan-head">' + media + '<div class="ns-plan-headtext">' + head + '</div></div>'
        + pickRow
        + (body ? '<div class="ns-plan-body">' + body + '</div>' : '')
        + '</div>';
    }

    function renderCards() {
      var el = slots.plan;
      if (!el) return;
      if (!entries.length) { el.innerHTML = ''; return; }
      var orderable = !!mainItem();
      var html = util.tpl('<h3 class="ns-sub">{t}</h3>', { t: ctx.text('plan_title') });
      for (var i = 0; i < entries.length; i++) html += card(entries[i], i, orderable);
      /* Produsul principal epuizat: cardurile rămân (clientul vede ce i se recomandă), dar în locul
         butonului primește o explicație calmă (și, dedesubt, blocul „comandă cu un om"). */
      if (!orderable) html += util.tpl('<p class="ns-note">{t}</p>', { t: ctx.text('plan_none') });
      el.innerHTML = html;
    }

    function row(className, label, value) {
      return util.tpl('<p class="ns-total-row{c}"><span class="ns-total-label">{l}</span><span class="ns-total-value">{v}</span></p>',
        { c: className, l: label, v: value });
    }

    /* Textul comerciantului cu suma ÎNGROȘATĂ: fiecare bucată de text e escapată, iar `{suma}` devine
       `<strong>` — markupul e al nostru, valorile rămân text. */
    function withStrongSum(template, sum, vars) {
      var parts = String(template).split('{suma}');
      var strong = util.tpl('<strong class="ns-scan-price-sum">{s}</strong>', { s: sum });
      return parts.map(function (part) { return util.esc(util.fmt(part, vars)); }).join(strong);
    }

    /* După email / telefon: „Prețul tău din scanare: X lei — cu 5% mai puțin decât pe site", plus
       nota că de cod se ocupă formularul. Textul golit din editor → rămâne un rând simplu cu totalul. */
    function scanPriceHtml(t) {
      var line = ctx.text('scan_price_line').trim();
      var html = line
        ? '<p class="ns-scan-price-line">' + withStrongSum(line, moneyText(t.total), { pct: t.discountPct }) + '</p>'
        : row(' is-scan', ctx.text('totals_total'), ctx.money(t.total));
      var note = ctx.text('scan_price_code').trim();
      if (note && t.discountCode) html += util.tpl('<p class="ns-scan-price-code">{t}</p>', { t: util.fmt(note, { cod: t.discountCode }) });
      return '<div class="ns-scan-price">' + html + '</div>';
    }

    /* ÎNAINTE de email: îndemnul „lasă-ne emailul sau telefonul și primești 5% la comanda asta",
       cu un buton care duce la bloc. Doar dacă blocul chiar există și chiar dezvăluie un cod. */
    function hintHtml() {
      /* Ordinea nouă (v2): cardul cu 5% stă chiar sub total — un îndemn care trimite la el ar fi dublură. */
      if (ctx.config && ctx.config.layout === 'v2') return '';
      var email = ctx.email;
      if (!email || typeof email.configured !== 'function' || !email.configured() || email.done()) return '';
      if (!email.bonusCode()) return '';
      var pct = email.bonusPct();
      var text = ctx.text('bonus_hint').trim();
      if (!(pct > 0) || !text) return '';
      var action = ctx.text('bonus_hint_action').trim();
      return '<div class="ns-bonus-hint">'
        + util.tpl('<p class="ns-bonus-hint-text">{t}</p>', { t: util.fmt(text, { pct: pct }) })
        + (action ? util.tpl('<button type="button" class="ns-btn ns-btn-ghost ns-bonus-hint-btn" data-ns-action="bonus-jump">{t}</button>', { t: util.fmt(action, { pct: pct }) }) : '')
        + '</div>';
    }

    function renderTotals() {
      var el = slots.totals;
      if (!el) return;
      if (!entries.length || !mainItem()) { el.innerHTML = ''; return; }
      var t = totals();
      var deal = t.discount > 0;
      /* „Total" = prețul de pe site. Cu reducerea bonus deblocată, rămâne pe ecran TĂIAT, iar sub el
         vine prețul din scanare: omul vede exact cât câștigă față de site. */
      var value = util.tpl(deal ? '<s>{v}</s>' : '{v}', { v: ctx.money(t.subtotal) });
      var html = util.tpl('<p class="ns-total-row is-total{d}"><span class="ns-total-label">{l}</span>', { d: deal ? ' is-struck' : '', l: ctx.text('totals_total') })
        + '<strong class="ns-total-value">' + value + '</strong></p>';
      html += deal ? scanPriceHtml(t) : hintHtml();
      if (t.shipping.show && t.shipping.free) html += row(' is-shipping', ctx.text('totals_shipping'), ctx.text('totals_shipping_free'));
      if (t.shipping.show && !t.shipping.free) {
        html += util.tpl('<p class="ns-total-note">{t}</p>', { t: util.fmt(ctx.text('totals_shipping_left'), { suma: ctx.money(t.shipping.missing) }) });
      }
      var ship = ctx.text('totals_ship_note').trim();
      if (ship) html += util.tpl('<p class="ns-total-note ns-ship-note">{t}</p>', { t: ship });
      el.innerHTML = html;
      ctx.emit('ns:totals', t);
    }

    function render(result) {
      if (ctx.state.destroyed) return;
      picked = {};
      entries = build(result && typeof result === 'object' ? result : {});
      renderCards();
      renderTotals();
    }

    function clear() {
      entries = [];
      picked = {};
      current = null;
      currentKey = '';
      bonus = null;
      if (slots.plan) slots.plan.innerHTML = '';
      if (slots.totals) slots.totals.innerHTML = '';
    }

    /* Bifa unui supliment. Doar cardul își schimbă clasa (focusul rămâne pe bifă); totalurile se
       rescriu și anunță restul paginii prin `ns:totals` (butonul, bara lipită, mesajul WhatsApp). */
    function toggle(index, on) {
      var one = entries[index];
      if (!isAddon(one) || !one.available || !mainItem()) return false;
      picked[index] = on === true;
      util.qsa(slots.plan, '[data-ns-addon]').forEach(function (input) {
        if (String(input.getAttribute('data-ns-addon')) !== String(index)) return;
        input.checked = picked[index];
        var cardEl = closestAttr(input, 'data-ns-item', slots.plan);
        if (cardEl && cardEl.classList) cardEl.classList[picked[index] ? 'add' : 'remove']('is-picked');
      });
      renderTotals();
      return true;
    }

    /* Contractul sarcinii 68: după ce clientul își lasă emailul, codul bonus se dezvăluie și
       totalurile se recalculează pe loc. `null` îl scoate. */
    function setBonus(next) {
      var code = next ? validCode(next.code) : '';
      var pct = next ? (wholeNumber(next.pct, 0, 100) || 0) : 0;
      bonus = code && pct > 0 ? { code: code, pct: pct } : null;
      renderTotals();
      return bonus;
    }

    /* Planul se randează pe `ns:result` (sarcina 66 îl emite DUPĂ ce restul rezultatului e pe
       ecran). `cta` își înregistrează ascultătorul DUPĂ al nostru, ca butonul să găsească planul
       deja construit — ordinea e pironită de un test. */
    ctx.on(root, 'ns:result', function (ev) {
      render(ev && ev.detail ? ev.detail.result : null);
    });
    ctx.actions.registerChange('data-ns-addon', function (ev, el) {
      if (ctx.state.destroyed || !el) return;
      var index = wholeNumber(el.getAttribute('data-ns-addon'), 0, 1000);
      if (index === null || !toggle(index, el.checked === true)) return;
      if (ctx.history) ctx.history.action('addon_toggled');
    });
    ctx.onReset(clear);

    return {
      for: planFor,
      pick: pick,
      key: function () { return currentKey; },
      addonsFor: addonsFor,
      build: build,
      items: function () { return entries.slice(); },
      available: available,
      chosen: chosen,
      main: mainItem,
      picked: function () { var out = []; for (var k in picked) if (Object.prototype.hasOwnProperty.call(picked, k) && picked[k] === true) out.push(Number(k)); return out; },
      toggle: toggle,
      totals: totals,
      code: function () { var deal = effective(); return deal ? deal.code : ''; },
      handoffUrl: handoffUrl,
      setBonus: setBonus,
      bonus: function () { return bonus; },
      render: render,
      renderTotals: renderTotals,
      clear: clear,
      hasItems: function () { return entries.length > 0; },
      hasAvailable: function () { return !!mainItem(); },
      tierTotals: tierTotals,
      percentOff: percentOff
    };
  }

  function createCta(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var ctaEl = util.qs(root, '[data-ns-slot="cta"]');
    var stickyEl = util.qs(root, '[data-ns-slot="sticky"]');
    /* v1.2 §12.3: blocul „comandă cu un om" stă imediat sub butonul din flux. Îl umple `orderHelp`;
       aici doar îl URMĂRIM, ca bara lipită să nu stea niciodată peste butoanele lui. */
    var helpEl = util.qs(root, '[data-ns-slot="order-help"]');

    var token = null;
    /* Butonul e desenat pentru rezultatul de pe ecran. Până atunci, un `ns:totals` (planul emite
       unul chiar în randarea lui, ÎNAINTEA noastră) nu are ce reîmprospăta. */
    var live = false;
    var observer = null;
    /* Presupunem butonul din flux VIZIBIL până ne spune observatorul altceva: altfel bara ar
       clipi pe ecran la fiecare randare. Fără observator, rămâne „mereu pe ecran". */
    var inflowVisible = true;
    /* Blocul de comandă pornește „nevăzut" (e sub buton, deci sub marginea ecranului): observatorul
       îl raportează când intră. Cât se vede, bara stă stinsă — altfel, în clipa în care butonul din
       flux iese pe sus, bara ar reapărea jos exact PESTE „Comandă pe WhatsApp" și „Sună-ne". */
    var helpVisible = false;
    /* 02.10.2026 (Robert): butonul de comandă e cel ORIGINAL Releasit. Secțiunea randează ascuns, în
       `[data-ns-rsi-pool]`, câte un „app block” Releasit pe produs al planurilor; aici mutăm în rezultat
       blocul produsului principal (Releasit i-a pus deja butonul), iar la click scan-handoff.js bifează în
       pop-up oferta, suplimentele și codul — omul nu mai pleacă de pe pagină. Fără bloc sau fără butonul
       Releasit în el (aplicația n-a încărcat) → rămâne linkul de dinainte spre pagina produsului. */
    var pool = util.qs(root, '[data-ns-rsi-pool]');
    var rsiBlock = null;

    function rsiBlockFor(main) {
      if (!pool || !main || main.id === null || main.id === undefined) return null;
      var blocks = util.qsa(root, '[data-ns-rsi-block]');
      for (var i = 0; i < blocks.length; i++) {
        if (blocks[i].getAttribute('data-ns-rsi-variant') !== String(main.id)) continue;
        return blocks[i].querySelector('._rsi-buy-now-button') ? blocks[i] : null;
      }
      return null;
    }

    /* Releasit citește varianta și cantitatea din formularul de produs (`form.shopify-product-form`, ca pe
       pagina produsului) și deschide pop-up-ul cu oferta acelei cantități deja bifată (verificat pe
       delfino.ro, 02.10: cantitatea 6 → „4 bucăți + 2 gratis”). Formularul stă DOAR în blocul afișat —
       pe pagină nu există niciodată două —, e ascuns și n-are buton de trimitere. */
    function syncRsiForm() {
      if (!rsiBlock) return;
      var main = ctx.plan.hasAvailable() ? ctx.plan.main() : null;
      if (!main) return;
      var form = rsiBlock.querySelector('form[data-ns-rsi-form]');
      if (!form) {
        form = rsiBlock.ownerDocument.createElement('form');
        form.className = 'shopify-product-form';
        form.setAttribute('data-ns-rsi-form', '');
        form.setAttribute('action', '/cart/add');
        form.setAttribute('method', 'post');
        form.setAttribute('hidden', '');
        form.innerHTML = '<input type="hidden" name="id"><input type="hidden" name="quantity">';
        rsiBlock.insertBefore(form, rsiBlock.firstChild);
      }
      form.querySelector('[name="id"]').value = String(main.id);
      form.querySelector('[name="quantity"]').value = String(main.qty);
    }

    /* Blocul se întoarce în rezervă ÎNAINTE de orice `innerHTML` pe slot — altfel butonul Releasit s-ar
       pierde odată cu marcajul vechi. Formularul lui pleacă odată cu el. */
    function parkRsi() {
      if (rsiBlock) {
        var form = rsiBlock.querySelector('form[data-ns-rsi-form]');
        if (form && form.parentNode) form.parentNode.removeChild(form);
        if (pool && rsiBlock.parentNode !== pool) pool.appendChild(rsiBlock);
      }
      rsiBlock = null;
    }

    function onRsiClick() {
      if (state.destroyed || state.viewer) return;
      var main = ctx.plan.hasAvailable() ? ctx.plan.main() : null;
      if (!main || !rsiBlock) return;
      syncRsiForm();
      rememberWorker();
      var keys = [];
      var list = ctx.plan.chosen();
      for (var i = 0; i < list.length; i++) {
        if (list[i] === main || !list[i].key || keys.indexOf(list[i].key) > -1) continue;
        keys.push(list[i].key);
      }
      var w = getWindow();
      if (w && w.DLScanHandoff && typeof w.DLScanHandoff.start === 'function') {
        /* 03.10.2026: comanda poartă „Sursa: Scaner unghii” + planul + tokenul scanării (Merlo → lista de sunat).
           05.10.2026 (scaner v2): și „Scaner versiune” (v1/v2), când pagina are eticheta. */
        var attrs = { 'Sursa': 'Scaner unghii', 'Scaner plan': (ctx.plan.key() || '') + ' · ' + main.qty + ' buc.' + (keys.length ? ' + ' + keys.join(', ') : ''), 'Scaner token': token || '' };
        var variant = scanVariant(ctx.config);
        if (variant) attrs['Scaner versiune'] = variant;
        w.DLScanHandoff.start({ qty: main.qty, token: token || '', addons: keys, code: ctx.plan.code(), productId: rsiBlock.getAttribute('data-ns-rsi-product'), orderAttrs: attrs });
      }
      if (ctx.history) ctx.history.action('cod_click');
    }

    function url() { return ctx.plan.handoffUrl(token); }

    /* Un LINK adevărat (merge și în browserele din aplicații, și cu clic de mijloc): navigarea o face
       browserul; acțiunea doar raportează pasul. */
    function buttonHtml(className, placement) {
      var href = ctx.plan.hasAvailable() ? url() : '';
      if (!href) return '';
      return util.tpl('<a class="ns-btn ns-btn-primary ns-cod-btn {c}" data-ns-action="order-cod" data-ns-placement="{p}" href="{href}">{t}</a>',
        { c: className, p: placement, href: href, t: ctx.text('cta_order') });
    }

    function renderCta() {
      if (!ctaEl) return;
      parkRsi();
      var block = !state.viewer && ctx.plan.hasAvailable() ? rsiBlockFor(ctx.plan.main()) : null;
      if (block) {
        var rsiNote = ctx.text('cta_order_note').trim();
        ctaEl.innerHTML = rsiNote ? util.tpl('<p class="ns-cta-note">{t}</p>', { t: rsiNote }) : '';
        ctaEl.insertBefore(block, ctaEl.firstChild);
        rsiBlock = block;
        syncRsiForm();
        return;
      }
      var btn = buttonHtml('ns-cta-btn', 'inline');
      if (!btn) { ctaEl.innerHTML = ''; return; }
      var note = ctx.text('cta_order_note').trim();
      ctaEl.innerHTML = btn + (note ? util.tpl('<p class="ns-cta-note">{t}</p>', { t: note }) : '');
    }

    function renderSticky() {
      if (!stickyEl) return;
      /* Cu butonul Releasit în rezultat, bara cu linkul spre pagina produsului n-ar mai avea sens. */
      var btn = rsiBlock ? '' : buttonHtml('ns-sticky-btn', 'sticky');
      if (!btn) { stickyEl.innerHTML = ''; syncSticky(); return; }
      stickyEl.innerHTML = util.tpl('<span class="ns-sticky-total"><span class="ns-sticky-label">{l}</span><strong>{v}</strong></span>',
        { l: ctx.text('totals_total'), v: ctx.money(ctx.plan.totals().total) })
        + btn;
      syncSticky();
    }

    /* Bifele și codul bonus se schimbă DUPĂ randare: adresa butonului din pagină se rescrie pe loc
       (nodul rămâne același, ca observatorul să-l urmărească în continuare); bara lipită se redesenează. */
    function refresh() {
      if (!live) return;
      var href = ctx.plan.hasAvailable() ? url() : '';
      if (!href) { renderCta(); watchInflow(); renderSticky(); return; }
      if (rsiBlock) { renderSticky(); return; }
      var links = util.qsa(ctaEl, '[data-ns-action="order-cod"]');
      if (!links.length) { renderCta(); watchInflow(); }
      else links.forEach(function (a) { a.setAttribute('href', href); });
      renderSticky();
    }

    /* Bara e utilă DOAR pe ecranul de rezultat și doar cât timp butonul din flux nu se vede.
       Se ascunde cu `hidden` (reset-ul temei are `[hidden]{display:none!important}`) — niciodată
       mutată în afara ecranului: o cutie `position: fixed` parcată lateral lățește pagina pe iOS. */
    function syncSticky() {
      if (!stickyEl) return;
      /* …și NICIODATĂ peste poarta de email (v1.1 §4): cât e închisă, planul și butonul din flux
         sunt ascunse, deci observatorul ar raporta „butonul nu se vede" și bara ar vinde un plan pe
         care omul încă nu l-a văzut. */
      var on = ctx.plan.hasAvailable() && stickyEl.innerHTML !== '' && state.stage === 'result' && !inflowVisible && !helpVisible && state.gated !== true;
      if (on) stickyEl.removeAttribute('hidden');
      else stickyEl.setAttribute('hidden', '');
      /* Cât timp bara e pe ecran, ea acoperă ultimele ~80 px ale rezultatului: clasa asta lasă loc
         dedesubt. Spațiul se adaugă SUB conținut, deci nu mișcă butonul urmărit de observator. */
      if (root && root.classList) root.classList[on ? 'add' : 'remove']('has-sticky');
    }

    function stopObserver() {
      if (!observer) return;
      try { observer.disconnect(); } catch (e) { /* deja plecat */ }
      observer = null;
    }

    function watchInflow() {
      stopObserver();
      var w = getWindow();
      var IO = w && w.IntersectionObserver;
      var btn = ctaEl ? util.qs(ctaEl, '.ns-cta-btn') : null;
      if (!btn || typeof IO !== 'function') {
        /* Browser fără IntersectionObserver: bara rămâne pe ecran cât ține rezultatul. */
        inflowVisible = !btn;
        syncSticky();
        return;
      }
      inflowVisible = true;
      helpVisible = false;
      try {
        /* Un singur observator, două ținte. Browserul raportează DOAR ținta care s-a schimbat, deci
           fiecare își ține starea ei. Un slot gol e `display: none` (nu se „vede" niciodată). */
        observer = new IO(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (helpEl && entries[i].target === helpEl) helpVisible = !!entries[i].isIntersecting;
            else inflowVisible = !!entries[i].isIntersecting;
          }
          syncSticky();
        }, { threshold: 0 });
        observer.observe(btn);
        if (helpEl) observer.observe(helpEl);
      } catch (e) {
        observer = null;
        inflowVisible = false;
        helpVisible = false;
      }
      syncSticky();
    }

    /* Omul pleacă spre formular: adresa Worker-ului rămâne în browser, ca pagina produsului să poată
       raporta `form_opened` / `code_applied` pe tokenul din adresă. Doar o adresă https (sau
       localhost, în teste) — altceva nu se scrie. */
    function rememberWorker() {
      var base = ctx.api && typeof ctx.api.base === 'string' ? ctx.api.base : '';
      if (!base || !WORKER_URL_RE.test(base)) return false;
      return util.safeStorage('local').set(WORKER_KEY, base);
    }

    function onClick(ev, el) {
      if (state.destroyed || state.viewer) return;
      rememberWorker();
      /* Adresa se reîmprospătează în gestul omului: o bifă făcută chiar înainte nu se pierde. */
      var href = ctx.plan.hasAvailable() ? url() : '';
      if (href && el && typeof el.setAttribute === 'function') el.setAttribute('href', href);
      if (!ctx.history) return;
      if (el && typeof el.getAttribute === 'function' && el.getAttribute('data-ns-placement') === 'sticky') ctx.history.action('sticky_click');
      ctx.history.action('cod_click');
    }

    /* ── ciclul de viață ── */

    function render(detail) {
      if (state.destroyed) return;
      var raw = detail && detail.token ? String(detail.token) : '';
      token = TOKEN_RE.test(raw) ? raw : null;
      live = true;
      renderCta();
      renderSticky();
      watchInflow();
    }

    function clear() {
      token = null;
      live = false;
      stopObserver();
      inflowVisible = true;
      helpVisible = false;
      parkRsi();
      if (ctaEl) ctaEl.innerHTML = '';
      if (stickyEl) { stickyEl.innerHTML = ''; stickyEl.setAttribute('hidden', ''); }
      if (root && root.classList) root.classList.remove('has-sticky');
    }

    ctx.actions.register('order-cod', onClick);
    /* Click pe butonul Releasit din rezultat: în faza de captură, înaintea aplicației (ea deschide pop-up-ul). */
    if (ctaEl) ctx.on(ctaEl, 'click', function (ev) {
      var t = ev && ev.target && ev.target.closest ? ev.target.closest('._rsi-buy-now-button') : null;
      if (t && rsiBlock && rsiBlock.contains(t)) onRsiClick();
    }, true);
    /* DUPĂ ascultătorul lui `plan` (înregistrat mai devreme, în `bind`): adresa butonului se scrie
       din planul deja construit. */
    ctx.on(root, 'ns:result', function (ev) { render(ev && ev.detail ? ev.detail : null); });
    /* Bifele și codul bonus dezvăluit după email schimbă totalul și adresa. */
    ctx.on(root, 'ns:totals', function () { if (!state.destroyed) refresh(); });
    ctx.on(root, 'ns:stage', syncSticky);
    /* Poarta se închide DUPĂ randarea noastră (emailul ascultă `ns:result` după noi) și se deschide
       oricând mai târziu: la fiecare schimbare, bara se hotărăște din nou. */
    ctx.on(root, 'ns:gate', syncSticky);
    ctx.onReset(clear);

    return {
      url: url,
      token: function () { return token; },
      render: render,
      clear: clear,
      refresh: refresh,
      syncSticky: syncSticky,
      rememberWorker: rememberWorker,
      stickyVisible: function () { return !!stickyEl && !stickyEl.hasAttribute('hidden'); }
    };
  }

  /* ── email ── (sarcina 68) Klaviyo + exit-intent; fiecare formular se limitează la
     `closest('[data-ns-form]')`.

     Sarcina 66 livrează doar CIOTUL, fiindcă formularul „te anunțăm când e liber" de pe etapa de
     eroare are nevoie de el acum. CONTRACTUL pe care sarcina 68 îl înlocuiește (păstrându-l):

       ctx.email.subscribe({ email, source }) → Promise<{ok:true} | {ok:false, reason}>
       ctx.email.subscribe({ phone, source }) → la fel (2026-09-24: „Nu am email" → WhatsApp)

     • `email`  — deja curățat de spații și scris cu litere mici de apelant;
     • `phone`  — un mobil românesc, în orice formă scrisă de om (07xx…, +40 7xx…, 0040 7xx…); aici
       devine `+407XXXXXXXX` (E.164, forma cerută de Klaviyo) sau cererea nu pleacă (`invalid`);
     • `source` — de unde vine abonarea: 'overload' (etapa de eroare), 'result', 'exit';
     • promisiunea NU se respinge NICIODATĂ (ca `api`): un canal de marketing căzut nu are voie să
       oprească fluxul clientului. Ciotul întoarce `{ok:false, reason:'not_configured'}`;
     • `reason` e pentru jurnal și teste, nu pentru ecran: formularele arată aceeași confirmare
       neutră indiferent de răspuns — clientul n-are ce face cu „lista Klaviyo lipsește".

     `EMAIL_RE` e validarea de client (o verificare grosieră, adevărul îl spune serverul).

     API-ul PUBLIC al lui Klaviyo (`/client/…`), nu cel privat: se autentifică DOAR cu `company_id`
     (cheia publică a magazinului, aceeași care stă în orice fragment Klaviyo din pagină), deci n-are
     ce secret să scape în browser. Ambele cereri merg cu `credentials: 'omit'` — un cookie n-are ce
     căuta acolo — și răspund `202` cu corpul gol. */

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
  var EMAIL_MAX = 254;
  var BONUS_CODE_RE = /^[A-Za-z0-9_-]{1,40}$/;
  var KLAVIYO_SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions?company_id=';
  var KLAVIYO_EVENTS_URL = 'https://a.klaviyo.com/client/events?company_id=';
  /* Rezerva telefonului: dacă abonarea pe canalul WhatsApp e refuzată (4xx — de pildă contul Klaviyo
     n-are încă WhatsApp pornit), profilul se salvează totuși, cu numărul și `prefers_whatsapp`, ca
     Robert să-l găsească într-un segment și să trimită codul de mână. */
  var KLAVIYO_PROFILES_URL = 'https://a.klaviyo.com/client/profiles?company_id=';
  /* Proprietățile de profil (2026-09-24): de unde vine omul și ce i-am promis. Nu sunt texte de ecran. */
  var LEAD_SOURCE = 'nail_scan';
  var KLAVIYO_REVISION = '2026-07-15';
  var KLAVIYO_TYPE = 'application/vnd.api+json';
  var KLAVIYO_TIMEOUT_MS = 15000;
  /* `custom_source` ajunge în profilul din Klaviyo, ca Robert să vadă de unde a venit adresa.
     Nu e text de ecran, deci stă în cod (și n-are diacritice, ca restul fișierului). */
  var KLAVIYO_SOURCES = { result: 'Scanare unghii', exit: 'Scanare unghii', gate: 'Scanare unghii', overload: 'Scanare unghii — aglomerare' };
  var METRIC_SCAN = 'Nail Scan Completed';
  var METRIC_WAITLIST = 'Nail Scan Waitlist';
  var BONUS_FLAG_PREFIX = 'ns:bonus:';
  /* v1.1 §4 — poarta de email. Steagul spune DOAR „rezultatul cu tokenul ăsta a fost deblocat";
     nicio adresă, niciun număr de telefon, nimic despre cum s-a deblocat. */
  var UNLOCK_FLAG_PREFIX = 'ns:unlocked:';
  /* „În sesiunea asta omul ȘI-A LĂSAT adresa" (un 202 adevărat, din oricare formular): la o scanare
     nouă nu-l mai oprim la poartă — aceeași regulă ca la fereastra de ieșire. Tot fără adresă. */
  var GATE_EMAIL_KEY = 'ns:gate:email';
  var GATE_MODES = { off: 1, soft: 1, hard: 1 };
  var WA_MIN_DIGITS = 10;
  var WA_MAX_DIGITS = 15; /* E.164 */
  var WA_ALLOWED_RE = /^[0-9+\s().\/-]+$/;
  var PHONE_MAX = 20;
  var LEAD_CHANNELS = { email: 1, phone: 1 };
  var EXIT_FLAG_KEY = 'ns:exit';
  /* „A citit destul cât să merite oferta": sub 8 secunde clientul abia a ajuns pe rezultat. */
  var EXIT_DELAY_MS = 8000;
  var EXIT_TOP_PX = 20;
  var NEXT_SCAN_DAYS = 30;
  var DAY_MS = 86400000;

  /* Data calendaristică în UTC, `YYYY-MM-DD` — aceeași zi pentru toată lumea, indiferent de fusul
     telefonului (altfel „peste 30 de zile" ar putea deveni 29 sau 31 la trecerea la ora de vară). */
  function isoDate(value) {
    var d = value instanceof Date ? value : new Date(value);
    if (!d || isNaN(d.getTime())) return '';
    var month = d.getUTCMonth() + 1;
    var day = d.getUTCDate();
    return d.getUTCFullYear() + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day;
  }

  /* Numărul de WhatsApp, în forma pe care o cere `wa.me`: DOAR cifre, cu prefixul țării și fără
     zeroul din față. Comerciantul îl scrie cum îl știe („0799 008 009"), iar `wa.me/0799008009` nu
     duce nicăieri — de-aia formatul românesc de 10 cifre primește „4" în față, iar `+40…` și
     `0040…` rămân `40…`. Orice altceva decât cifre și semnele obișnuite de scriere (spațiu, +, -,
     paranteze, punct, bară) înseamnă că în setare nu e un număr: nu ghicim, întoarcem gol. La fel
     sub 10 cifre, peste 15 (E.164) sau cu un zero în față care nu e forma românească. Singura
     corectură în plus: `40` + `0` + 9 cifre (prefix și zero național scrise împreună). */
  function waNumber(value) {
    var raw = asText(value).trim();
    if (!raw || !WA_ALLOWED_RE.test(raw)) return '';
    var digits = raw.replace(/[^0-9]/g, '');
    if (digits.indexOf('00') === 0) digits = digits.slice(2);
    else if (digits.charAt(0) === '0') digits = digits.length === 10 ? '4' + digits : '';
    if (digits.charAt(0) === '0') return '';
    /* „+40 0799 008 009": prefixul țării ȘI zeroul național. Niciun număr românesc nu începe cu 0
       după 40, deci zeroul e de prisos — altfel `wa.me/400799008009` dă „număr nevalid". */
    if (digits.length === 12 && digits.indexOf('400') === 0) digits = '40' + digits.slice(3);
    return digits.length >= WA_MIN_DIGITS && digits.length <= WA_MAX_DIGITS ? digits : '';
  }

  /* Numărul de MOBIL al clientului (formularul „Nu am email"), în forma E.164 cerută de Klaviyo:
     `+407XXXXXXXX`. Doar România și doar mobil (WhatsApp stă pe mobil): 07xx xxx xxx, +40 7xx…,
     0040 7xx…, chiar și „+40 07xx…" (prefix și zero scrise împreună). Orice altceva — fix, străin,
     litere, un „+" la mijloc — întoarce gol: nu ghicim un număr la care să trimitem codul. */
  function roMobile(value) {
    var raw = asText(value).trim();
    if (!raw || raw.length > PHONE_MAX || !WA_ALLOWED_RE.test(raw)) return '';
    if (raw.lastIndexOf('+') > 0) return '';
    var digits = raw.replace(/[^0-9]/g, '');
    if (digits.indexOf('0040') === 0) digits = digits.slice(4);
    else if (digits.indexOf('40') === 0 && digits.length >= 11) digits = digits.slice(2);
    if (digits.charAt(0) === '0') digits = digits.slice(1);
    return /^7\d{8}$/.test(digits) ? '+40' + digits : '';
  }

  /* Adresa PAGINII, fără interogare și fără fragment: `?r=`, `preview_theme_id` sau `view` n-au ce
     căuta într-un email trimis clientului. */
  function pageUrl() {
    var w = getWindow();
    var loc = (w && w.location) || null;
    if (!loc) return '';
    var origin = typeof loc.origin === 'string' ? loc.origin : '';
    var path = typeof loc.pathname === 'string' ? loc.pathname : '';
    if (origin && path) return origin + path;
    return String(loc.href || '').split('#')[0].split('?')[0];
  }

  function createEmail(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var config = ctx.config || {};
    var store = util.safeStorage('session');
    var emailSlot = util.qs(root, '[data-ns-slot="email"]');

    /* Un singur lacăt pentru TOATE formularele de email ale secțiunii: adresa se cere o dată, iar
       o reușită în fereastra de ieșire închide și blocul din rezultat (ruling 3). */
    var sending = false;
    var done = false;
    var doneChannel = 'email'; /* pe ce canal a lăsat omul contactul: 'email' | 'phone' */
    var doneAtGate = false;     /* adresa a fost lăsată ÎN POARTĂ: confirmarea e sus, nu în blocul de jos */
    var token = null;
    var data = null;
    var gen = 0;

    function settings() {
      var k = config.klaviyo;
      return k && typeof k === 'object' ? k : {};
    }
    function publicKey() { return asText(settings().publicKey).trim(); }
    function listId() { return asText(settings().listId).trim(); }
    /* Fără CHEIE și fără LISTĂ nu există integrare: blocul de email nu se randează deloc, fereastra
       de ieșire nu se armează, iar formularul de aglomerare rămâne pe drumul neutru (ruling 1). */
    function configured() { return !!(publicKey() && listId()); }
    function bonusCode() {
      var code = asText(settings().bonusCode).trim();
      return BONUS_CODE_RE.test(code) ? code : '';
    }
    function bonusPct() {
      var pct = wholeNumber(settings().bonusPct, 0, 100);
      return pct === null ? 0 : pct;
    }

    function valid(value) {
      var v = String(value === null || value === undefined ? '' : value).trim();
      return v.length <= EMAIL_MAX && EMAIL_RE.test(v);
    }

    function resultUrl() {
      if (!token || !TOKEN_RE.test(token)) return '';
      var base = pageUrl();
      return base ? base + '?r=' + token : '';
    }

    /* Cererea: aborter + cronometru din registrul instanței (deci resetarea și teardown-ul le
       opresc). Nu respinge NICIODATĂ — răspunsul e mereu un `{ok}` normalizat. */
    function post(url, body) {
      var controller = ctx.aborter();
      var timer = ctx.timer(function () {
        try { controller.abort(); } catch (e) { /* deja oprit */ }
      }, KLAVIYO_TIMEOUT_MS);
      function settle(out) {
        ctx.clearTimer(timer);
        ctx.releaseAborter(controller);
        return out;
      }
      var payload;
      try { payload = JSON.stringify(body); } catch (e) { return Promise.resolve(settle({ ok: false, reason: 'payload' })); }
      return doFetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': KLAVIYO_TYPE, revision: KLAVIYO_REVISION },
        body: payload,
        signal: controller.signal
      }).then(function (res) {
        var status = (res && res.status) || 0;
        return settle(status === 202 ? { ok: true } : { ok: false, reason: 'status_' + status });
      }, function (err) {
        return settle({ ok: false, reason: err && err.name === 'AbortError' ? 'aborted' : 'network' });
      });
    }

    /* Oferta promisă, ca etichetă de profil: „5pct+ghid" (procentul din setări) — după ea filtrează
       Robert în Klaviyo cui îi trimite codul și ghidul. Fără procent rămâne doar ghidul. */
    function offerTag() {
      var pct = bonusPct();
      return pct > 0 ? pct + 'pct+ghid' : 'ghid';
    }

    /* Proprietățile profilului. Pe formularul de aglomerare nu promitem nicio ofertă. */
    function leadProps(channel, source) {
      var props = { source: LEAD_SOURCE, lead_channel: channel === 'phone' ? 'whatsapp' : 'email' };
      if (source !== 'overload') props.offer = offerTag();
      if (channel === 'phone') props.prefers_whatsapp = true;
      return props;
    }

    function subscribeBody(address, source) {
      return {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: util.own(KLAVIYO_SOURCES, source) || KLAVIYO_SOURCES.result,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: address,
                  properties: leadProps('email', source),
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: listId() } } }
        }
      };
    }

    /* Telefonul: aceeași listă, canalul WhatsApp (API-ul client al Klaviyo, revizia 2026-07-15, are
       `subscriptions.whatsapp.marketing`). Consimțământul e bifa de sub câmp, cu textul ei. */
    function phoneSubscribeBody(phone, source) {
      return {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: util.own(KLAVIYO_SOURCES, source) || KLAVIYO_SOURCES.result,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  phone_number: phone,
                  properties: leadProps('phone', source),
                  subscriptions: { whatsapp: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: listId() } } }
        }
      };
    }

    /* Rezerva (abonarea WhatsApp refuzată): doar profilul, cu data acordului, ca dovadă. */
    function phoneProfileBody(phone, source) {
      var props = leadProps('phone', source);
      props.whatsapp_consent_at = new Date().toISOString();
      return { data: { type: 'profile', attributes: { phone_number: phone, properties: props } } };
    }

    /* Ce pleacă spre Klaviyo: numere și cuvinte-cod din rezultat, plus linkul paginii. NICIODATĂ
       poze, object URL-uri, coordonate de reper sau răspunsurile din quiz în afara zonei. */
    function eventProps(source) {
      if (source === 'overload') {
        var only = asText(state.quiz && state.quiz.zone).trim();
        return only ? { zone: only } : {};
      }
      var result = data && typeof data === 'object' ? data : {};
      var props = {};
      var score = wholeNumber(result.nail_health_score, 0, 100);
      if (score !== null) props.score = score;
      var severity = asText(result.severity).trim();
      if (severity) props.severity = severity;
      var zone = asText(result.zone).trim() || asText(state.quiz && state.quiz.zone).trim();
      if (zone) props.zone = zone;
      var affected = wholeNumber(result.affected_nails_count, 0, 40);
      if (affected !== null) props.affected_count = affected;
      var link = resultUrl();
      if (link) props.result_url = link;
      var code = bonusCode();
      if (code) props.bonus_code = code;
      var next = isoDate(new Date(Date.now() + NEXT_SCAN_DAYS * DAY_MS));
      if (next) props.next_scan_date = next;
      /* „Are semne de ciupercă?" — după el poate alege fluxul ce ghid trimite. */
      var signs = asText(result.signs_present).trim();
      if (util.own(SIGNS_KNOWN, signs) === true) props.signs_present = signs;
      props.offer = offerTag();
      /* Scaner v2: eticheta paginii (v1/v2), ca fluxul să poată deosebi variantele. Fără setare, lipsește. */
      var variant = scanVariant(ctx.config);
      if (variant) props.variant = variant;
      return props;
    }

    /* `channel`: 'email' (implicit) sau 'phone' — atunci profilul evenimentului e numărul de telefon,
       iar `channel: 'whatsapp'` îi spune fluxului din Klaviyo pe unde să trimită codul și ghidul. */
    function eventBody(address, source, channel) {
      var props = eventProps(source);
      if (source !== 'overload') props.channel = channel === 'phone' ? 'whatsapp' : 'email';
      var who = channel === 'phone' ? { phone_number: address } : { email: address };
      return {
        data: {
          type: 'event',
          attributes: {
            properties: props,
            metric: { data: { type: 'metric', attributes: { name: source === 'overload' ? METRIC_WAITLIST : METRIC_SCAN } } },
            profile: { data: { type: 'profile', attributes: who } }
          }
        }
      };
    }

    /* Întâi ABONAREA (aia contează pentru client), abia apoi evenimentul. Un eveniment căzut nu are
       voie să strice confirmarea: e telemetrie de marketing, nu o promisiune făcută clientului. */
    function subscribe(options) {
      var opts = options || {};
      var source = asText(opts.source).trim() || 'result';
      if (opts.phone !== undefined && opts.phone !== null) return subscribePhone(opts.phone, source);
      var address = String(opts.email === null || opts.email === undefined ? '' : opts.email).trim().toLowerCase();
      if (!configured()) return Promise.resolve({ ok: false, reason: 'not_configured' });
      if (!valid(address)) return Promise.resolve({ ok: false, reason: 'invalid' });
      var company = encodeURIComponent(publicKey());
      return post(KLAVIYO_SUBSCRIBE_URL + company, subscribeBody(address, source)).then(function (res) {
        if (!res || !res.ok) return res || { ok: false, reason: 'network' };
        post(KLAVIYO_EVENTS_URL + company, eventBody(address, source, 'email'));
        return { ok: true };
      });
    }

    /* Telefonul: abonarea pe WhatsApp; un 4xx (canal nepornit în cont) → profilul cu numărul, tot un
       202 adevărat. Rețeaua căzută sau un 5xx NU trec pe rezervă: acolo nu s-a salvat nimic, deci nici
       confirmarea nu apare. Evenimentul pleacă după oricare dintre cele două reușite. */
    function subscribePhone(value, source) {
      if (!configured()) return Promise.resolve({ ok: false, reason: 'not_configured' });
      var phone = roMobile(value);
      if (!phone) return Promise.resolve({ ok: false, reason: 'invalid' });
      var company = encodeURIComponent(publicKey());
      function after(res) {
        if (!res || !res.ok) return res || { ok: false, reason: 'network' };
        post(KLAVIYO_EVENTS_URL + company, eventBody(phone, source, 'phone'));
        return res;
      }
      return post(KLAVIYO_SUBSCRIBE_URL + company, phoneSubscribeBody(phone, source)).then(function (res) {
        if (res && res.ok) return after({ ok: true });
        if (!res || !/^status_4\d\d$/.test(asText(res.reason))) return res || { ok: false, reason: 'network' };
        return post(KLAVIYO_PROFILES_URL + company, phoneProfileBody(phone, source)).then(function (fallback) {
          return after(fallback && fallback.ok ? { ok: true, via: 'profile' } : fallback);
        });
      });
    }

    /* ── blocul din rezultat ── */

    function bonusHtml() {
      var code = bonusCode();
      if (!code) return '';
      return util.tpl('<p class="ns-bonus" data-ns-slot="bonus">{t}</p>',
        { t: util.fmt(ctx.text('bonus_reveal'), { cod: code, code: code }) });
    }

    function headHtml(withPhone) {
      var text = withPhone && phoneEnabled() && ctx.text('lead_text_phone').trim()
        ? dual(ctx.text('email_text'), ctx.text('lead_text_phone'))
        : util.tpl('{t}', { t: ctx.text('email_text') });
      return util.tpl('<h3 class="ns-email-title">{t}</h3>', { t: ctx.text('email_title') })
        + '<p class="ns-email-text">' + text + '</p>';
    }

    /* ACELEAȘI câmpuri în blocul din rezultat și în poartă (v1.1 §4): se schimbă doar eticheta
       butonului și numele slotului de stare — validarea, bifa și trimiterea sunt unele singure.
       2026-09-24 — „Nu am email": al doilea buton, ALĂTURI de trimitere, comută formularul pe un număr
       de telefon (mobil românesc) și butonul pe „Trimite pe WhatsApp"; din modul telefon, același buton
       întoarce la email. Ambele câmpuri, ambele texte de acord și ambele etichete stau în formular de la
       început (unul ascuns), ca schimbarea să nu mute nimic din jur. Fără textul „Nu am email" în
       setări, formularul rămâne doar pe email, ca înainte. */
    function dual(emailText, phoneText) {
      return util.tpl('<span data-ns-lead-when="email">{e}</span><span data-ns-lead-when="phone" hidden>{p}</span>', { e: emailText, p: phoneText });
    }

    function phoneEnabled() { return !!ctx.text('lead_no_email').trim(); }

    function fieldsHtml(submitKey, statusName) {
      var phone = phoneEnabled();
      var html = util.tpl('<label class="ns-field" data-ns-lead-when="email"><span class="ns-field-label">{p}</span>'
        + '<input class="ns-input" type="email" inputmode="email" autocomplete="email" maxlength="254" required data-ns-email placeholder="{p}"></label>',
      { p: ctx.text('email_placeholder') });
      if (phone) {
        html += util.tpl('<label class="ns-field" data-ns-lead-when="phone" hidden><span class="ns-field-label">{p}</span>'
          + '<input class="ns-input" type="tel" inputmode="tel" autocomplete="tel" maxlength="20" data-ns-phone placeholder="{p}"></label>',
        { p: ctx.text('lead_phone_placeholder') });
      }
      html += '<label class="ns-consent"><input type="checkbox" class="ns-check" required data-ns-email-consent>'
        + '<span class="ns-consent-text">' + (phone ? dual(ctx.text('email_consent'), ctx.text('lead_phone_consent')) : util.tpl('{c}', { c: ctx.text('email_consent') })) + '</span></label>';
      var submit = phone ? dual(ctx.text(submitKey), ctx.text('lead_phone_submit')) : util.tpl('{s}', { s: ctx.text(submitKey) });
      html += '<div class="ns-lead-actions">'
        + '<button type="submit" class="ns-btn ns-btn-primary ns-lead-submit" data-ns-action="email-submit">' + submit + '</button>'
        + (phone ? '<button type="button" class="ns-btn ns-btn-ghost ns-lead-switch" data-ns-action="lead-switch" aria-pressed="false">'
          + dual(ctx.text('lead_no_email'), ctx.text('lead_use_email') || ctx.text('email_placeholder')) + '</button>' : '')
        + '</div>';
      html += util.tpl('<p class="ns-form-status" data-ns-slot="{n}" role="status" aria-live="polite"></p>', { n: statusName });
      return html;
    }

    /* Comută UN formular între email și telefon. Bifa de acord se golește: textul ei s-a schimbat
       (email ↔ WhatsApp), deci acordul trebuie dat din nou pentru canalul ales (GDPR). */
    function setChannel(form, channel) {
      if (!form || !util.own(LEAD_CHANNELS, channel)) return;
      form.setAttribute('data-ns-channel', channel);
      util.qsa(form, '[data-ns-lead-when]').forEach(function (el) {
        if (el.getAttribute('data-ns-lead-when') === channel) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });
      var emailEl = util.qs(form, '[data-ns-email]');
      var phoneEl = util.qs(form, '[data-ns-phone]');
      if (emailEl) { if (channel === 'email') emailEl.setAttribute('required', ''); else emailEl.removeAttribute('required'); }
      if (phoneEl) { if (channel === 'phone') phoneEl.setAttribute('required', ''); else phoneEl.removeAttribute('required'); }
      var consentEl = util.qs(form, '[data-ns-email-consent]');
      if (consentEl) consentEl.checked = false;
      var sw = util.qs(form, '[data-ns-action="lead-switch"]');
      if (sw) sw.setAttribute('aria-pressed', channel === 'phone' ? 'true' : 'false');
      setStatus(util.qs(form, '.ns-form-status'), '', false);
      var target = channel === 'phone' ? phoneEl : emailEl;
      try { if (target && typeof target.focus === 'function') target.focus(); } catch (e) { /* DOM fals */ }
    }

    function channelOf(form) {
      return form && form.getAttribute('data-ns-channel') === 'phone' && util.qs(form, '[data-ns-phone]') ? 'phone' : 'email';
    }

    function successText(channel) {
      if (channel === 'phone') return ctx.text('lead_phone_success').trim() || ctx.text('email_success');
      return ctx.text('email_success');
    }

    function renderBlock() {
      if (!emailSlot) return;
      if (!configured()) { emailSlot.innerHTML = ''; return; }
      if (done && doneAtGate) {
        /* Adresa a fost lăsată ÎN POARTĂ, iar confirmarea stă deja sus, în locul cardului (v1.1 §4:
           „blocul de email din rezultat nu se mai arată"). Aici rămâne doar codul bonus — fără un al
           doilea titlu care cere iar adresa și fără a doua confirmare. */
        var bonus = bonusHtml();
        emailSlot.innerHTML = bonus ? '<div class="ns-email-done">' + bonus + '</div>' : '';
        return;
      }
      if (done) {
        emailSlot.innerHTML = '<div class="ns-email-done" tabindex="-1">'
          + util.tpl('<h3 class="ns-email-title">{t}</h3>', { t: ctx.text('email_title') })
          + util.tpl('<p class="ns-email-ok" role="status">{t}</p>', { t: successText(doneChannel) })
          + bonusHtml() + '</div>';
        return;
      }
      /* Poarta închisă ține ea formularul (același, cu aceleași câmpuri): două formulare pe ecran
         ar cere de două ori același lucru. */
      if (gateClosed) { emailSlot.innerHTML = ''; return; }
      emailSlot.innerHTML = '<form class="ns-email-form ns-lead-form" data-ns-form="result" data-ns-channel="email">' + headHtml(true) + fieldsHtml('email_submit', 'result-status') + '</form>';
    }

    function setStatus(el, text, isError) {
      if (!el) return;
      util.setText(el, text);
      if (el.classList) el.classList[isError ? 'add' : 'remove']('ns-email-error');
    }

    function applyBonus() {
      var code = bonusCode();
      if (!code || !ctx.plan || typeof ctx.plan.setBonus !== 'function') return null;
      try { return ctx.plan.setBonus({ code: code, pct: bonusPct() }); } catch (e) { return null; }
    }

    /* Steagul spune doar PE CE CANAL a lăsat omul un contact ('1' = email, ca până acum; 'phone'),
       ca după o reîncărcare confirmarea să spună „pe WhatsApp", nu „verifică-ți emailul". Niciun număr. */
    function remember(channel) {
      if (!token || !TOKEN_RE.test(token)) return false;
      return store.set(BONUS_FLAG_PREFIX + token, channel === 'phone' ? 'phone' : '1');
    }
    function rememberedChannel() {
      if (!token) return '';
      var v = store.get(BONUS_FLAG_PREFIX + token);
      return v === 'phone' ? 'phone' : (v === '1' ? 'email' : '');
    }

    function exitForms() { return util.qsa(root, '[data-ns-form="exit"]'); }

    function setFormEnabled(form, on) {
      util.qsa(form, 'input').forEach(function (el) {
        el.disabled = !on;
        if (on) el.removeAttribute('disabled'); else el.setAttribute('disabled', '');
      });
      var button = util.qs(form, '[data-ns-action="email-submit"]');
      if (!button) return;
      button.disabled = !on;
      if (on) button.removeAttribute('disabled'); else button.setAttribute('disabled', '');
    }

    /* Formularul din fereastra de ieșire e ACELAȘI formular: o reușită oriunde îl închide și pe
       celălalt, altfel clientul ar fi întrebat a doua oară de aceeași adresă. */
    function markForms() {
      exitForms().forEach(function (form) {
        setFormEnabled(form, false);
        setStatus(util.qs(form, '.ns-form-status'), successText(doneChannel), false);
      });
    }

    /* PERECHEA lui `markForms`, pentru un rezultat nou sau o resetare. Fără ea, formularul
       ferestrei rămânea dezactivat PE VECI: a doua scanare putea deschide o fereastră MOARTĂ —
       câmp blocat, buton blocat și confirmarea veche încă pe ecran. Ce a scris clientul rămâne
       neatins; se schimbă doar starea „închis/deschis" și mesajul. */
    function resetForms() {
      exitForms().forEach(function (form) {
        setFormEnabled(form, true);
        setStatus(util.qs(form, '.ns-form-status'), '', false);
      });
    }

    function succeed(channel) {
      done = true;
      doneChannel = channel === 'phone' ? 'phone' : 'email';
      /* Reținut ÎNAINTE de deblocare: spune blocului de jos că adresa a venit prin poartă. */
      doneAtGate = gateClosed;
      /* Prin `ctx.track`, nu prin `track` de la nivel de modul: garda modului „vizitator" e a instanței.
         Nici adresa, nici numărul nu pleacă spre pixeli — doar canalul, ca nume de conținut. */
      ctx.track.event('CompleteRegistration', { content_name: doneChannel === 'phone' ? 'nail_scan_whatsapp' : 'nail_scan_email' });
      /* v1.4: în istoric ajunge doar FAPTUL că un contact a fost salvat — adresa/numărul, niciodată. */
      if (ctx.history) ctx.history.action(doneChannel === 'phone' ? 'phone_saved' : 'email_saved');
      applyBonus();
      remember(doneChannel);
      markSubscribed();
      renderBlock();
      markForms();
      /* Cine și-a lăsat adresa NU mai e întrebat de un popup în sesiunea asta — nici după o
         scanare nouă. Același steag ca la afișare (cu rezerva în memorie, dacă stocarea e blocată). */
      markExitUsed();
      disarmExit();
      /* Reușita din ORICARE loc de montare deschide și poarta (v1.1 §4) — aici, și numai aici,
         deblochează emailul: `succeed()` rulează doar după un 202 adevărat. */
      if (gateClosed) unlock(doneChannel);
      /* Formularul tocmai a dispărut din pagină, poate cu focusul încă în câmp („Go" de pe tastatura
         telefonului trimite fără să mute focusul), iar un nod scos nu mai emite `focusout`: fără
         asta, `has-keyboard` ar rămâne agățată și bara lipită ar sta stinsă toată vizita. */
      syncKeyboard();
    }

    /* ── poarta de email (v1.1 §4) ──
       După analiză se văd scorul și cardul „Ce pare să fie"; restul rezultatului stă în învelișul
       `[data-ns-gated]` (`hidden` + `inert`), iar în locul lui apare cardul porții. Trei drumuri o
       deschid: emailul (DOAR un 202 adevărat — aceeași `subscribe()` ca blocul din rezultat și
       fereastra de ieșire), WhatsApp (un link adevărat spre numărul magazinului, cu mesajul gata
       scris; nu avem cum ști dacă a fost trimis — asumat) și „sari" (la `soft`, sau oricând după o
       defecțiune a NOASTRĂ: nimeni nu rămâne blocat din vina noastră).
       Poarta există doar dacă avem CE da: Klaviyo configurat SAU un număr de WhatsApp bun — și un
       token, fiindcă fără el nu există link de rezultat. În modul „vizitator" `ns:result` nu se
       emite, deci aici nu se ajunge deloc.
       Deblocarea se ține minte pe TOKEN (`ns:unlocked:<token>`, cu rezervă în memorie dacă stocarea
       e blocată); adresa nu se păstrează nicăieri. */

    var gateSlot = util.qs(root, '[data-ns-slot="gate"]');
    var gatedEl = util.qs(root, '[data-ns-gated]');
    var gateClosed = false;
    var gateSkippable = false;  /* „sari" e la vedere: la `soft`, sau după o defecțiune a noastră */
    var gateLeaving = false;    /* s-a apăsat WhatsApp; DOM-ul se schimbă în clipa următoare */
    var gateShownFor = null;    /* tokenul pentru care am raportat deja afișarea */
    var unlockedHere = {};      /* rezerva din memorie a steagului de deblocare */
    var subscribedHere = false; /* rezerva din memorie a steagului „și-a lăsat adresa" */

    function gateMode() {
      var mode = asText(config.emailGate);
      return util.own(GATE_MODES, mode) ? mode : 'off';
    }

    function gateNumber() {
      var c = config.contact;
      return waNumber(c && typeof c === 'object' ? c.whatsapp : '');
    }

    function gateWanted() {
      if (!gateSlot || !gatedEl || !token) return false;
      if (gateMode() === 'off') return false;
      return configured() || !!gateNumber();
    }

    /* Cine și-a lăsat deja adresa pentru tokenul ăsta (`ns:bonus:`) nu mai are ce poartă să vadă. */
    function isUnlocked() {
      if (!token) return true;
      if (subscribed()) return true;
      if (util.own(unlockedHere, token) === true) return true;
      return store.get(UNLOCK_FLAG_PREFIX + token) === '1' || rememberedChannel() !== '';
    }

    /* Pe SESIUNE, nu pe token: cine ne-a dat deja adresa (202) nu mai e oprit la a doua scanare.
       WhatsApp și „sari" NU ajung aici — ele deblochează doar rezultatul de pe ecran. */
    function subscribed() { return subscribedHere || store.get(GATE_EMAIL_KEY) === '1'; }
    function markSubscribed() {
      subscribedHere = true;
      store.set(GATE_EMAIL_KEY, '1');
    }

    function markUnlocked() {
      if (!token || !TOKEN_RE.test(token)) return false;
      unlockedHere[token] = true;
      return store.set(UNLOCK_FLAG_PREFIX + token, '1');
    }

    /* Mesajul pleacă spre NUMĂRUL MAGAZINULUI, cu linkul de rezultat al proprietarului — cel din
       email (`resultUrl()`: fără `view`, fără `preview_theme_id`). Un mesaj din care comerciantul a
       scos `{link}` îl primește la final: fără link, omul n-ar rămâne cu nimic în conversație. */
    function gateWhatsAppUrl() {
      var number = gateNumber();
      var link = resultUrl();
      if (!number || !link) return '';
      var score = wholeNumber(data && typeof data === 'object' ? data.nail_health_score : null, 0, 100);
      var text = util.fmt(ctx.text('gate_whatsapp_message'), { scor: score === null ? '' : score, link: link }).trim();
      if (text.indexOf(link) < 0) text = text ? text + '\n' + link : link;
      return 'https://wa.me/' + number + '?text=' + encodeURIComponent(text);
    }

    function renderGate() {
      if (!gateSlot) return;
      var withEmail = configured();
      var waUrl = gateWhatsAppUrl();
      var html = util.tpl('<h3 class="ns-gate-title">{t}</h3>', { t: ctx.text('gate_title') });
      /* Textul cardului vorbește despre email; când singura cale e WhatsApp, explică nota lui. */
      var lead = ctx.text('gate_text').trim();
      if (withEmail && lead) html += util.tpl('<p class="ns-gate-text">{t}</p>', { t: lead });
      var benefits = '';
      for (var i = 1; i <= 3; i++) {
        var line = ctx.text('gate_benefit_' + i).trim();
        if (line) benefits += util.tpl('<li class="ns-gate-benefit">{t}</li>', { t: line });
      }
      if (benefits) html += '<ul class="ns-gate-benefits">' + benefits + '</ul>';
      if (withEmail) {
        html += '<form class="ns-email-form ns-lead-form ns-gate-form" data-ns-form="gate" data-ns-channel="email">' + fieldsHtml('gate_submit', 'gate-status') + '</form>';
      }
      /* 2026-09-24: cu Klaviyo, formularul are deja drumul lui de WhatsApp („Nu am email" → numărul
         omului). Linkul spre numărul MAGAZINULUI rămâne doar pe poarta fără Klaviyo — altfel omul ar
         vedea două butoane de WhatsApp care fac lucruri diferite. */
      if (waUrl && !withEmail) {
        var or = ctx.text('gate_or').trim();
        if (withEmail && or) html += util.tpl('<p class="ns-gate-or"><span>{t}</span></p>', { t: or });
        /* Un `<a>` ADEVĂRAT, nu `window.open`: merge și în browserele din aplicațiile Facebook și
           Instagram. Acțiunea doar deblochează; navigarea o face browserul. */
        html += util.tpl('<a class="ns-btn {kind} ns-gate-wa" data-ns-gate="whatsapp" data-ns-action="gate-whatsapp" href="{href}" target="_blank" rel="noopener">{t}</a>',
          { kind: withEmail ? 'ns-btn-ghost' : 'ns-btn-primary', href: waUrl, t: ctx.text('gate_whatsapp_label') });
        var note = ctx.text('gate_whatsapp_note').trim();
        if (note) html += util.tpl('<p class="ns-gate-note">{t}</p>', { t: note });
      }
      /* „Sari" e MEREU în card, dar la `hard` pornește ascuns: îl arată doar o defecțiune a noastră. */
      /* Pe cardul care are DOAR WhatsApp nimeni nu cere vreun email, deci „fără email" n-ar avea sens:
         acolo linkul are textul lui (gol în setări → cel obișnuit, niciodată un buton fără nume). */
      var skipText = withEmail ? '' : ctx.text('gate_skip_whatsapp').trim();
      html += util.tpl('<button type="button" class="ns-link ns-gate-skip" data-ns-action="gate-skip"' + (gateSkippable ? '' : ' hidden') + '>{t}</button>',
        { t: skipText || ctx.text('gate_skip') });
      /* La `hard` nu există „sari", iar „Testează din nou" stă în învelișul ascuns: cine a ales zona
         greșită ar rămâne fără nicio ieșire (reîncărcarea aduce aceeași poartă). Rescanarea nu
         arată nimic din rezultat, deci nu e o scurtătură pe lângă poartă. */
      if (gateMode() === 'hard') {
        html += util.tpl('<button type="button" class="ns-link ns-gate-again" data-ns-action="rescan">{t}</button>', { t: ctx.text('rescan_label') });
      }
      gateSlot.innerHTML = '<div class="ns-gate-card">' + html + '</div>';
    }

    function setGated(on) {
      gateClosed = on;
      state.gated = on;
      if (!gatedEl) return;
      if (on) {
        gatedEl.setAttribute('hidden', '');
        gatedEl.setAttribute('inert', '');
      } else {
        gatedEl.removeAttribute('hidden');
        gatedEl.removeAttribute('inert');
      }
    }

    function emptyGate() {
      if (!gateSlot) return;
      gateSlot.innerHTML = '';
      gateSlot.setAttribute('hidden', '');
    }

    function closeGate() {
      gateLeaving = false;
      gateSkippable = gateMode() === 'soft';
      setGated(true);
      renderGate();
      gateSlot.removeAttribute('hidden');
      ctx.emit('ns:gate', { closed: true });
      if (gateShownFor !== token) {
        gateShownFor = token;
        ctx.track.event('NailScanGateShown', { mode: gateMode() });
      }
    }

    function revealSkip() {
      if (!gateClosed) return;
      gateSkippable = true;
      var link = util.qs(gateSlot, '[data-ns-action="gate-skip"]');
      if (link) link.removeAttribute('hidden');
    }

    /* Primul slot din înveliș care chiar are ceva în el (de obicei estimările): un element MIC de la
       începutul conținutului apărut, bun de țintă pentru derularea minimă de după deblocare. */
    function firstRevealed() {
      var slots = util.qsa(gatedEl, '[data-ns-slot]');
      for (var i = 0; i < slots.length; i++) {
        if (slots[i].parentNode === gatedEl && slots[i].innerHTML !== '') return slots[i];
      }
      return null;
    }

    /* `how`: 'email' | 'whatsapp' | 'skip' | null (nu era nimic de deschis). Conținutul apare sub
       presupunere, iar focusul merge pe ceva care EXISTĂ (butonul apăsat tocmai a dispărut), cu
       `preventScroll`.
       DERULAREA: pagina nu sare nicăieri — nici la scor, nici la începutul cardului. Dar cardul
       porții are ~700 px pe telefon, iar butoanele lui stau jos: cu pagina înțepenită, omul ar
       ateriza la mijlocul listei de poze, fără confirmare și fără începutul a ceea ce a deblocat.
       De-aia `block: 'nearest'` pe un element mic de la ÎNCEPUTUL conținutului apărut: dacă acela
       e deja pe ecran nu se mișcă nimic, altfel pagina urcă exact cât să-l aducă sub antetul lipit
       (`scroll-margin-top` din CSS). */
    /* „Vederea rămâne pe loc" are un dușman: ancorarea derulării (`overflow-anchor`). Măsurat în
       Chromium la 1440 × 900, cu linkul „sari" pe ecran: browserul își alege ca ancoră ceva de SUB
       card (nota legală, subsolul), iar când cele ~3000 px de rezultat apar deasupra ei, ține
       ancora pe loc — adică aruncă omul 3000 px mai jos, în subsol. Cât schimbăm DOM-ul, nimic din
       pagină nu poate fi ancoră; forțăm layoutul cât regula e pusă, apoi o scoatem. */
    function withoutScrollAnchoring(fn) {
      var w = getWindow();
      var html = (w && w.document && w.document.documentElement) || null;
      var style = (html && html.style) || null;
      var before = '';
      try {
        if (style) { before = style.overflowAnchor || ''; style.overflowAnchor = 'none'; }
      } catch (e) { style = null; }
      try { fn(); } finally {
        try { if (html) void html.offsetHeight; } catch (e) { /* DOM fals */ }
        try { if (style) style.overflowAnchor = before; } catch (e) { /* idem */ }
      }
    }

    function openGate(how) {
      var was = gateClosed;
      gateLeaving = false;
      gateSkippable = false;
      if (!was) {
        setGated(false);
        emptyGate();
        return;
      }
      var target = gatedEl;
      withoutScrollAnchoring(function () {
        setGated(false);
        emptyGate();
        /* Formularul stătea în poartă; deschisă fără un contact lăsat („sari", WhatsApp), el coboară
           la locul lui de sub presupunere — o singură dată pe ecran, niciodată două. */
        if (configured()) renderBlock();
        if ((how === 'email' || how === 'phone') && gateSlot) {
          /* Confirmarea existentă, în locul cardului — și doar pe drumul ăsta, adică după un 202. */
          gateSlot.innerHTML = util.tpl('<p class="ns-gate-ok" role="status" tabindex="-1">{t}</p>', { t: successText(how) });
          gateSlot.removeAttribute('hidden');
          target = util.qs(gateSlot, '.ns-gate-ok') || gatedEl;
        }
        ctx.emit('ns:gate', { closed: false, how: how || '' });
      });
      if (target) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        try { if (typeof target.focus === 'function') target.focus({ preventScroll: true }); } catch (e) { /* DOM fals */ }
      }
      var first = (how === 'email' || how === 'phone') && target !== gatedEl ? target : firstRevealed();
      try {
        if (first && typeof first.scrollIntoView === 'function') {
          first.scrollIntoView({ block: 'nearest', behavior: ctx.stages.reducedMotion() ? 'auto' : 'smooth' });
        }
      } catch (e) { /* DOM fals / browser fără opțiuni */ }
      syncKeyboard();
      /* Abia ACUM vede omul rezultatul complet: răbdarea ferestrei de ieșire pornește de aici. */
      if (!done) armExit();
    }

    function unlock(how) {
      if (!gateClosed || state.destroyed) return false;
      markUnlocked();
      openGate(how);
      return true;
    }

    /* ── fereastra de ieșire (exit-intent) ──
       Un singur moment pe sesiune, și doar dacă mai avem ce cere. Pe ecranele cu mouse citim
       intenția din `mouseleave` spre bara de adrese; pe telefon nu există cursor, deci punem o
       SINGURĂ santinelă în istoric și prindem primul „înapoi". A doua santinelă ar ține omul
       captiv în pagină — n-o punem niciodată.

       DOUĂ steaguri, nu unul: `sentinelOut` e contabilitate de istoric („o intrare de-a noastră
       stă în spatele nostru"), iar `sentinelWait` e intenția („următorul `popstate` de pe ea e al
       nostru"). Resetarea stinge INTENȚIA, nu contabilitatea: intrarea rămâne fizic în istoric,
       deci n-avem voie să mai punem una — altfel istoricul ar aduna o santinelă la fiecare
       scanare și omul ar avea nevoie de trei „înapoi" ca să plece. */

    var SENTINEL_TAG = 'ns-exit';
    var modal = util.qs(root, '[data-ns-modal="exit"]');
    var exitShown = false;      /* steagul de rezervă, când `sessionStorage` e blocat */
    var exitArmed = false;
    var exitReady = false;      /* au trecut cele 8 secunde de la randarea rezultatului */
    var exitTimer = null;
    var sentinelOut = false;    /* o intrare de-a NOASTRĂ e în istoric, în spatele nostru */
    var sentinelWait = false;   /* așteptăm `popstate`-ul de pe ea */
    var sentinelBefore = null;  /* starea intrării pe care stăteam ÎNAINTE de push (ca JSON) */
    var exitFocus = null;

    /* „Fără hover" e semnul unui ecran de atins: `pointer: coarse` singur ratează unele
       hibride, iar `hover: hover` singur ratează stilourile. Le folosim pe amândouă. */
    function coarsePointer() {
      var w = getWindow();
      try {
        if (!w || typeof w.matchMedia !== 'function') return false;
        if (w.matchMedia('(pointer: coarse)').matches) return true;
        return !w.matchMedia('(hover: hover)').matches;
      } catch (e) { return false; }
    }

    function exitUsed() { return exitShown || store.get(EXIT_FLAG_KEY) === '1'; }

    function markExitUsed() {
      exitShown = true;
      store.set(EXIT_FLAG_KEY, '1');
    }

    function exitFocusables() {
      return util.qsa(modal, 'input, button, a').filter(function (el) {
        return !isDisabled(el) && !el.hasAttribute('hidden');
      });
    }

    function exitOpen() { return !!modal && !modal.hasAttribute('hidden'); }

    function openExit() {
      if (!modal || exitOpen() || state.destroyed) return false;
      if (!configured() || done || !exitArmed || exitUsed()) return false;
      if (state.stage !== 'result') return false;
      /* Cât e poarta închisă, pagina cere DEJA adresa — o a doua cerere peste ea ar fi zgomot. */
      if (gateClosed) return false;
      var w = getWindow();
      exitFocus = (w && w.document && w.document.activeElement) || null;
      markExitUsed();
      modal.removeAttribute('hidden');
      ctx.lockScroll('exit', true);
      var list = exitFocusables();
      if (list.length) list[0].focus();
      return true;
    }

    /* `restoreFocus` doar pe drumul CERUT de client (butonul, Escape, fundalul) — la curățare
       focusul s-ar muta pe un nod care tocmai dispare din pagină (același tipar ca la lupă). */
    function closeExit(restoreFocus) {
      if (!exitOpen()) return;
      modal.setAttribute('hidden', '');
      ctx.lockScroll('exit', false);
      var back = exitFocus;
      exitFocus = null;
      if (restoreFocus === true && back && typeof back.focus === 'function') back.focus();
    }

    function stopExitTimer() {
      if (exitTimer === null) return;
      ctx.clearTimer(exitTimer);
      exitTimer = null;
    }

    function disarmExit() {
      stopExitTimer();
      exitArmed = false;
      exitReady = false;
    }

    function stateJson(value) {
      try { return JSON.stringify(value === undefined ? null : value); } catch (e) { return null; }
    }
    function isOurSentinel(value) {
      return !!value && typeof value === 'object' && util.own(value, 'ns') === SENTINEL_TAG;
    }

    function pushSentinel() {
      if (sentinelOut) return;
      var w = getWindow();
      var hist = w && w.history;
      if (!hist || typeof hist.pushState !== 'function') return;
      /* STĂM DEJA PE O SANTINELĂ. Se întâmplă într-un webview fără bfcache (Facebook, Instagram):
         rezultat → santinelă → redirect obligatoriu la /cart → „înapoi" → pagina se REÎNCARCĂ pe
         chiar intrarea-santinelă, tokenul din sesiune reface rezultatul și `armExit` rulează din
         nou — dar pe o instanță nouă, unde `sentinelOut` e fals. Fără garda asta, istoricul aduna
         câte o santinelă la fiecare drum spre coș, iar omul avea nevoie de trei „înapoi" ca să
         plece de pe pagină. Intrarea există deja: o luăm în evidență și NU mai punem alta. */
      if (isOurSentinel(hist.state)) {
        sentinelOut = true;
        return;
      }
      try {
        /* Reținem starea de pe care plecăm: la `popstate` ea e singurul semn că „înapoi"-ul ăsta
           a fost chiar de pe santinela noastră, nu de pe intrarea altui script din pagină. */
        sentinelBefore = stateJson(hist.state);
        hist.pushState({ ns: SENTINEL_TAG }, '', (w.location && w.location.href) || '');
        sentinelOut = true;
        sentinelWait = true;
      } catch (e) {
        sentinelOut = false;
        sentinelWait = false;
        sentinelBefore = null;
      }
    }

    function armExit() {
      stopExitTimer();
      exitReady = false;
      exitArmed = configured() && !done && !exitUsed();
      if (!exitArmed) return;
      exitTimer = ctx.timer(function () { exitTimer = null; exitReady = true; }, EXIT_DELAY_MS);
      if (coarsePointer()) pushSentinel();
    }

    function handleSubmit(kind) {
      return function (ev, form) {
        if (state.destroyed || sending || done) return;
        /* Modul „vizitator": nicio adresă nu pleacă nicăieri, nici de pe formularul STATIC al
           ferestrei de ieșire (azi ascuns, dar prezent în DOM) — garda e la sursă. */
        if (state.viewer) return;
        var channel = channelOf(form);
        var emailEl = util.qs(form, '[data-ns-email]');
        var phoneEl = util.qs(form, '[data-ns-phone]');
        var consentEl = util.qs(form, '[data-ns-email-consent]');
        var statusEl = util.qs(form, '.ns-form-status');
        var button = util.qs(form, '[data-ns-action="email-submit"]');
        var value = channel === 'phone'
          ? roMobile(phoneEl && phoneEl.value)
          : String((emailEl && emailEl.value) || '').trim().toLowerCase();

        /* Greșelile de completare NU pun lacătul: clientul trebuie să poată corecta și retrimite.
           Bifa se verifică și aici, nu doar prin `required`: un submit programatic o sare. */
        if (channel === 'phone' && !value) { setStatus(statusEl, ctx.text('lead_phone_invalid') || ctx.text('email_invalid'), true); return; }
        if (channel === 'email' && !valid(value)) { setStatus(statusEl, ctx.text('email_invalid'), true); return; }
        if (!consentEl || consentEl.checked !== true) { setStatus(statusEl, ctx.text('email_consent_required'), true); return; }

        sending = true;
        var myGen = gen;
        if (button) { button.disabled = true; button.setAttribute('disabled', ''); }
        setStatus(statusEl, '', false);

        subscribe(channel === 'phone' ? { phone: value, source: kind } : { email: value, source: kind }).then(function (res) {
          sending = false;
          if (state.destroyed || myGen !== gen) return;
          if (res && res.ok) { succeed(channel); return; }
          setStatus(statusEl, ctx.text('email_error'), true);
          if (button) { button.disabled = false; button.removeAttribute('disabled'); }
          /* Defecțiunea e a NOASTRĂ (Klaviyo, rețea), nu a omului: chiar și la `hard` primește un
             drum mai departe. O adresă scrisă greșit sau bifa lipsă nu ajung aici. */
          revealSkip();
        });
      };
    }

    /* După ce starea emailului s-a hotărât pentru rezultatul NOU (bonus refăcut din sesiune sau nu),
       totalurile planului se rescriu: îndemnul „lasă emailul → 5% la comanda asta" apare doar cât omul
       nu și-a lăsat încă un contact, iar prețul redus doar după. Planul se randează ÎNAINTEA noastră,
       deci ar fi citit starea rezultatului vechi. */
    function onResult(detail) {
      applyResult(detail);
      if (!state.destroyed && ctx.plan && typeof ctx.plan.renderTotals === 'function') ctx.plan.renderTotals();
    }

    function applyResult(detail) {
      if (state.destroyed) return;
      gen += 1;
      sending = false;
      done = false;
      doneAtGate = false;
      var raw = detail && detail.token ? String(detail.token) : '';
      token = TOKEN_RE.test(raw) ? raw : null;
      data = detail ? detail.result : null;
      /* Un rezultat NOU redeschide formularul ferestrei (perechea lui `markForms`). */
      resetForms();
      /* Poarta se hotărăște ÎNAINTE de ieșirile de mai jos: ea există și cu un singur drum
         (doar WhatsApp). Cine vine din linkul din email (`fromLink`) sosește deblocat, iar steagul
         se scrie, ca reîncărcarea — când `r=` nu mai e în adresă — să rămână deblocată. */
      if (token && detail && detail.fromLink === true) markUnlocked();
      if (gateWanted() && !isUnlocked()) closeGate();
      else openGate(null);
      if (!configured()) { if (emailSlot) emailSlot.innerHTML = ''; return; }
      /* FĂRĂ TOKEN nu există link de rezultat (se întâmplă când Worker-ul n-a putut salva:
         `token: null`). Din 2026-09-24 blocul promite codul de reducere și ghidul, nu linkul, deci
         formularul rămâne — Robert vrea măcar un email SAU un telefon de la fiecare om. Doar fereastra
         de plecare (care promite linkul) nu se armează. */
      if (!token) {
        renderBlock();
        disarmExit();
        return;
      }
      /* Reîncărcarea paginii: știm doar că BONUSUL a fost deblocat pentru tokenul ăsta și pe ce canal
         — adresa/numărul nu se păstrează nicăieri (nici în sesiune, nici în local). */
      var before = rememberedChannel();
      if (before) {
        done = true;
        doneChannel = before;
        applyBonus();
      }
      renderBlock();
      /* Cât e poarta închisă, fereastra de ieșire NU se armează (nici santinela din istoric nu se
         pune): o armează `openGate`, când omul ajunge la rezultatul complet. */
      if (!gateClosed) armExit();
    }

    function clear() {
      gen += 1;
      sending = false;
      done = false;
      doneAtGate = false;
      token = null;
      data = null;
      gateLeaving = false;
      gateSkippable = false;
      setGated(false);
      emptyGate();
      if (emailSlot) emailSlot.innerHTML = '';
      resetForms();
      closeExit(false);
      disarmExit();
      /* Stingem INTENȚIA, nu contabilitatea: intrarea pusă de noi rămâne fizic în istoric (n-o
         putem scoate), deci `sentinelOut` rămâne pus și nu mai punem niciodată una nouă. */
      sentinelWait = false;
      sentinelBefore = null;
    }

    /* Tastatura de pe telefon urcă peste jumătate de ecran, iar bara lipită se așază chiar peste
       câmpul în care scrie clientul. Cât timp un câmp are focus, clasa stinge bara din CSS. */
    function isField(el) {
      if (!el || el.nodeType !== 1) return false;
      var tag = String(el.tagName || '').toLowerCase();
      if (tag === 'textarea') return true;
      if (tag !== 'input') return false;
      var type = String(el.getAttribute('type') || 'text').toLowerCase();
      return type !== 'checkbox' && type !== 'radio' && type !== 'button' && type !== 'submit' && type !== 'file';
    }
    function setKeyboard(on) {
      if (root && root.classList) root.classList[on ? 'add' : 'remove']('has-keyboard');
    }
    /* Starea ADEVĂRATĂ, întrebată documentului: un câmp scos din pagină nu mai e „cu focus". */
    function syncKeyboard() {
      var w = getWindow();
      var active = (w && w.document && w.document.activeElement) || null;
      var inside = !!active && (typeof root.contains === 'function' ? root.contains(active) : true);
      setKeyboard(inside && isField(active));
    }

    ctx.actions.registerSubmit('result', handleSubmit('result'));
    ctx.actions.registerSubmit('exit', handleSubmit('exit'));
    ctx.actions.registerSubmit('gate', handleSubmit('gate'));
    ctx.actions.register('lead-switch', function (ev, el) {
      if (state.destroyed || sending || done) return;
      var form = closestAttr(el, 'data-ns-form', root);
      if (!form) return;
      setChannel(form, channelOf(form) === 'phone' ? 'email' : 'phone');
    });
    ctx.actions.register('gate-skip', function () {
      /* Linkul ascuns (la `hard`) nu se poate apăsa, dar acțiunea se poate chema: se verifică aici. */
      if (!gateClosed || !gateSkippable || gateLeaving) return;
      var mode = gateMode();
      if (unlock('skip')) ctx.track.event('NailScanGateSkipped', { mode: mode });
    });
    ctx.actions.register('gate-whatsapp', function () {
      if (!gateClosed || gateLeaving) return;
      gateLeaving = true;
      /* Steagul și evenimentul PE LOC, în gestul omului; cardul dispare în clipa următoare, ca
         browserul să apuce să urmeze linkul de pe un nod care încă e în pagină (webview-uri vechi).
         Nimic spre Klaviyo și nicio abonare pretinsă: `done` rămâne fals. */
      markUnlocked();
      ctx.track.event('NailScanGateWhatsApp', { mode: gateMode() });
      var myGen = gen;
      ctx.timer(function () {
        /* `!gateClosed` = între timp a deschis-o altcineva (un 202 sosit chiar acum): confirmarea
           lui rămâne pe ecran, nu o ștergem. */
        if (state.destroyed || myGen !== gen || !gateClosed) return;
        openGate('whatsapp');
      }, 0);
    });
    ctx.actions.register('exit-close', function () { closeExit(true); });
    /* Butonul de sub total („Vreau reducerea de 5%"): duce la blocul cu emailul și pune cursorul în
       câmpul vizibil (email sau telefon). Cât e poarta închisă, formularul e în cardul ei. */
    ctx.actions.register('bonus-jump', function () {
      if (state.destroyed || done) return;
      var host = gateClosed ? gateSlot : emailSlot;
      var form = host ? util.qs(host, '[data-ns-form]') : null;
      if (!form) return;
      try {
        if (typeof form.scrollIntoView === 'function') form.scrollIntoView({ block: 'center', behavior: ctx.stages.reducedMotion() ? 'auto' : 'smooth' });
      } catch (e) { /* browser fără opțiuni */ }
      var field = util.qs(form, channelOf(form) === 'phone' ? '[data-ns-phone]' : '[data-ns-email]');
      try { if (field && typeof field.focus === 'function') field.focus({ preventScroll: true }); } catch (e) { /* DOM fals */ }
    });
    ctx.on(root, 'ns:result', function (ev) { onResult(ev && ev.detail ? ev.detail : null); });

    /* Cursorul care iese pe SUS din pagină = „plec". Lateral sau în jos înseamnă altceva (o filă,
       bara de scroll, un alt monitor), deci nu deranjăm. */
    var docEl = (function () {
      var w = getWindow();
      return (w && w.document && w.document.documentElement) || null;
    })();
    if (docEl) {
      ctx.on(docEl, 'mouseleave', function (ev) {
        if (!exitReady || coarsePointer()) return;
        var y = ev && typeof ev.clientY === 'number' ? ev.clientY : EXIT_TOP_PX;
        if (y >= EXIT_TOP_PX) return;
        openExit();
      });
    }
    var win = getWindow();
    if (win) {
      ctx.on(win, 'popstate', function () {
        if (!sentinelWait) return;
        var hist = win.history;
        var now = hist ? hist.state : undefined;
        /* Am ajuns pe CHIAR santinela noastră? Atunci altcineva și-a scos intrarea lui de deasupra;
           a noastră e tot acolo și tot așteptăm. */
        if (isOurSentinel(now)) return;
        /* Altfel, „înapoi"-ul e al nostru doar dacă am aterizat exact pe intrarea de pe care am
           plecat când am pus santinela. La îndoială (n-am putut reține starea) NU deschidem nimic:
           mai bine ratăm o ofertă decât să oprim un om care chiar voia să plece. */
        if (sentinelBefore === null || stateJson(now) !== sentinelBefore) return;
        /* Santinela se consumă AICI: următorul „înapoi" chiar duce omul de pe pagină. */
        sentinelOut = false;
        sentinelWait = false;
        sentinelBefore = null;
        openExit();
      });
    }
    if (modal) {
      ctx.on(modal, 'click', function (ev) { if (ev && ev.target === modal) closeExit(true); });
    }
    ctx.on(document, 'keydown', function (ev) {
      if (!exitOpen()) return;
      var key = ev && ev.key;
      if (key === 'Escape' || key === 'Esc') {
        if (typeof ev.preventDefault === 'function') ev.preventDefault();
        closeExit(true);
        return;
      }
      if (key !== 'Tab') return;
      var list = exitFocusables();
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      var inside = ev.target && typeof modal.contains === 'function' ? modal.contains(ev.target) : true;
      if (ev.shiftKey && ev.target === first) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && ev.target === last) { ev.preventDefault(); first.focus(); }
      else if (!inside) { ev.preventDefault(); first.focus(); }
    });
    ctx.on(root, 'focusin', function (ev) { setKeyboard(isField(ev && ev.target)); });
    /* Omul corectează adresa sau numărul: mesajul de eroare de sub butoane dispare pe loc. */
    ctx.on(root, 'input', function (ev) {
      var el = ev && ev.target;
      if (!el || typeof el.hasAttribute !== 'function') return;
      if (!el.hasAttribute('data-ns-email') && !el.hasAttribute('data-ns-phone')) return;
      var form = closestAttr(el, 'data-ns-form', root);
      var status = form ? util.qs(form, '.ns-form-status') : null;
      if (status && status.classList && status.classList.contains('ns-email-error')) setStatus(status, '', false);
    });
    /* `focusout` vine ÎNAINTE de `focusin`-ul următorului câmp: dacă am stinge clasa pe loc, bara
       ar clipi la fiecare tab. Întrebăm documentul în clipa imediat următoare. */
    ctx.on(root, 'focusout', function () {
      ctx.timer(syncKeyboard, 0);
    });
    ctx.onReset(clear);

    return {
      subscribe: subscribe,
      valid: valid,
      configured: configured,
      render: renderBlock,
      clear: clear,
      done: function () { return done; },
      bonusCode: bonusCode,
      bonusPct: bonusPct,
      resultUrl: resultUrl,
      pageUrl: pageUrl,
      token: function () { return token; },
      succeed: succeed,
      openExit: openExit,
      closeExit: closeExit,
      exitOpen: exitOpen,
      armExit: armExit,
      disarmExit: disarmExit,
      exitArmed: function () { return exitArmed; },
      exitUsed: exitUsed,
      sentinel: function () { return sentinelWait; },
      sentinelOut: function () { return sentinelOut; },
      coarsePointer: coarsePointer,
      waNumber: waNumber,
      roMobile: roMobile,
      channel: function () { return doneChannel; },
      gate: {
        mode: gateMode,
        closed: function () { return gateClosed; },
        skippable: function () { return gateSkippable; },
        number: gateNumber,
        whatsappUrl: gateWhatsAppUrl,
        unlock: unlock
      }
    };
  }

  /* ── orderHelp ── (v1.2 §12.3) blocul „comandă cu un om": WhatsApp + telefon, imediat sub butonul
     de comandă. Magazinul vinde cu plata la livrare, iar comenzile le închid operatorii de la telefon —
     deci ăsta e un drum de comandă ADEVĂRAT, nu un „contact" de la coada paginii (linkurile vechi
     din acțiunile de final s-au mutat aici).

     • „Comandă pe WhatsApp" e un `<a target="_blank" rel="noopener">` adevărat spre
       `wa.me/<numărul magazinului>`, cu mesajul gata scris: produsele din comandă (planul + suplimentele
       BIFATE), totalul afișat și linkul de rezultat al proprietarului
       (`email.resultUrl()` — același ca în email și la poartă). Fără token → varianta fără link;
       fără niciun produs disponibil → „aș vrea să vorbesc cu un operator".
     • „Sună-ne: 0799 008 009" e un `tel:+40…`.
     Numerele trec prin ACEEAȘI `waNumber()` ca la poartă; blocul apare dacă măcar unul e bun.
     Mesajul se rescrie la fiecare `ns:totals` (cod bonus dezvăluit, plan schimbat). Evenimentele
     pleacă DOAR prin `ctx.track` (`Contact`), câte unul pe clic; nimic spre Worker sau Klaviyo.
     În modul „vizitator" `ns:result` nu se emite, deci aici nu se ajunge — garda e și la sursă. */

  var ORDER_TITLE_MAX = 60;
  var ORDER_TIMES = '\u00d7';
  /* Iconițele sunt SVG inline: fontul de iconițe vine de pe un CDN care în browserele din aplicații
     poate lipsi, iar un pătrățel gol pe butonul de comandă ar strica tocmai gestul important. */
  /* Desenele: Bootstrap Icons (MIT) — `whatsapp` și `telephone-fill`, aceeași familie ca restul temei. */
  var ORDER_ICON_WA = '<svg class="ns-order-icon" viewBox="0 0 16 16" width="20" height="20" aria-hidden="true" focusable="false"><path fill="currentColor" d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"></path></svg>';
  var ORDER_ICON_CALL = '<svg class="ns-order-icon" viewBox="0 0 16 16" width="20" height="20" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"></path></svg>';

  /* Text SIMPLU pentru un mesaj: fără etichete (formatul de bani al unui magazin poate fi
     `<span class=money>…</span>`), fără rânduri rupte și spații duble. */
  function plainText(value) {
    return asText(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  /* Tăiere la `max` caractere, cu „…" la capăt (inclus în numărătoare). Nu lasă NICIODATĂ jumătate
     de pereche surogat: `encodeURIComponent` aruncă `URIError` pe un surogat orfan, iar butonul de
     comandă ar dispărea din cauza unui emoji din titlul unui produs. */
  function cutText(value, max) {
    /* Titlul e TEXT, nu markup: rămâne cum e (doar spațiile se strâng) — mesajul e text simplu. */
    var text = asText(value).replace(/\s+/g, ' ').trim();
    if (text.length <= max) return text;
    var cut = text.slice(0, max - 1);
    var last = cut.charCodeAt(cut.length - 1);
    if (last >= 0xD800 && last <= 0xDBFF) cut = cut.slice(0, -1);
    return cut.replace(/\s+$/, '') + '\u2026';
  }

  /* Numărul „de citit": forma românească pe care o știe omul („0799 008 009"). Un număr din altă
     țară rămâne internațional — nu-i ghicim gruparea. Grupele sunt legate cu spații NEDESPĂRȚITOARE:
     pe un ecran îngust eticheta se poate rupe înaintea numărului, dar niciodată în mijlocul lui. */
  var PHONE_GAP = '\u00a0';
  function phoneDisplay(digits) {
    if (/^40[0-9]{9}$/.test(digits)) return '0' + digits.slice(2, 5) + PHONE_GAP + digits.slice(5, 8) + PHONE_GAP + digits.slice(8);
    return '+' + digits;
  }

  function createOrderHelp(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var config = ctx.config || {};
    var slot = util.qs(root, '[data-ns-slot="order-help"]');
    /* v1.4: același bloc, pe ecranul „ai făcut deja scanările de azi" — omul care are un rezultat
       trebuie să poată comanda și de-acolo, nu doar după ce se întoarce la rezultat. */
    var limitSlot = util.qs(root, '[data-ns-slot="limit-order"]');
    /* E un rezultat pe ecran? `ns:totals` poate veni și fără (un modul care recalculează după o
       resetare): atunci nu desenăm un bloc de comandă sub o pagină fără rezultat. */
    var active = false;

    function contact() {
      var c = config.contact;
      return c && typeof c === 'object' ? c : {};
    }
    function whatsappNumber() { return waNumber(contact().whatsapp); }
    function phoneNumber() { return waNumber(contact().phone); }

    /* Ce intră în comandă (29.09): planul + suplimentele BIFATE — aceleași din totalul de pe ecran. */
    function products() {
      var list = ctx.plan && typeof ctx.plan.chosen === 'function' ? ctx.plan.chosen() : [];
      var out = [];
      for (var i = 0; i < list.length; i++) {
        var title = cutText(list[i].title, ORDER_TITLE_MAX);
        if (title) out.push(list[i].qty + ORDER_TIMES + ' ' + title);
      }
      return out;
    }

    /* Patru texte, după ce AVEM: produse disponibile și/sau un link de rezultat. Un loc rămas fără
       valoare n-are cum să existe: fiecare variantă are exact locurile pe care le putem umple. */
    function message() {
      var items = products();
      var link = ctx.email && typeof ctx.email.resultUrl === 'function' ? ctx.email.resultUrl() : '';
      var key = items.length
        ? (link ? 'order_whatsapp_message' : 'order_whatsapp_message_nolink')
        : (link ? 'order_whatsapp_message_noplan' : 'order_whatsapp_message_noplan_nolink');
      /* `{cod}` = codul care A DAT totalul de mai sus (al planului sau bonusul — același ca în rândul
         „Reducere" de pe ecran). Operatorul închide comanda de mână și n-are de unde să-l afle:
         bonusul ține de sesiunea omului, deci nici linkul `?r=` nu i-l arată. Fără reducere (sau fără
         niciun produs, deci fără total) locul rămâne GOL — niciodată acolade în mesajul omului. */
      var sums = items.length ? ctx.plan.totals() : null;
      var total = sums ? plainText(ctx.money(sums.total)) : '';
      var code = sums && sums.discount > 0 ? plainText(sums.discountCode) : '';
      var list = items.join(', ');
      var template = ctx.text(key);
      /* Ultimul titlu TĂIAT se termină deja cu „…": punctul pus de text imediat după `{produse}`
         ar da „cuticul…. Total". Cade DOAR punctul acela — restul textului e al comerciantului. */
      if (/\u2026$/.test(list)) template = template.replace(/\{produse\}\./g, '{produse}');
      return util.fmt(template, { produse: list, total: total, link: link, cod: code }).trim();
    }

    function whatsappUrl() {
      var number = whatsappNumber();
      if (!number) return '';
      var text = message();
      if (!text) return 'https://wa.me/' + number;
      try { return 'https://wa.me/' + number + '?text=' + encodeURIComponent(text); } catch (e) { return 'https://wa.me/' + number; }
    }

    function clear() {
      active = false;
      if (slot) slot.innerHTML = '';
      if (limitSlot) limitSlot.innerHTML = '';
    }

    function render() {
      if (!slot) return;
      if (state.destroyed) return;
      if (!active || state.viewer) { slot.innerHTML = ''; return; }
      slot.innerHTML = blockHtml();
    }

    /* Întoarce `true` dacă blocul chiar s-a desenat (există măcar un număr valid). */
    function renderLimit(on) {
      if (!limitSlot || state.destroyed) return false;
      limitSlot.innerHTML = on && !state.viewer ? blockHtml() : '';
      return limitSlot.innerHTML !== '';
    }

    function blockHtml() {
      var buttons = '';
      var waLabel = ctx.text('order_whatsapp_label').trim();
      var waUrl = waLabel ? whatsappUrl() : '';
      if (waUrl) {
        buttons += util.tpl('<a class="ns-btn ns-order-btn ns-order-wa" data-ns-order="whatsapp" data-ns-action="order-whatsapp" href="{href}" target="_blank" rel="noopener">', { href: waUrl })
          + ORDER_ICON_WA + util.tpl('<span>{t}</span></a>', { t: waLabel });
      }
      var phone = phoneNumber();
      var callLabel = phone ? util.fmt(ctx.text('order_call_label'), { telefon: phoneDisplay(phone) }).trim() : '';
      if (callLabel) {
        buttons += util.tpl('<a class="ns-btn ns-btn-ghost ns-order-btn ns-order-call" data-ns-order="call" data-ns-action="order-call" href="tel:+{n}">', { n: phone })
          + ORDER_ICON_CALL + util.tpl('<span>{t}</span></a>', { t: callLabel });
      }
      if (!buttons) return '';
      var html = '';
      var title = ctx.text('order_help_title').trim();
      if (title) html += util.tpl('<h3 class="ns-order-title">{t}</h3>', { t: title });
      /* Fără niciun produs de cumpărat (fără plan sau totul epuizat), chiar sub nota „produsele nu
         sunt disponibile acum", cardul nu promite „să finalizezi comanda": are textul lui. Golit
         din editor → rămâne textul obișnuit. */
      var text = products().length ? '' : ctx.text('order_help_text_noplan').trim();
      if (!text) text = ctx.text('order_help_text').trim();
      if (text) html += util.tpl('<p class="ns-order-text">{t}</p>', { t: text });
      return html + '<div class="ns-order-btns">' + buttons + '</div>';
    }

    /* Acțiunea doar RAPORTEAZĂ: navigarea (wa.me în filă nouă, `tel:`) o face browserul, pe un
       link adevărat — nimic nu e oprit și nimic nu se deschide din JS. */
    function report(name, action) {
      return function () {
        if (state.destroyed || state.viewer) return;
        ctx.track.event('Contact', { content_name: name });
        /* v1.4: și în istoricul din Merlo (doar faptul, legat de tokenul rezultatului). */
        if (ctx.history) ctx.history.action(action);
      };
    }

    ctx.actions.register('order-whatsapp', report('nail_scan_order_whatsapp', 'order_whatsapp'));
    ctx.actions.register('order-call', report('nail_scan_order_call', 'order_call'));
    /* DUPĂ ascultătorii lui `plan`, `cta` și `email` (modulul se creează după ei, în `bind`): la
       `ns:result` planul e deja construit, iar `email` ține deja tokenul rezultatului NOU. */
    ctx.on(root, 'ns:result', function () { active = true; render(); });
    ctx.on(root, 'ns:totals', function () { if (active) render(); });
    ctx.onReset(clear);

    return { render: render, renderLimit: renderLimit, clear: clear, message: message, whatsappUrl: whatsappUrl, products: products, phoneDisplay: phoneDisplay };
  }

  /* ── history ── (v1.4 §B3/§C1) planul PROPUS și ce face omul cu el, spre Worker → Merlo.

     Ce pleacă: produsele de pe ecran (titlu, variantă, cantitate, preț), totalurile EXACT cum le
     calculează `plan.totals()` și, mai târziu, câte un cuvânt pentru fiecare pas al omului
     (`order_whatsapp`, `order_call`, `email_saved`, `share_opened` și, din 29.09, toată pâlnia —
     vezi `createFunnel`). Fiecare cuvânt pleacă O SINGURĂ dată pe token (steag în sesiune, cu rezervă
     în memorie): Worker-ul notează oricum doar prima apariție, iar o cerere în plus ar consuma din
     limita pe IP. Ce NU pleacă, niciodată: poze, adresa de email, telefon, `sid`, nimic din ce ar
     identifica un om.

     `/plan` pleacă O DATĂ pe token, și doar pentru o scanare NOUĂ a proprietarului: un rezultat
     refăcut (`?r=` sau sesiune) l-a trimis deja când a fost făcut, iar în modul „vizitator" nu
     pleacă nimic (garda e în `api.history`). Fără token nu există de ce să legăm planul, deci nu
     trimitem nimic. Totul e „trage și uită": niciun ecran și nicio navigare nu așteaptă după ele. */

  var PLAN_SENT_PREFIX = 'ns:plan:';
  var ACTIONS_SENT_PREFIX = 'ns:act:';
  var HISTORY_TITLE_MAX = 80;
  var HISTORY_ITEMS_MAX = 20;

  function createHistory(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var store = util.safeStorage('session');
    var token = null;
    var sentHere = {};   /* rezerva din memorie a steagului „am trimis", când stocarea e blocată */
    var actedHere = {};  /* la fel, pentru acțiuni: `<token>:<acțiune>` */

    function snapshot() {
      if (!ctx.plan) return null;
      var key = ctx.plan.key();
      var entries = ctx.plan.items();
      if (!key || !entries.length) return null;
      var items = [];
      for (var i = 0; i < entries.length && items.length < HISTORY_ITEMS_MAX; i++) {
        var one = entries[i];
        var title = cutText(one.title, HISTORY_TITLE_MAX);
        /* Un card fără variantă (produs nelegat în editor) sau fără titlu n-are ce căuta într-un
           istoric de produse: Worker-ul ar arunca TOT mesajul din cauza lui. */
        if (one.id === null || !title) continue;
        items.push({
          title: title,
          variant_id: String(one.id),
          qty: one.qty,
          price_cents: one.price,
          compare_at_cents: one.compareAt === null || one.compareAt === undefined ? null : one.compareAt,
          kind: one.step !== null ? 'plan' : 'addon',
          available: one.available === true
        });
      }
      if (!items.length) return null;
      var sums = ctx.plan.totals();
      var discounted = sums.discount > 0 && !!sums.discountCode;
      var out = {
        key: key,
        items: items,
        subtotal_cents: sums.subtotal,
        discount_code: discounted ? sums.discountCode : null,
        discount_pct: discounted ? sums.discountPct : 0,
        total_cents: sums.total,
        currency: 'RON'
      };
      /* Scaner v2: eticheta paginii (v1/v2). Fără setare, cheia lipsește — Merlo o citește ca v1. */
      var variant = scanVariant(ctx.config);
      if (variant) out.variant = variant;
      return out;
    }

    function alreadySent(id) { return sentHere[id] === true || store.get(PLAN_SENT_PREFIX + id) === '1'; }

    function sendPlan() {
      if (state.destroyed || state.viewer || !token || alreadySent(token)) return false;
      var plan = snapshot();
      if (!plan) return false;
      if (!ctx.api.history('plan', { token: token, plan: plan })) return false;
      sentHere[token] = true;
      store.set(PLAN_SENT_PREFIX + token, '1');
      return true;
    }

    function actedList() { return asText(store.get(ACTIONS_SENT_PREFIX + token)).split(','); }
    function acted(name) {
      return util.own(actedHere, token + ':' + name) === true || actedList().indexOf(name) > -1;
    }
    function markActed(name) {
      actedHere[token + ':' + name] = true;
      var list = actedList().filter(function (one) { return !!one && util.own(HISTORY_ACTIONS, one) === 1; });
      if (list.indexOf(name) < 0) list.push(name);
      store.set(ACTIONS_SENT_PREFIX + token, list.join(','));
    }

    function action(name) {
      if (state.destroyed || state.viewer || !token) return false;
      var key = String(name || '');
      if (util.own(HISTORY_ACTIONS, key) !== 1) return false;
      if (acted(key)) return false;
      var sent = false;
      try { sent = ctx.api.history('action', { token: token, action: key }); } catch (e) { sent = false; }
      if (sent) markActed(key);
      return sent;
    }

    /* Modulul se creează ULTIMUL dintre ascultătorii lui `ns:result` (vezi `bind`): planul e deja
       construit, iar un bonus refăcut de `email` e deja în totaluri. */
    ctx.on(root, 'ns:result', function (ev) {
      var detail = (ev && ev.detail) || {};
      var next = detail.token === null || detail.token === undefined ? '' : String(detail.token);
      token = TOKEN_RE.test(next) && !state.viewer ? next : null;
      if (!token || detail.fromToken) return;
      try { sendPlan(); } catch (e) { /* istoricul nu strică rezultatul */ }
    });
    ctx.onReset(function () { token = null; });

    return { snapshot: snapshot, action: action, acted: function (name) { return !!token && acted(String(name || '')); }, token: function () { return token; } };
  }

  /* ── funnel ── (29.09.2026) pâlnia rezultatului, spre Worker, prin `history.action`.

     Robert: „altfel suntem de gard" — trebuie să știm unde se opresc oamenii după rezultat. Fiecare pas
     pleacă O dată pe scanare (deduplicarea e în `history.action`), fără nicio dată personală:
       result_viewed  — la randarea rezultatului;
       lead_viewed    — blocul „5% + ghid" (sau cardul porții) a stat cel puțin pe jumătate pe ecran;
       plan_viewed    — la fel, planul cu produse;
       lead_focus     — cursorul a intrat (sau s-a scris) într-un câmp de email / telefon;
       scroll_50/100  — cât din ETAPA de rezultat a trecut prin ecran (partea de jos a ecranului a
                        ajuns la jumătatea ei / la capătul ei);
       time_30s/90s   — secunde petrecute pe rezultat cu fila VIZIBILĂ (o filă în fundal nu numără).
     Restul pașilor îi raportează modulele lor: `addon_toggled` (plan), `cod_click` + `sticky_click`
     (cta), iar `form_opened` + `code_applied` vin de pe pagina produsului (scan-handoff.js).

     „Pe ecran" se măsoară cu `getBoundingClientRect` la derulare, la redimensionare și o dată pe
     secundă cât curge cronometrul — fără IntersectionObserver: un slot GOL are înălțimea 0, iar
     observatorul raportează o țintă fără arie ca „100% vizibilă". Cronometrul se oprește singur după
     90 de secunde vizibile și nu curge deloc în afara etapei de rezultat. */

  var FUNNEL_TICK_MS = 1000;
  var FUNNEL_TIMES = [{ s: 30, name: 'time_30s' }, { s: 90, name: 'time_90s' }];
  var FUNNEL_BLOCKS = [
    { slot: 'email', name: 'lead_viewed' },
    { slot: 'gate', name: 'lead_viewed' },
    { slot: 'plan', name: 'plan_viewed' }
  ];

  function createFunnel(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var stageEl = util.qs(root, '[data-ns-stage="result"]');
    var active = false;
    var seconds = 0;
    var tickId = null;
    var sent = {};

    function send(name) {
      if (!active || util.own(sent, name) === true) return false;
      if (!ctx.history || typeof ctx.history.action !== 'function') return false;
      sent[name] = true;
      ctx.history.action(name);
      return true;
    }

    function viewportHeight() {
      var w = getWindow();
      var h = w ? Number(w.innerHeight) : 0;
      return isFinite(h) && h > 0 ? h : 0;
    }

    function rectOf(el) {
      try { return el && typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null; } catch (e) { return null; }
    }

    /* Cel puțin jumătate din bloc pe ecran — sau, la un bloc mai înalt decât ecranul, cel puțin
       jumătate de ecran ocupată de el. Un slot gol sau ascuns nu se „vede" niciodată. */
    function halfSeen(el, vh) {
      if (!el || el.innerHTML === '' || el.hasAttribute('hidden')) return false;
      var r = rectOf(el);
      if (!r || !(r.height > 0)) return false;
      var seen = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      return seen > 0 && seen >= Math.min(r.height, vh) * 0.5;
    }

    function check() {
      if (!active || state.destroyed || state.stage !== 'result') return;
      var vh = viewportHeight();
      if (!vh) return;
      for (var i = 0; i < FUNNEL_BLOCKS.length; i++) {
        var block = FUNNEL_BLOCKS[i];
        if (util.own(sent, block.name) === true) continue;
        if (halfSeen(util.qs(root, '[data-ns-slot="' + block.slot + '"]'), vh)) send(block.name);
      }
      var r = rectOf(stageEl);
      if (!r || !(r.height > 0)) return;
      var seenPx = Math.min(r.height, vh - r.top);
      if (seenPx >= r.height * 0.5) send('scroll_50');
      if (seenPx >= r.height * 0.98 || r.bottom <= vh + 2) send('scroll_100');
    }

    function tabVisible() {
      var w = getWindow();
      var doc = w && w.document;
      if (!doc) return true;
      if (doc.visibilityState) return doc.visibilityState === 'visible';
      return doc.hidden !== true;
    }

    function stopTicking() {
      if (tickId !== null) ctx.clearTimer(tickId);
      tickId = null;
    }

    function tick() {
      tickId = null;
      if (!active || state.destroyed || state.stage !== 'result') return;
      if (tabVisible()) {
        seconds += 1;
        for (var i = 0; i < FUNNEL_TIMES.length; i++) if (seconds >= FUNNEL_TIMES[i].s) send(FUNNEL_TIMES[i].name);
      }
      check();
      if (seconds < FUNNEL_TIMES[FUNNEL_TIMES.length - 1].s) tickId = ctx.timer(tick, FUNNEL_TICK_MS);
    }

    function startTicking() {
      if (tickId !== null || !active || state.stage !== 'result') return;
      if (seconds >= FUNNEL_TIMES[FUNNEL_TIMES.length - 1].s) return;
      tickId = ctx.timer(tick, FUNNEL_TICK_MS);
    }

    /* Derularea și redimensionarea se ascultă DOAR cât e un rezultat activ: pe pagina primită
       (`?s=`) sau după „Testează din nou", fereastra nu păstrează niciun ascultător de-al nostru. */
    var win = getWindow();
    var listening = false;
    function listen(on) {
      if (!win || listening === on) return;
      listening = on;
      if (on) {
        ctx.on(win, 'scroll', check, { passive: true });
        ctx.on(win, 'resize', check);
      } else {
        ctx.off(win, 'scroll', check);
        ctx.off(win, 'resize', check);
      }
    }

    function start() {
      stopTicking();
      sent = {};
      seconds = 0;
      active = !state.viewer && !!(ctx.history && ctx.history.token());
      listen(active);
      if (!active) return;
      send('result_viewed');
      check();
      startTicking();
    }

    function stop() {
      active = false;
      stopTicking();
      listen(false);
    }

    function onField(ev) {
      if (!active) return;
      var el = ev && ev.target;
      if (!el || typeof el.hasAttribute !== 'function') return;
      if (el.hasAttribute('data-ns-email') || el.hasAttribute('data-ns-phone')) send('lead_focus');
    }

    /* DUPĂ `history` (vezi `bind`): tokenul rezultatului e deja al lui. */
    ctx.on(root, 'ns:result', start);
    ctx.on(root, 'ns:stage', function () {
      if (state.stage === 'result') { check(); startTicking(); }
      else stopTicking();
    });
    ctx.on(root, 'ns:gate', function () { check(); });
    ctx.on(root, 'focusin', onField);
    ctx.on(root, 'input', onField);
    ctx.onReset(stop);

    return {
      check: check,
      seconds: function () { return seconds; },
      active: function () { return active; },
      sent: function () { var out = []; for (var k in sent) if (Object.prototype.hasOwnProperty.call(sent, k) && sent[k] === true) out.push(k); return out; }
    };
  }

  /* ── callback ── (05.10.2026, scaner v2) cardul „Vrei să te sune un consultant Delfino?".

     Primele zile de reclame: oamenii primesc un rezultat care îi îngrijorează și un plan de ~199 lei,
     dar n-au cu cine vorbi. Aici își lasă PRENUMELE și TELEFONUL, iar fata din call center îi sună
     având în față exact ce au văzut (Merlo, fila „De sunat"). Spec: docs/superpowers/specs/
     2026-10-05-scan-callback-design.md, secțiunea „Contract temă".

     Reguli:
       • există DOAR cu setarea `callback_enabled` (configul `callback.enabled === true`) și doar cu
         slotul în markup (Liquid îl scrie numai cu setarea pornită): pe pagina v1 nu se schimbă NIMIC —
         niciun nod, nicio cerere;
       • apare la `ns:result`, DOAR cu token (fără token nu există cerere de făcut), nu în modul
         „vizitator"; după o cerere trimisă (steag de sesiune `ns:cb:<token>`) nu mai apare la reîncărcare;
       • `callback_viewed` pleacă prin `history.action` (o dată pe token) când cardul intră în ecran;
       • validarea e a noastră (prenume 1–40, mobil românesc prin `roMobile`, bifa obligatorie), cu
         mesajele sub câmp și `aria-invalid` + `aria-describedby`;
       • `POST {worker}/callback` cu `{token, first_name, phone, consent_call:true, variant, place}`,
         `keepalive` + `credentials:'omit'`; DOAR un 204 înseamnă „gata" (confirmarea cu {prenume} și,
         în afara programului, {cand}); altceva → `cb_error` (cu numărul ca link `tel:`), formularul rămâne;
       • pixelul `Schedule` (doar `content_name`) pleacă NUMAI după 204; telefonul și numele nu pleacă
         nicăieri în afară de Worker.
     Programul: luni–vineri 09:00–17:30 (ora României, prin `Intl` cu `timeZone`) → „în ~15 minute";
     altfel „{cand} dimineață, după ora 9": mâine (zi lucrătoare), luni (vineri seara, sâmbătă) sau
     azi (dimineața devreme, înainte de 9). Sărbătorile legale nu se tratează (spec). Ceasul se poate
     înlocui (`setNow`), ca testele să fie deterministe. */

  var CALLBACK_PATH = '/callback';
  var CALLBACK_TIMEOUT_MS = 15000;
  var CALLBACK_NAME_MAX = 40;
  var CALLBACK_FLAG_PREFIX = 'ns:cb:';
  var CALLBACK_PLACES = { result: 1, plan: 1 };
  var CALLBACK_TZ = 'Europe/Bucharest';
  var CALLBACK_OPEN_MIN = 9 * 60;                 /* 09:00 */
  var CALLBACK_PROMISE_UNTIL_MIN = 17 * 60 + 30;  /* 17:30 — ultima promisiune „în ~15 minute" */
  var WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  /* Ziua săptămânii (0 = duminică) și minutul zilei la BUCUREȘTI, oricare ar fi fusul telefonului.
     `Intl.DateTimeFormat` cu `timeZone` e singura cale fără tabel de ore de vară; `hourCycle: 'h23'`
     ca miezul nopții să fie 0, nu 24. Fără Intl (browser foarte vechi) → ora locală a aparatului. */
  function bucharestClock(date) {
    var d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return null;
    try {
      var fmt = new Intl.DateTimeFormat('en-US', { timeZone: CALLBACK_TZ, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
      var parts = fmt.formatToParts(d);
      var day = null;
      var hour = null;
      var minute = null;
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === 'weekday') day = util.own(WEEKDAY_INDEX, parts[i].value);
        else if (parts[i].type === 'hour') hour = parseInt(parts[i].value, 10);
        else if (parts[i].type === 'minute') minute = parseInt(parts[i].value, 10);
      }
      if (day !== null && isFinite(hour) && isFinite(minute)) return { day: day, minutes: (hour % 24) * 60 + minute };
    } catch (e) { /* fără Intl sau fără fusul ăsta: cădem pe ora aparatului */ }
    return { day: d.getDay(), minutes: d.getHours() * 60 + d.getMinutes() };
  }

  /* `{ open, when }`: `open` = promisiunea „în ~15 minute" e valabilă; altfel `when` spune ce cuvânt
     intră în `{cand}`: 'tomorrow' | 'monday' | 'today' (cheile cuvintelor din config). */
  function callbackSchedule(date) {
    var clock = bucharestClock(date);
    if (!clock) return { open: false, when: 'tomorrow' };
    var workday = clock.day >= 1 && clock.day <= 5;
    if (workday && clock.minutes >= CALLBACK_OPEN_MIN && clock.minutes < CALLBACK_PROMISE_UNTIL_MIN) return { open: true, when: '' };
    /* Dimineața devreme într-o zi lucrătoare: echipa începe la 9, deci „azi", nu „mâine". */
    if (workday && clock.minutes < CALLBACK_OPEN_MIN) return { open: false, when: 'today' };
    /* Vineri după 17:30 și sâmbătă → luni; duminică (și luni–joi seara) → mâine. */
    if (clock.day === 5 || clock.day === 6) return { open: false, when: 'monday' };
    return { open: false, when: 'tomorrow' };
  }

  function createCallback(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var config = ctx.config || {};
    var store = util.safeStorage('session');
    var slot = util.qs(root, '[data-ns-slot="callback"]');
    var lineSlot = util.qs(root, '[data-ns-slot="callback-plan"]');
    var ids = util.uid();
    var token = null;
    var place = 'result';   /* 'plan' dacă omul a ajuns la card prin linia de lângă plan */
    var sending = false;
    var done = false;
    var viewedFor = null;   /* tokenul pentru care am raportat deja `callback_viewed` */
    var observer = null;
    var now = function () { return new Date(); };

    function settings() {
      var c = config.callback;
      return c && typeof c === 'object' ? c : {};
    }
    function enabled() { return !!slot && settings().enabled === true; }
    function variant() { return scanVariant(config) || 'v1'; }
    function whenWord(key) {
      var words = settings().when;
      return words && typeof words === 'object' ? asText(util.own(words, key)) : '';
    }
    function privacyUrl() { return safeUrl(settings().privacyUrl); }
    function phoneHref() {
      var digits = waNumber(ctx.text('cb_phone_display'));
      return digits ? 'tel:+' + digits : '';
    }
    function remembered() { return !!token && store.get(CALLBACK_FLAG_PREFIX + token) === '1'; }
    function remember() {
      if (token && TOKEN_RE.test(token)) store.set(CALLBACK_FLAG_PREFIX + token, '1');
    }

    function card() { return slot ? util.qs(slot, '[data-ns-callback]') : null; }
    function form() { return slot ? util.qs(slot, '[data-ns-form="callback"]') : null; }
    function field(name) {
      var f = form();
      return f ? util.qs(f, '[data-ns-cb-' + name + ']') : null;
    }
    function hint(name) {
      var f = form();
      return f ? util.qs(f, '[data-ns-cb-hint="' + name + '"]') : null;
    }

    /* ── markup ── aceeași înfățișare ca blocul de email (`.ns-field`, `.ns-input`, `.ns-consent`).
       Mesajul de eroare al fiecărui câmp stă dedesubt, într-un paragraf cu id, ascuns până e nevoie;
       `aria-describedby` se pune pe câmp DOAR cât e greșit (un id spre un paragraf gol n-ar spune nimic). */
    function fieldHtml(name, labelKey, attrs) {
      var id = 'ns-cb-' + name + '-' + ids;
      return '<div class="ns-field ns-cb-field">'
        + util.tpl('<label class="ns-field-label ns-cb-label" for="{id}">{l}</label>', { id: id, l: ctx.text(labelKey) })
        + util.tpl('<input class="ns-input ns-cb-input" id="{id}" ', { id: id }) + attrs + ' data-ns-cb-' + name + '>'
        + util.tpl('<p class="ns-cb-hint" id="{id}-err" data-ns-cb-hint="{n}" hidden></p>', { id: id, n: name })
        + '</div>';
    }

    function consentHtml() {
      var id = 'ns-cb-consent-' + ids;
      var url = privacyUrl();
      var label = asText(settings().privacyLabel).trim();
      var link = url && label ? ' ' + util.tpl('<a class="ns-cb-privacy" href="{h}" target="_blank" rel="noopener">{t}</a>', { h: url, t: label }) : '';
      return '<div class="ns-cb-field ns-cb-field--consent">'
        + util.tpl('<label class="ns-consent ns-cb-consent" for="{id}"><input type="checkbox" class="ns-check" id="{id}" required data-ns-cb-consent>', { id: id })
        + '<span class="ns-consent-text">' + util.tpl('{t}', { t: ctx.text('cb_consent') }) + link + '</span></label>'
        + util.tpl('<p class="ns-cb-hint" id="{id}-err" data-ns-cb-hint="consent" hidden></p>', { id: id })
        + '</div>';
    }

    function headHtml() {
      /* 05.10.2026 (Robert): consultanța e prezentată ca un CADOU — eticheta + iconița de cadou (clasa
         `ns-cb-card--gift` schimbă receptorul cu cadoul). Eticheta golită → cardul de dinainte. */
      var eyebrow = ctx.text('cb_eyebrow').trim();
      var html = eyebrow ? util.tpl('<p class="ns-cb-eyebrow">{t}</p>', { t: eyebrow }) : '';
      html += util.tpl('<h3 class="ns-cb-title">{t}</h3>', { t: ctx.text('cb_title') });
      var text = ctx.text('cb_text').trim();
      if (text) html += util.tpl('<p class="ns-cb-text">{t}</p>', { t: text });
      return html;
    }

    /* `novalidate`: mesajele sunt ale noastre, sub câmp, în română; balonul nativ al browserului ar
       vorbi peste ele. `required` rămâne, ca cititoarele de ecran să știe ce e obligatoriu. */
    function formHtml() {
      return '<form class="ns-cb-form" data-ns-form="callback" novalidate>'
        + fieldHtml('name', 'cb_name_label', 'type="text" inputmode="text" autocomplete="given-name" autocapitalize="words" maxlength="' + CALLBACK_NAME_MAX + '" required')
        + fieldHtml('phone', 'cb_phone_label', 'type="tel" inputmode="tel" autocomplete="tel" maxlength="' + PHONE_MAX + '" required')
        + consentHtml()
        + util.tpl('<button type="submit" class="ns-btn ns-btn-primary ns-cb-submit" data-ns-cb-submit>{t}</button>', { t: ctx.text('cb_button') })
        + '<p class="ns-form-status ns-cb-status" data-ns-cb-status role="status" aria-live="polite"></p>'
        + '</form>';
    }

    function renderLine() {
      if (!lineSlot) return;
      var text = ctx.text('cb_plan_line').trim();
      /* După cerere linia n-are ce să mai ceară; fără card (token lipsă) n-are unde să ducă. */
      lineSlot.innerHTML = text && !done && card() ? util.tpl('<button type="button" class="ns-link ns-cb-line" data-ns-action="callback-jump">{t}</button>', { t: text }) : '';
    }

    function clearDom() {
      stopObserver();
      if (slot) slot.innerHTML = '';
      if (lineSlot) lineSlot.innerHTML = '';
    }

    function render() {
      if (!slot) return;
      if (state.destroyed || !enabled() || !token || state.viewer || remembered()) { clearDom(); return; }
      var gift = ctx.text('cb_eyebrow').trim() ? ' ns-cb-card--gift' : '';
      slot.innerHTML = util.tpl('<div class="ns-cb-card{g}" data-ns-callback data-ns-cb-variant="{v}">', { g: gift, v: variant() }) + headHtml() + formHtml() + '</div>';
      renderLine();
      watch();
    }

    /* ── `callback_viewed` ── cardul a intrat în ecran: IntersectionObserver, o dată pe token (și
       `history.action` deduplică oricum pe sesiune). Fără observator (browser vechi) → la randare. */
    function stopObserver() {
      if (!observer) return;
      try { observer.disconnect(); } catch (e) { /* deja plecat */ }
      observer = null;
    }

    function reportViewed() {
      if (!token || viewedFor === token) return;
      viewedFor = token;
      if (ctx.history && typeof ctx.history.action === 'function') ctx.history.action('callback_viewed');
    }

    function watch() {
      stopObserver();
      var el = card();
      if (!el) return;
      var w = getWindow();
      var IO = w && w.IntersectionObserver;
      if (typeof IO !== 'function') { reportViewed(); return; }
      try {
        observer = new IO(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i] && entries[i].isIntersecting) { reportViewed(); stopObserver(); return; }
          }
        }, { threshold: 0.25 });
        observer.observe(el);
      } catch (e) {
        observer = null;
        reportViewed();
      }
    }

    /* ── validare ── */
    function setFieldError(name, message) {
      var input = field(name);
      var el = hint(name);
      if (!el) return;
      if (message) {
        util.setText(el, message);
        el.removeAttribute('hidden');
        if (input) {
          input.setAttribute('aria-invalid', 'true');
          input.setAttribute('aria-describedby', el.getAttribute('id') || '');
        }
      } else {
        util.setText(el, '');
        el.setAttribute('hidden', '');
        if (input) {
          input.removeAttribute('aria-invalid');
          input.removeAttribute('aria-describedby');
        }
      }
    }

    function validate() {
      var nameEl = field('name');
      var phoneEl = field('phone');
      var consentEl = field('consent');
      var name = asText(nameEl && nameEl.value).replace(/\s+/g, ' ').trim();
      var phone = roMobile(phoneEl && phoneEl.value);
      var consent = !!(consentEl && consentEl.checked === true);
      var first = null;
      if (!name || name.length > CALLBACK_NAME_MAX) { setFieldError('name', ctx.text('cb_err_name')); first = first || nameEl; } else setFieldError('name', '');
      if (!phone) { setFieldError('phone', ctx.text('cb_err_phone')); first = first || phoneEl; } else setFieldError('phone', '');
      if (!consent) { setFieldError('consent', ctx.text('cb_err_consent')); first = first || consentEl; } else setFieldError('consent', '');
      return { ok: !first, name: name, phone: phone, first: first };
    }

    function setStatus(html) {
      var f = form();
      var el = f ? util.qs(f, '[data-ns-cb-status]') : null;
      if (!el) return;
      el.innerHTML = html || '';
      if (el.classList) el.classList[html ? 'add' : 'remove']('ns-cb-status--error');
    }

    function setFormEnabled(f, on) {
      util.qsa(f, 'input').forEach(function (el) {
        el.disabled = !on;
        if (on) el.removeAttribute('disabled'); else el.setAttribute('disabled', '');
      });
      var button = util.qs(f, '[data-ns-cb-submit]');
      if (!button) return;
      button.disabled = !on;
      if (on) { button.removeAttribute('disabled'); button.removeAttribute('aria-busy'); }
      else { button.setAttribute('disabled', ''); button.setAttribute('aria-busy', 'true'); }
    }

    /* ── trimiterea ── `fetch` cu `keepalive` + `credentials: 'omit'`, ca restul drumurilor spre
       Worker, dar aici AȘTEPTĂM răspunsul: doar un 204 e o promisiune pe care o putem face omului.
       Aborter + cronometru din registrul instanței (resetarea și teardown-ul le opresc). Nu respinge. */
    function post(body) {
      var base = ctx.api && typeof ctx.api.base === 'string' ? ctx.api.base : '';
      if (!base || !hasFetch()) return Promise.resolve({ ok: false, reason: 'not_configured' });
      var payload;
      try { payload = JSON.stringify(body); } catch (e) { return Promise.resolve({ ok: false, reason: 'payload' }); }
      var controller = ctx.aborter();
      var timer = ctx.timer(function () {
        try { controller.abort(); } catch (e) { /* deja oprit */ }
      }, CALLBACK_TIMEOUT_MS);
      function settle(out) {
        ctx.clearTimer(timer);
        ctx.releaseAborter(controller);
        return out;
      }
      return doFetch(base + CALLBACK_PATH, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        signal: controller.signal
      }).then(function (res) {
        var status = (res && res.status) || 0;
        return settle(status === 204 ? { ok: true } : { ok: false, reason: 'status_' + status });
      }, function (err) {
        return settle({ ok: false, reason: err && err.name === 'AbortError' ? 'aborted' : 'network' });
      });
    }

    /* Textul de eroare, cu numărul afișat (dacă apare în el) ca link `tel:`. Fiecare bucată de text
       e escapată; markupul linkului e al nostru. */
    function errorHtml() {
      var text = ctx.text('cb_error');
      var display = ctx.text('cb_phone_display').trim();
      var href = phoneHref();
      if (!href || !display) return util.esc(text);
      var link = util.tpl('<a class="ns-cb-tel" href="{h}">{d}</a>', { h: href, d: display });
      if (text.indexOf(display) > -1) return text.split(display).map(function (part) { return util.esc(part); }).join(link);
      return util.esc(text) + ' ' + link;
    }

    function succeed(name) {
      done = true;
      remember();
      var when = callbackSchedule(now());
      var text = util.fmt(ctx.text(when.open ? 'cb_done_open' : 'cb_done_closed'), { prenume: name, cand: whenWord(when.when) });
      stopObserver();
      if (slot) {
        slot.innerHTML = '<div class="ns-cb-card is-done" data-ns-callback data-ns-cb-done="' + (when.open ? 'open' : 'closed') + '">'
          + util.tpl('<h3 class="ns-cb-title">{t}</h3>', { t: ctx.text('cb_title') })
          + util.tpl('<p class="ns-cb-ok" role="status" tabindex="-1">{t}</p>', { t: text })
          + '</div>';
        var ok = util.qs(slot, '.ns-cb-ok');
        try { if (ok) ok.focus({ preventScroll: true }); } catch (e) { try { if (ok) ok.focus(); } catch (err) { /* DOM fals */ } }
      }
      renderLine();
      /* Prin `ctx.track` (garda modului „vizitator"). Doar numele conținutului — niciun telefon, niciun nume. */
      ctx.track.event('Schedule', { content_name: 'nail_scan_callback' });
    }

    function fail(f) {
      setFormEnabled(f, true);
      setStatus(errorHtml());
    }

    function submit(ev, f) {
      if (state.destroyed || state.viewer || sending || done || !token || !f) return;
      var checked = validate();
      if (!checked.ok) {
        try { if (checked.first && typeof checked.first.focus === 'function') checked.first.focus(); } catch (e) { /* DOM fals */ }
        return;
      }
      sending = true;
      setStatus('');
      setFormEnabled(f, false);
      var sentFor = token;
      var body = {
        token: token,
        first_name: checked.name,
        phone: checked.phone,
        consent_call: true,
        variant: variant(),
        place: util.own(CALLBACK_PLACES, place) === 1 ? place : 'result'
      };
      post(body).then(function (res) {
        sending = false;
        /* Rezultat nou sau resetare în timpul cererii: răspunsul nu mai are pe ce să cadă. */
        if (state.destroyed || token !== sentFor || !form()) return;
        if (res && res.ok) succeed(checked.name);
        else fail(f);
      });
    }

    /* Linia de lângă plan: derulează la card și pune cursorul pe prenume; de-aici încolo cererea
       poartă `place: 'plan'`. */
    function jump() {
      if (state.destroyed) return;
      var el = card();
      if (!el || done) return;
      place = 'plan';
      var reduce = false;
      try {
        var w = getWindow();
        reduce = !!(w && w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches);
      } catch (e) { reduce = false; }
      try { if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }); } catch (e) { try { el.scrollIntoView(); } catch (err) { /* DOM fals */ } }
      var input = field('name');
      try { if (input) input.focus({ preventScroll: true }); } catch (e) { try { if (input) input.focus(); } catch (err) { /* DOM fals */ } }
    }

    /* Mesajul unui câmp dispare de cum omul îl corectează. */
    ctx.on(root, 'input', function (ev) {
      var el = ev && ev.target;
      if (!el || typeof el.hasAttribute !== 'function') return;
      if (el.hasAttribute('data-ns-cb-name')) setFieldError('name', '');
      else if (el.hasAttribute('data-ns-cb-phone')) setFieldError('phone', '');
    });
    ctx.actions.registerChange('data-ns-cb-consent', function (ev, el) {
      if (el && el.checked === true) setFieldError('consent', '');
    });
    ctx.actions.registerSubmit('callback', submit);
    ctx.actions.register('callback-jump', jump);

    ctx.on(root, 'ns:result', function (ev) {
      var detail = (ev && ev.detail) || {};
      var next = detail.token === null || detail.token === undefined ? '' : String(detail.token);
      token = TOKEN_RE.test(next) && !state.viewer ? next : null;
      place = 'result';
      sending = false;
      done = false;
      render();
    });
    ctx.onReset(function () {
      token = null;
      place = 'result';
      sending = false;
      done = false;
      viewedFor = null;
      clearDom();
    });

    return {
      enabled: enabled,
      schedule: callbackSchedule,
      clock: bucharestClock,
      setNow: function (fn) { now = typeof fn === 'function' ? fn : function () { return new Date(); }; },
      token: function () { return token; },
      place: function () { return place; },
      done: function () { return done; },
      sending: function () { return sending; },
      viewed: function () { return !!token && viewedFor === token; },
      variant: variant,
      phoneHref: phoneHref,
      validate: validate,
      render: render
    };
  }

  /* ── progress ── (sarcina 68) `{token, score, date}` în localStorage și linia „față de scanarea
     trecută".

     Intrarea citită din localStorage e date NEDE ÎNCREDERE: o poate scrie orice extensie, orice
     altă filă sau o versiune mai veche a modulului. Fiecare câmp e verificat (proprietate PROPRIE,
     număr finit în interval, dată calendaristică reală) înainte să ajungă într-un calcul sau pe
     ecran; orice altceva înseamnă „nu există intrare".

     Diferența de timp se măsoară în ZILE ÎNTREGI, pe UTC: la trecerea la ora de vară o diferență în
     ore ar da 30,96 zile, iar „acum 30 de zile" ar deveni „acum 31". */

  var PROGRESS_KEY = 'ns:progress';

  /* Numărul zilei calendaristice (UTC) dintr-un `YYYY-MM-DD`. `null` = nu e o dată. */
  function dayNumber(value) {
    var text = asText(value).trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
    var ms = Date.parse(text + 'T00:00:00Z');
    if (typeof ms !== 'number' || !isFinite(ms)) return null;
    /* `Date.parse` înghite și „2026-13-45": verificăm că data chiar s-a păstrat. */
    return isoDate(new Date(ms)) === text ? Math.round(ms / DAY_MS) : null;
  }

  function createProgress(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var store = util.safeStorage('local');
    var slot = util.qs(root, '[data-ns-slot="progress"]');

    function read() {
      var raw = store.get(PROGRESS_KEY);
      if (!raw) return null;
      var parsed;
      try { parsed = JSON.parse(String(raw)); } catch (e) { return null; }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      var score = util.own(parsed, 'score');
      var date = util.own(parsed, 'date');
      if (typeof score !== 'number' || !isFinite(score) || score < 0 || score > 100) return null;
      var day = dayNumber(date);
      if (day === null) return null;
      var token = util.own(parsed, 'token');
      return { token: typeof token === 'string' ? token : null, score: Math.round(score), day: day };
    }

    function write(token, score) {
      if (state.viewer) return false; /* scorul altcuiva nu e „scanarea mea trecută" (v1.1 §3a) */
      var today = isoDate(new Date());
      if (!today) return false;
      var entry;
      try {
        entry = JSON.stringify({ token: token || null, score: score, date: today });
      } catch (e) { return false; }
      return store.set(PROGRESS_KEY, entry);
    }

    function lineHtml(delta, days) {
      var key = delta > 0 ? 'progress_up' : (delta < 0 ? 'progress_down' : 'progress_same');
      var mod = delta > 0 ? 'is-up' : (delta < 0 ? 'is-down' : 'is-same');
      var icon = delta > 0 ? 'bi-arrow-up-right' : (delta < 0 ? 'bi-arrow-down-right' : 'bi-dash-lg');
      /* Aceleași valori sub două nume: `{delta}`/`{zile}` (implicitele din setări, în română) și
         `{n}`/`{d}` (numele din spec) — comerciantul poate folosi ce vrea. */
      var text = util.fmt(ctx.text(key), { delta: delta, zile: days, n: delta, d: days });
      return util.tpl('<p class="ns-progress-title">{t}</p>', { t: ctx.text('progress_title') })
        + util.tpl('<p class="ns-progress-line {m}"><i class="bi {i}" aria-hidden="true"></i><span>{s}</span></p>',
          { m: mod, i: icon, s: text });
    }

    function clear() { if (slot) slot.innerHTML = ''; }

    function render(detail) {
      if (state.destroyed) return;
      clear();
      var info = detail || {};
      var result = info.result && typeof info.result === 'object' ? info.result : {};
      var score = wholeNumber(result.nail_health_score, 0, 100);
      if (score === null) return;
      var raw = info.token ? String(info.token) : '';
      var token = TOKEN_RE.test(raw) ? raw : null;

      /* Un rezultat încărcat dintr-un link (`?r=`) sau de după o reîncărcare NU e o scanare nouă:
         nu are voie nici să arate o evoluție inventată, nici să înlocuiască intrarea salvată. */
      if (info.fromToken) return;

      var previous = read();
      /* Aceeași scanare randată încă o dată (o a doua emitere de `ns:result`) — nu o numărăm. */
      if (previous && token && previous.token === token) return;

      /* ÎNTÂI linia pentru intrarea veche, ABIA APOI intrarea nouă (altfel scanarea de acum ar
         deveni „scanarea trecută" a ei înseși). */
      var today = dayNumber(isoDate(new Date()));
      if (previous && today !== null) {
        var days = today - previous.day;
        /* Zile întregi: în aceeași zi (sau cu ceasul dat înainte) n-avem ce afirma. */
        if (days >= 1 && slot) slot.innerHTML = lineHtml(score - previous.score, days);
      }
      write(token, score);
    }

    ctx.on(root, 'ns:result', function (ev) { render(ev && ev.detail ? ev.detail : null); });
    ctx.onReset(clear);

    return { render: render, clear: clear, read: read, write: write, key: PROGRESS_KEY };
  }

  /* ── actions ── (sarcina 68) „Testează din nou" și trimiterea rezultatului. (CTA-urile de contact
     au plecat de aici în v1.2: WhatsApp și telefonul sunt acum un drum de COMANDĂ, în `orderHelp`.)

     TRIMITEREA (v1.1 §3b). Se trimite un link AUTONOM `?s=v1.…` (scor, etichetă, zonă, cele 7
     criterii — vezi `encodeShared`), niciodată linkul cu token (`?r=` l-ar face pe cel care îl
     primește „proprietar") și cu atât mai puțin poza. Butonul deschide un PANOU în pagină — nu o
     fereastră modală — care arată exact ce pleacă, apoi, mereu în aceeași ordine:
       1. „Trimite pe WhatsApp" — un `<a href="https://wa.me/?text=…">` adevărat: merge și în
          browserele din aplicațiile Facebook/Instagram, unde `navigator.share` de obicei lipsește;
       2. „Trimite altfel" — doar dacă există `navigator.share`, chemat SINCRON în handlerul de clic
          (după un `await` gestul utilizatorului e pierdut și Safari refuză). `AbortError` = omul a
          închis meniul: tăcere. Orice altă eroare → copiere;
       3. „Copiază linkul" — mereu. Clipboard lipsă sau refuzat → un câmp `readonly` cu linkul și
          explicația `share_manual`. NICIODATĂ un „link copiat" care nu s-a întâmplat;
       4. „Renunț".
     „Testează din nou" e și singurul drum înapoi dintr-un rezultat deschis dintr-un link, deci
     curăță și urma: tokenul din sesiune ȘI `?r=` din adresă. */

  var SHARE_VIEW_RE = /^[a-z0-9_-]{1,40}$/i;
  var THEME_ID_RE = /^[0-9]{1,20}$/;

  /* Adresa de la care pornește linkul trimis — funcție NOUĂ, separată de `pageUrl()` (care rămâne
     a emailului: acolo n-au ce căuta parametri de previzualizare). Pe tema publicată iese un link
     curat, `origin + pathname`. Pe o temă-draft scanerul stă de obicei pe alt șablon al unei pagini
     existente (`/pages/contact?view=nail-scan`) — fără `view` și fără `preview_theme_id`, cel care
     primește linkul ar vedea pagina „Contact" de pe tema publicată. Amândouă se iau DOAR din surse
     verificate: `view` din adresa curentă, dacă arată a nume de șablon; id-ul din `Shopify.theme`,
     dacă e numeric și tema nu e cea publicată. Niciodată `r=`, UTM-uri sau fragmentul. */
  function shareBase() {
    var base = pageUrl();
    if (!base) return '';
    var w = getWindow();
    var params = [];
    var found = /[?&]view=([^&#]*)/.exec(String((w && w.location && w.location.search) || ''));
    if (found) {
      var view = '';
      try { view = decodeURIComponent(found[1]); } catch (e) { view = ''; }
      if (SHARE_VIEW_RE.test(view)) params.push('view=' + view);
    }
    var theme = w && w.Shopify && w.Shopify.theme;
    if (theme && typeof theme === 'object' && theme.role !== 'main') {
      var id = typeof theme.id === 'number' || typeof theme.id === 'string' ? String(theme.id) : '';
      if (THEME_ID_RE.test(id)) params.push('preview_theme_id=' + id);
    }
    return base + (params.length ? '?' + params.join('&') : '');
  }

  function createEndActions(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var slot = util.qs(root, '[data-ns-slot="actions"]');
    /* Rezultatul de pe ecran, luat din `ns:result` (nu din `state.result`, pe care îl pun doar
       drumurile reale: o randare directă ar trimite altfel un link gol). */
    var current = null;
    /* Semn de generație: un clipboard sau un meniu nativ care răspunde DUPĂ o resetare sau o
       randare nouă nu mai are voie să scrie nimic. */
    var gen = 0;

    function nav() {
      var w = getWindow();
      return (w && w.navigator) || (typeof navigator !== 'undefined' ? navigator : null);
    }
    function canShare() {
      var n = nav();
      return !!(n && typeof n.share === 'function');
    }
    function canCopy() {
      var n = nav();
      return !!(n && n.clipboard && typeof n.clipboard.writeText === 'function');
    }
    function panelEl() { return util.qs(slot, '[data-ns-slot="share-panel"]'); }
    function triggerEl() { return util.qs(slot, '[data-ns-action="share"]'); }

    /* ── ce se trimite ── */

    function shareUrl() {
      var base = shareBase();
      if (!base) return '';
      var code = encodeShared(current);
      if (!code) return base;
      return base + (base.indexOf('?') > -1 ? '&' : '?') + 's=' + code;
    }

    /* Scorul apare în mesaj doar când e o veste bună („sănătos", „ușor"); altfel mesajul e fără
       cifre — omul alege singur dacă vrea să-l arate. Un rezultat care nu încape în link (n-ar
       trebui să existe: Worker-ul îl repară) trimite doar invitația. */
    function message() {
      if (!encodeShared(current)) return ctx.text('share_text');
      var score = wholeNumber(current.nail_health_score, 0, 100);
      if (util.own(SHARE_GOOD, asText(current.severity)) && score !== null) {
        return util.fmt(ctx.text('share_text_score'), { scor: score });
      }
      return ctx.text('share_text_plain');
    }

    function payload() {
      var url = shareUrl();
      var text = message();
      return { url: url, text: text, both: text ? text + '\n' + url : url };
    }

    /* ── panoul ── */

    function status(text) {
      util.setText(util.qs(slot, '[data-ns-slot="share-status"]'), text);
    }

    function hideManual() {
      var el = util.qs(slot, '[data-ns-slot="share-manual"]');
      if (!el) return;
      el.innerHTML = '';
      el.setAttribute('hidden', '');
    }

    /* Copierea n-a mers (sau nu există): linkul într-un câmp din care omul îl poate copia cu
       degetul, plus explicația. Nu pretindem nimic. */
    function showManual(url) {
      status('');
      var el = util.qs(slot, '[data-ns-slot="share-manual"]');
      if (!el) return;
      el.innerHTML = util.tpl('<p class="ns-share-manual-text">{t}</p>', { t: ctx.text('share_manual') })
        + util.tpl('<input class="ns-input ns-share-field" type="text" readonly value="{url}" aria-label="{label}">',
          { url: url, label: ctx.text('share_copy_label') });
      el.removeAttribute('hidden');
      var field = util.qs(el, 'input');
      try {
        if (field && typeof field.focus === 'function') field.focus();
        if (field && typeof field.select === 'function') field.select();
      } catch (e) { /* DOM fals / câmp fără selecție */ }
    }

    function isOpen() {
      var panel = panelEl();
      return !!panel && !panel.hasAttribute('hidden');
    }

    function openPanel() {
      var panel = panelEl();
      if (!panel || state.destroyed) return;
      /* A doua apăsare nu închide și nu redesenează: pe telefon două atingeri vin des una după
         alta, iar un panou care apare și dispare pe loc arată a buton stricat. */
      if (isOpen()) return;
      var p = payload();
      if (!p.url) return;
      if (ctx.history) ctx.history.action('share_opened');
      var html = util.tpl('<h3 class="ns-share-title" tabindex="-1">{t}</h3>', { t: ctx.text('share_panel_title') });
      var intro = ctx.text('share_panel_text').trim();
      if (intro) html += util.tpl('<p class="ns-share-text">{t}</p>', { t: intro });
      var label = ctx.text('share_message_label').trim();
      if (label) html += util.tpl('<p class="ns-share-label">{t}</p>', { t: label });
      html += '<blockquote class="ns-share-quote" data-ns-slot="share-message">'
        + (p.text ? util.tpl('<p class="ns-share-msg">{t}</p>', { t: p.text }) : '')
        + util.tpl('<p class="ns-share-url">{u}</p>', { u: p.url })
        + '</blockquote>';
      html += util.tpl('<a class="ns-btn ns-btn-primary ns-share-wa" data-ns-share="whatsapp" href="https://wa.me/?text={q}" target="_blank" rel="noopener">{t}</a>',
        { q: encodeURIComponent(p.both), t: ctx.text('share_whatsapp_label') });
      if (canShare()) {
        html += util.tpl('<button type="button" class="ns-btn ns-btn-ghost" data-ns-action="share-native">{t}</button>', { t: ctx.text('share_other_label') });
      }
      html += util.tpl('<button type="button" class="ns-btn ns-btn-ghost" data-ns-action="share-copy">{t}</button>', { t: ctx.text('share_copy_label') });
      /* Confirmarea stă LIPITĂ de „Copiază linkul", nu sub „Renunț": acolo, pe un telefon mic, „Link
         copiat" ajungea sub bara lipită a coșului și apăsarea părea că n-a făcut nimic. Ordinea
         butoanelor nu se schimbă.
         Câmpul de copiat cu degetul rămâne ULTIMUL, sub „Renunț": primește focus (deci browserul
         derulează la el), iar un buton pus SUB un câmp cu focus ajunge pe marginea de jos a
         ecranului exact când bara lipită reapare sub deget (`has-keyboard` se stinge la `focusout`) —
         măsurat în e2e: apăsarea pe „Renunț" nu mai ajungea la buton. */
      html += '<p class="ns-share-status" data-ns-slot="share-status" role="status" aria-live="polite"></p>';
      html += util.tpl('<button type="button" class="ns-link ns-share-cancel" data-ns-action="share-cancel">{t}</button>', { t: ctx.text('share_cancel_label') });
      html += '<div class="ns-share-manual" data-ns-slot="share-manual" hidden></div>';
      panel.innerHTML = html;
      panel.removeAttribute('hidden');
      var trigger = triggerEl();
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      var title = util.qs(panel, '.ns-share-title');
      try { if (title && typeof title.focus === 'function') title.focus(); } catch (e) { /* DOM fals */ }
    }

    function closePanel(restoreFocus) {
      gen += 1;
      var panel = panelEl();
      if (panel) {
        panel.innerHTML = '';
        panel.setAttribute('hidden', '');
      }
      var trigger = triggerEl();
      if (!trigger) return;
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus === true) {
        try { if (typeof trigger.focus === 'function') trigger.focus(); } catch (e) { /* DOM fals */ }
      }
    }

    function copy() {
      var p = payload();
      if (!p.url) return;
      if (!canCopy()) { showManual(p.url); return; }
      var myGen = gen;
      var out;
      try { out = nav().clipboard.writeText(p.both); } catch (e) { showManual(p.url); return; }
      /* Fără promisiune (browser vechi) nu avem cum ști dacă a mers: nu confirmăm pe încredere. */
      if (!out || typeof out.then !== 'function') { showManual(p.url); return; }
      out.then(function () {
        if (state.destroyed || myGen !== gen) return;
        hideManual();
        status(ctx.text('share_copied'));
      }, function () {
        /* Clipboard-ul poate fi refuzat (permisiune lipsă, browser din aplicație). */
        if (state.destroyed || myGen !== gen) return;
        showManual(p.url);
      });
    }

    function shareNative() {
      if (!canShare()) { copy(); return; }
      var p = payload();
      if (!p.url) return;
      status('');
      var myGen = gen;
      var out;
      try {
        out = nav().share({ title: ctx.text('share_title'), text: p.text, url: p.url });
      } catch (e) {
        copy();
        return;
      }
      if (!out || typeof out.then !== 'function') return;
      out.then(null, function (err) {
        if (state.destroyed || myGen !== gen) return;
        /* „Am închis meniul" NU e o eroare: nu copiem nimic pe ascuns și nu spunem nimic. */
        if (err && err.name === 'AbortError') return;
        copy();
      });
    }

    /* Același ajutător ca la încărcarea prin `?r=` (vezi `stripResultParam`, la nivel de modul). */
    var stripToken = stripResultParam;

    function rescan() {
      if (ctx.result && typeof ctx.result.forget === 'function') ctx.result.forget();
      stripToken();
      ctx.stages.reset();
      ctx.stages.show('intro');
    }

    /* Id-ul panoului, din id-ul secțiunii (`nail-scan-<section.id>`), redus la caractere sigure. */
    function sharePanelId() {
      var own = asText(root && typeof root.getAttribute === 'function' ? root.getAttribute('id') : '').replace(/[^A-Za-z0-9_-]/g, '');
      return 'ns-share-panel-' + (own || 'x');
    }

    function render(detail) {
      if (!slot || state.destroyed) return;
      gen += 1;
      current = detail && detail.result && typeof detail.result === 'object' ? detail.result : null;
      var html = util.tpl('<button type="button" class="ns-btn ns-btn-ghost" data-ns-action="rescan">{t}</button>',
        { t: ctx.text('rescan_label') });
      /* Butonul de trimitere există MEREU: linkul de WhatsApp și câmpul de copiat cu degetul merg
         în orice browser, deci nu mai depinde de `navigator.share` sau de clipboard. */
      /* `aria-controls` leagă butonul de panoul pe care îl deschide (id propriu fiecărei secțiuni). */
      var panelId = sharePanelId();
      html += util.tpl('<button type="button" class="ns-btn ns-btn-ghost" data-ns-action="share" aria-expanded="false" aria-controls="{id}">{t}</button>',
        { id: panelId, t: ctx.text('share_label') });
      /* Panoul stă IMEDIAT sub butonul care îl deschide: pe telefon, deschis două rânduri mai jos,
         ar părea că butonul n-a făcut nimic.
         v1.2 §12.2: linkurile „Scrie-ne pe WhatsApp" / „Sună-ne" NU mai sunt aici — s-au mutat sus,
         în blocul „comandă cu un om" (`orderHelp`), imediat sub butonul de comandă. */
      html += util.tpl('<div class="ns-share-panel" id="{id}" data-ns-slot="share-panel" hidden></div>', { id: panelId });
      slot.innerHTML = html;
    }

    function clear() {
      gen += 1;
      current = null;
      if (slot) slot.innerHTML = '';
    }

    ctx.actions.register('rescan', rescan);
    ctx.actions.register('share', openPanel);
    ctx.actions.register('share-native', shareNative);
    ctx.actions.register('share-copy', copy);
    ctx.actions.register('share-cancel', function () { closePanel(true); });
    ctx.on(root, 'ns:result', function (ev) { render(ev && ev.detail ? ev.detail : null); });
    ctx.onReset(clear);

    return {
      render: render, clear: clear, rescan: rescan, stripToken: stripToken,
      openPanel: openPanel, closePanel: closePanel, shareNative: shareNative, copy: copy,
      shareBase: shareBase, shareUrl: shareUrl, message: message,
      canShare: canShare, canCopy: canCopy
    };
  }

  /* ── shared ── (v1.1 §3a) pagina „rezultat primit": ce vede cine deschide un link `?s=v1.…`.

     NU e proprietarul rezultatului, deci etapa e a ei (`data-ns-stage="shared"`), nu etapa
     `result` cu lucruri ascunse: sloturile proprietarului nu au cum să „scape" aici, iar
     `ns:result` nu se emite niciodată — de-asta planul, coșul și bara lui, emailul, fereastra de
     ieșire, evoluția și acțiunile de final sunt oprite PRIN CONSTRUCȚIE (toate ascultă `ns:result`).
     Gărzile în plus stau la sursă: `remember()`, `progress.write()`, `ctx.track.event()` și
     `api.event()` refuză cât timp `state.viewer` e adevărat.

     Textele statice (context, explicație, nota legală, butoanele) sunt în Liquid; de-aici vin doar
     inelul, barele, explicația pe severitate și îndemnul — toate din cele 4 câmpuri ale linkului.
     `s=` RĂMÂNE în adresă (reîncărcarea și „deschide în browser" arată aceeași pagină) până când
     omul apasă „Fă-ți și tu scanarea". */

  var SHARED_STICKY_END_PX = 100;

  var SHARED_EXIT_EVENT = 'ns:shared-exit';

  function createShared(ctx) {
    var root = ctx.root;
    var state = ctx.state;
    var stage = util.qs(root, '[data-ns-stage="shared"]');
    var okEl = util.qs(stage, '[data-ns-shared="ok"]');
    var invalidEl = util.qs(stage, '[data-ns-shared="invalid"]');
    var heroEl = util.qs(stage, '[data-ns-slot="shared-hero"]');
    var meaningEl = util.qs(stage, '[data-ns-slot="shared-meaning"]');
    var barsEl = util.qs(stage, '[data-ns-slot="shared-bars"]');
    var stickyEl = util.qs(root, '[data-ns-slot="shared-sticky"]');

    var valid = false;
    var observer = null;
    var scrolling = false;
    /* Ce buton e pe ecran, după `data-ns-placement`. Presupunem butonul de SUS vizibil până ne
       spune observatorul altceva — altfel bara ar clipi pe ecran la încărcarea paginii. */
    var seen = {};

    function setHidden(el, hidden) {
      if (!el) return;
      if (hidden) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    }

    function anySeen() {
      for (var key in seen) {
        if (Object.prototype.hasOwnProperty.call(seen, key) && seen[key]) return true;
      }
      return false;
    }

    /* Bara lipită există DOAR pe pagina primită validă și doar cât timp NICIUNUL dintre cele două
       butoane din pagină nu se vede: cu un singur buton urmărit, la capătul paginii ar fi stat două
       butoane identice unul sub altul. Se ascunde cu `hidden`, nu se mută (vezi bara coșului). */
    /* Bara e `position: fixed` pe marginea de jos: la CAPĂTUL paginii ar acoperi ultimele rânduri
       ale subsolului temei (acolo stau „Termeni" și „Setări cookies"), iar sub ea nu mai e nimic de
       derulat. Pe ultimii ~100 px ai paginii se dă la o parte. */
    function atPageEnd() {
      var w = getWindow();
      var doc = w && w.document && w.document.documentElement;
      if (!doc) return false;
      var height = Number(w.innerHeight) || 0;
      var total = Number(doc.scrollHeight) || 0;
      var y = Number(w.scrollY === undefined ? w.pageYOffset : w.scrollY) || 0;
      return height > 0 && total > height && y + height >= total - SHARED_STICKY_END_PX;
    }

    function linkPresent() {
      var w = getWindow();
      return parseShared((w && w.location && w.location.search) || '').present;
    }

    function syncSticky() {
      if (!stickyEl) return;
      /* …și doar cât `s=` chiar e în adresă: dacă altcineva l-a scos (o a doua secțiune de scaner
         de pe pagină), bara asta fixă n-are voie să rămână peste ecranele celeilalte. */
      var on = !!state.viewer && valid && state.stage === 'shared' && !anySeen() && !atPageEnd() && linkPresent();
      setHidden(stickyEl, !on);
      if (root.classList) root.classList[on ? 'add' : 'remove']('has-shared-sticky');
    }

    function stopObserver() {
      if (scrolling) {
        ctx.off(getWindow(), 'scroll', syncSticky);
        scrolling = false;
      }
      if (!observer) return;
      try { observer.disconnect(); } catch (e) { /* deja plecat */ }
      observer = null;
    }

    function watchButtons() {
      stopObserver();
      seen = {};
      ctx.on(getWindow(), 'scroll', syncSticky, { passive: true });
      scrolling = true;
      var buttons = util.qsa(okEl, '[data-ns-action="shared-start"]');
      var w = getWindow();
      var IO = w && w.IntersectionObserver;
      /* Fără IntersectionObserver: bara rămâne pe ecran cât ține pagina primită. */
      if (!buttons.length || typeof IO !== 'function') { syncSticky(); return; }
      seen.top = true;
      try {
        observer = new IO(function (entries) {
          if (state.destroyed) return;
          for (var i = 0; i < entries.length; i++) {
            var target = entries[i] && entries[i].target;
            var place = target && typeof target.getAttribute === 'function' ? target.getAttribute('data-ns-placement') : '';
            if (place) seen[place] = !!entries[i].isIntersecting;
          }
          syncSticky();
        }, { threshold: 0 });
        for (var b = 0; b < buttons.length; b++) observer.observe(buttons[b]);
      } catch (e) {
        observer = null;
        seen = {};
      }
      syncSticky();
    }

    function render(data) {
      renderScoreHero(ctx, heroEl, data, { labelKey: 'shared_score_label', affected: false });
      renderDimensionBars(ctx, barsEl, data, 'shared_bars_title');
      /* Severitatea vine din link, dar tot prin lista albă ajunge într-un nume de cheie. */
      var severity = SHARE_SEVERITIES.indexOf(data.severity) > -1 ? data.severity : '';
      var meaning = severity ? ctx.text('shared_meaning_' + severity).trim() : '';
      util.setText(meaningEl, meaning);
      setHidden(meaningEl, !meaning);
      /* „Tu cât ai lua?" e un joc potrivit lângă un scor bun; lângă „modificări avansate" ar fi
         lipsit de tact — acolo întrebarea e neutră. Markup-ul pornește cu varianta neutră. */
      var hook = ctx.text(util.own(SHARE_GOOD, severity) ? 'shared_hook_good' : 'shared_hook_neutral');
      if (hook) util.qsa(root, '[data-ns-slot="shared-hook"]').forEach(function (el) { util.setText(el, hook); });
    }

    function clear() {
      stopObserver();
      seen = {};
      valid = false;
      if (heroEl) heroEl.innerHTML = '';
      if (barsEl) barsEl.innerHTML = '';
      util.setText(meaningEl, '');
      syncSticky();
    }

    /* Rulează la pornire, ÎNAINTEA lui `stages.show('intro')` și a lui `result.boot()`: cu un `s=`
       în adresă, `intro` nu apare nicio clipă, iar `r=` și tokenul din sesiune sunt ignorate (nu se
       șterg, nu se scriu). Întoarce `true` dacă pagina e în modul vizitator. */
    function boot() {
      if (state.destroyed || !stage) return false;
      var w = getWindow();
      var link = parseShared((w && w.location && w.location.search) || '');
      if (!link.present) return false;
      state.viewer = true;
      valid = !!link.data;
      setHidden(okEl, !valid);
      setHidden(invalidEl, valid);
      if (valid) render(link.data);
      /* Fără focus și fără derulare: suntem la ÎNCĂRCAREA paginii, nu într-un pas al omului. */
      ctx.stages.show('shared', { focus: false, scroll: false });
      if (valid) watchButtons();
      else syncSticky();
      ctx.emit('ns:shared', { valid: valid });
      return true;
    }

    /* „Fă-ți și tu scanarea": de-aici încolo e un vizitator obișnuit al scanerului. `s=` iese din
       adresă (restul parametrilor rămân) — și, odată cu el, un `r=` rătăcit: scanarea NOUĂ nu are
       voie să-și trimită evenimentele cu un token vechi în adresa paginii. Sesiunea nu e atinsă.
       Bifa de acord pornește NEbifată chiar dacă browserul a păstrat-o de la o vizită mai veche:
       acordul e al omului care apasă acum, nu al formularului. */
    function leave(showOptions) {
      ctx.stages.reset();
      state.viewer = false;
      state.fromShared = true;
      state.consent = false;
      if (ctx.intro && typeof ctx.intro.sync === 'function') ctx.intro.sync();
      ctx.stages.show('intro', showOptions);
    }

    function start() {
      if (state.destroyed || !state.viewer) return;
      stripParams(['s', 'r']);
      leave({ top: true });
      /* `s=` a plecat din adresă pentru TOATĂ pagina: o a doua secțiune de scaner rămasă în modul
         vizitator ar arăta mai departe rezultatul altcuiva, cu bara ei fixă peste ecranele noastre. */
      var w = getWindow();
      emit(w && w.document, SHARED_EXIT_EVENT, {});
    }

    /* Altă secțiune a pornit scanarea: ieșim și noi, în liniște — fără derulare și fără focus luat. */
    function onSharedExit() {
      if (state.destroyed || !state.viewer) return;
      leave({ focus: false, scroll: false });
    }

    ctx.actions.register('shared-start', start);
    (function () {
      var w = getWindow();
      if (w && w.document) ctx.on(w.document, SHARED_EXIT_EVENT, onSharedExit);
    })();
    ctx.onReset(clear);
    ctx.onTeardown(clear);

    return {
      boot: boot,
      start: start,
      render: render,
      clear: clear,
      parse: parseShared,
      encode: encodeShared,
      active: function () { return !!state.viewer; },
      valid: function () { return valid; }
    };
  }

  /* ── testimonials ── (sarcina 68) banda randată din Liquid devine un carusel.
     Îmbunătățire progresivă: fără JS banda se trage cu degetul (scroll-snap), iar săgețile stau
     ascunse — două butoane moarte ar fi mai rele decât niciunul. */

  function createTestimonials(ctx) {
    var root = ctx.root;
    var track = util.qs(root, '[data-ns-testi-track]');
    var prev = util.qs(root, '[data-ns-action="testi-prev"]');
    var next = util.qs(root, '[data-ns-action="testi-next"]');
    if (!track || !prev || !next) return null;

    /* Un pas = distanța dintre două carduri (include spațiul dintre ele). Fără layout real
       (teste, browser vechi) cade pe lățimea benzii. */
    function step() {
      var cards = util.qsa(track, '.ns-testi');
      if (cards.length > 1 && typeof cards[0].offsetLeft === 'number' && typeof cards[1].offsetLeft === 'number') {
        var gap = cards[1].offsetLeft - cards[0].offsetLeft;
        if (gap > 0) return gap;
      }
      if (cards.length && typeof cards[0].offsetWidth === 'number' && cards[0].offsetWidth > 0) return cards[0].offsetWidth;
      return Number(track.clientWidth) || 0;
    }

    function sync() {
      var left = Number(track.scrollLeft) || 0;
      var max = (Number(track.scrollWidth) || 0) - (Number(track.clientWidth) || 0);
      setDisabled(prev, left <= 1);
      setDisabled(next, left >= max - 1);
    }

    function setDisabled(btn, off) {
      btn.disabled = !!off;
      if (off) btn.setAttribute('disabled', '');
      else btn.removeAttribute('disabled');
    }

    /* `scrollBy` emite `scroll`, iar ascultătorul de mai jos aprinde/stinge butoanele — un `sync()`
       în plus aici ar citi poziția de la ÎNCEPUTUL derulării line, deci ar minți. Pe drumul de
       rezervă (fără `scrollBy`) îl chemăm noi. */
    function move(direction) {
      var by = step() * direction;
      if (!by) return;
      var behavior = ctx.stages && ctx.stages.reducedMotion && ctx.stages.reducedMotion() ? 'auto' : 'smooth';
      try {
        if (typeof track.scrollBy === 'function') { track.scrollBy({ left: by, behavior: behavior }); return; }
      } catch (e) { /* cădem pe varianta simplă */ }
      track.scrollLeft = (Number(track.scrollLeft) || 0) + by;
      sync();
    }

    prev.removeAttribute('hidden');
    next.removeAttribute('hidden');
    ctx.actions.register('testi-prev', function () { move(-1); });
    ctx.actions.register('testi-next', function () { move(1); });
    ctx.on(track, 'scroll', sync);
    sync();
    /* La teardown butoanele se întorc de unde au plecat: fără handlerele noastre ar fi două săgeți
       moarte (editorul de teme poate lăsa nodul în pagină). */
    ctx.onTeardown(function () {
      prev.setAttribute('hidden', '');
      next.setAttribute('hidden', '');
    });

    return { move: move, sync: sync, step: step };
  }

  /* ── boot ── citește configul, construiește starea și contextul instanței, leagă UN SINGUR
     ascultător delegat pe rădăcină și curăță tot la `shopify:section:unload` / `DL.onDestroy`. */

  /* Deosebim „gol în mod legitim" (secțiune fără script de config, setări nepuse) de „JSON stricat":
     al doilea e o defecțiune pe care comerciantul trebuie s-o vadă în consolă și care blochează
     scanarea, nu o pornire cu toate textele goale. */
  function readConfig(root) {
    var el = util.qs(root, '[data-ns-config]');
    if (!el) return { config: {}, broken: false };
    var raw = el.textContent || '';
    if (!raw.trim()) return { config: {}, broken: false };
    try {
      return { config: JSON.parse(raw) || {}, broken: false };
    } catch (e) {
      /* un config stricat nu are voie să oprească pagina, dar nici să lase scanarea să pornească */
      try {
        if (typeof console !== 'undefined' && console.error) console.error('[nail-scan] JSON invalid in [data-ns-config]: scanarea ramane blocata pentru aceasta sectiune.', e && e.message);
      } catch (err) { /* consola poate lipsi */ }
      return { config: {}, broken: true };
    }
  }

  function createState(root, config, broken) {
    return {
      root: root,
      config: config,
      stage: null,
      consent: false,
      quiz: { zone: null, duration: null, count: null },
      photos: [],
      token: null,
      result: null,
      /* v1.1 §3a: `viewer` = pagina arată rezultatul ALTCUIVA, primit printr-un link `?s=…` (nimic
         nu se scrie, nimic nu se raportează); `fromShared` = omul a ajuns la scaner dintr-un
         asemenea link (doar în memorie, pleacă pe `NailScanCompleted`). */
      viewer: false,
      fromShared: false,
      /* v1.1 §4: poarta de email e ÎNCHISĂ — restul rezultatului stă ascuns sub `[data-ns-gated]`.
         O pune și o scoate DOAR modulul de email; bara lipită a coșului doar o citește. */
      gated: false,
      timers: new Set(),
      aborters: new Set(),
      consentReported: false,
      configBroken: !!broken,
      destroyed: false
    };
  }

  /* Registrul de acțiuni: sarcinile 65–68 își pun aici handlerele, fără să mai lege listeneri
     proprii pe butoane. `register` = clic pe `[data-ns-action=…]`, `registerChange` = `change` pe
     un element care poartă atributul dat (ex. `data-ns-file`), `registerSubmit` = trimiterea unui
     `[data-ns-form=…]` (mereu oprită, ca pagina să nu navigheze). */
  function createActions() {
    var clicks = {};
    var changes = {};
    var submits = {};
    return {
      /* Un nume înregistrat de două ori: câștigă ULTIMUL (o sarcină ulterioară poate vrea să
         împacheteze intenționat un handler), dar spunem asta în consolă — de obicei e o greșeală. */
      register: function (name, fn) {
        if (typeof clicks[name] === 'function') {
          try {
            if (typeof console !== 'undefined' && console.warn) console.warn('[nail-scan] actiunea "' + name + '" are deja un handler; il inlocuiesc cu cel nou.');
          } catch (e) { /* consola poate lipsi */ }
        }
        clicks[name] = fn;
        return fn;
      },
      registerChange: function (attribute, fn) { changes[attribute] = fn; return fn; },
      registerSubmit: function (formName, fn) { submits[formName] = fn; return fn; },
      has: function (name) { return typeof clicks[name] === 'function'; },
      names: function () { return Object.keys(clicks); },
      changeAttributes: function () { return Object.keys(changes); },
      run: function (name, ev, el) {
        if (typeof clicks[name] !== 'function') return false;
        clicks[name](ev, el);
        return true;
      },
      runChange: function (attribute, ev, el) {
        if (typeof changes[attribute] !== 'function') return false;
        changes[attribute](ev, el);
        return true;
      },
      runSubmit: function (formName, ev, el) {
        if (typeof submits[formName] !== 'function') return false;
        submits[formName](ev, el);
        return true;
      }
    };
  }

  function createContext(root, config, broken) {
    var state = createState(root, config, broken);
    var listeners = [];
    var teardownHooks = [];
    var resetHooks = [];
    var workerUrl = String((config && config.workerUrl) || root.getAttribute('data-ns-worker-url') || '').trim().replace(/\/+$/, '');

    var instanceId = util.uid();

    var ctx = {
      root: root,
      config: config,
      state: state,
      workerUrl: workerUrl,
      util: util,
      /* Pixelii, prin poarta instanței: în modul „vizitator" (v1.1 §3a) nu pleacă NICIUN eveniment —
         `s=` e încă în adresa paginii, iar omul nu e în pâlnia noastră de scanare. */
      track: {
        standard: track.standard,
        event: function (name, data) { if (!state.viewer) track.event(name, data); }
      },
      emit: function (type, detail) { return emit(root, type, detail); },

      /* `ctx.lockScroll('magnifier', true|false)` — cât e deschisă o fereastră a ACESTEI instanțe,
         pagina din spate nu se derulează. Cheia poartă id-ul instanței, ca două secțiuni să nu se
         calce. Eliberarea totală se face la resetare și la teardown (mai jos). */
      lockScroll: function (name, on) { return setModalLock(instanceId + ':' + name, on); },
      releaseScroll: function () { releaseModalLocks(instanceId + ':'); },

      text: function (key) {
        var texts = (config && config.texts) || {};
        return typeof texts[key] === 'string' ? texts[key] : '';
      },
      money: function (cents) { return util.money(cents, config && config.money && config.money.format); },

      on: function (target, type, fn, options) {
        if (!target || typeof target.addEventListener !== 'function') return fn;
        target.addEventListener(type, fn, options);
        listeners.push({ target: target, type: type, fn: fn, options: options });
        return fn;
      },
      off: function (target, type, fn) {
        if (target && typeof target.removeEventListener === 'function') target.removeEventListener(type, fn);
        listeners = listeners.filter(function (l) { return !(l.target === target && l.type === type && l.fn === fn); });
      },
      timer: function (fn, ms) {
        var id = setTimeout(function () {
          state.timers.delete(id);
          fn();
        }, ms);
        state.timers.add(id);
        return id;
      },
      clearTimer: function (id) { clearTimeout(id); state.timers.delete(id); },
      clearTimers: function () {
        state.timers.forEach(function (id) { clearTimeout(id); });
        state.timers.clear();
      },
      aborter: function () {
        var controller = makeAborter();
        state.aborters.add(controller);
        return controller;
      },
      /* Cererea s-a terminat singură: scoatem controllerul din registru fără să-l abandonăm
         (altfel setul ar crește la fiecare scanare). */
      releaseAborter: function (controller) { state.aborters.delete(controller); },
      abortAll: function () {
        state.aborters.forEach(function (controller) {
          try { controller.abort(); } catch (e) { /* deja oprit */ }
        });
        state.aborters.clear();
      },
      onTeardown: function (fn) { teardownHooks.push(fn); },
      onReset: function (fn) { resetHooks.push(fn); },
      runReset: function () {
        resetHooks.slice().forEach(function (fn) {
          try { fn(); } catch (e) { /* un modul stricat nu blochează resetul celorlalte */ }
        });
      },
      releaseListeners: function () {
        listeners.slice().forEach(function (l) {
          if (l.target && typeof l.target.removeEventListener === 'function') l.target.removeEventListener(l.type, l.fn, l.options);
        });
        listeners = [];
      },
      runTeardown: function () {
        teardownHooks.slice().forEach(function (fn) {
          try { fn(); } catch (e) { /* idem */ }
        });
        teardownHooks = [];
      },
      actions: createActions()
    };
    /* Orice drum de curățare ridică blocarea derulării: o pagină care nu se mai derulează, cu
       fereastra deja dispărută, e cel mai urât fel de a rămâne blocat. */
    ctx.onReset(function () { ctx.releaseScroll(); });
    ctx.onTeardown(function () { ctx.releaseScroll(); });

    /* `api` primește contextul: cronometrul de 90 s și AbortController-ul lui trebuie să fie în
       registrul instanței, ca teardown-ul să le oprească. */
    ctx.api = createApi(workerUrl, ctx);
    return ctx;
  }

  function bind(root) {
    if (root.getAttribute('data-bound')) return null;
    root.dataset.bound = '1';

    var parsed = readConfig(root);
    var config = parsed.config;
    var ctx = createContext(root, config, parsed.broken);
    ctx.stages = createStages(ctx);
    ctx.intro = initIntro(ctx);
    ctx.quiz = initQuiz(ctx);
    ctx.images = createImages(ctx);
    ctx.camera = createCamera(ctx, ctx.images);
    ctx.capture = initCapture(ctx);
    ctx.review = ctx.capture.review;
    ctx.result = createResult(ctx);
    ctx.plan = createPlan(ctx);
    ctx.cta = createCta(ctx);
    /* DUPĂ `plan` și `cta`: ascultătorul lui `ns:result` trebuie să ruleze ULTIMUL, ca bonusul
       refăcut dintr-o sesiune veche să cadă peste un plan deja randat (altfel `setBonus` ar
       recalcula niște totaluri care încă nu există). */
    ctx.email = createEmail(ctx);
    /* DUPĂ `email`: mesajul de comandă poartă linkul de rezultat, iar tokenul îl ține `email`. */
    ctx.orderHelp = createOrderHelp(ctx);
    ctx.progress = createProgress(ctx);
    ctx.endActions = createEndActions(ctx);
    ctx.shared = createShared(ctx);
    ctx.testimonials = createTestimonials(ctx);
    /* DUPĂ toți cei care ascultă `ns:result` (plan, cta, email, orderHelp…): instantaneul trimis
       Worker-ului trebuie să fie planul așa cum a ajuns pe ecran. */
    ctx.history = createHistory(ctx);
    /* DUPĂ `history`: pâlnia raportează pe tokenul pe care tocmai l-a reținut istoricul. */
    ctx.funnel = createFunnel(ctx);
    /* Scaner v2 (05.10.2026): cardul „Te sună un consultant Delfino" — tot DUPĂ `history`, fiindcă
       `callback_viewed` pleacă prin el, pe tokenul rezultatului de pe ecran. */
    ctx.callback = createCallback(ctx);
    ctx.processing = createProcessing(ctx);

    /* UN SINGUR ascultător delegat pentru toată secțiunea: butoanele randate mai târziu din JS
       (sarcinile 66–68) merg fără să mai lege nimic. */
    function onDelegated(ev) {
      if (!ev || ctx.state.destroyed) return; /* handler rămas agățat pe un nod aruncat */
      var target = ev.target || null;
      if (ev.type === 'click') {
        var actionEl = closestAttr(target, 'data-ns-action', root);
        if (!actionEl || isDisabled(actionEl)) return;
        ctx.actions.run(actionEl.getAttribute('data-ns-action'), ev, actionEl);
        return;
      }
      if (ev.type === 'change') {
        var attributes = ctx.actions.changeAttributes();
        for (var i = 0; i < attributes.length; i++) {
          var node = closestAttr(target, attributes[i], root);
          if (node) { ctx.actions.runChange(attributes[i], ev, node); return; }
        }
        return;
      }
      if (ev.type === 'submit') {
        var form = closestAttr(target, 'data-ns-form', root);
        if (!form) return;
        if (typeof ev.preventDefault === 'function') ev.preventDefault();
        ctx.actions.runSubmit(form.getAttribute('data-ns-form'), ev, form);
      }
    }

    ctx.on(root, 'click', onDelegated);
    ctx.on(root, 'change', onDelegated);
    ctx.on(root, 'submit', onDelegated);

    /* ORDINEA CONTEAZĂ (v1.1 §3a). Întâi întrebăm dacă pagina a fost deschisă dintr-un link primit
       (`?s=…`): atunci se arată direct etapa `shared`, iar `intro` și `result.boot()` NU rulează —
       altfel `intro` ar clipi pe ecran, iar un vizitator cu un token în sesiune și-ar vedea propriul
       rezultat încărcat peste cel primit. */
    if (!ctx.shared.boot()) {
      /* Prima afișare NU mută focusul și nu derulează: ar fura focusul la încărcarea paginii. */
      ctx.stages.show('intro', { focus: false, scroll: false });

      /* `?r=TOKEN` dintr-un email, sau tokenul păstrat în sesiune (reîncărcare de pagină): dacă
         n-avem ce încărca, intro rămâne exact cum e. */
      ctx.result.boot();
    }
    /* Atributul anti-flash (pus de scriptul inline din secțiune înainte să existe JS-ul ăsta) și-a
       făcut treaba: de-aici `hidden`/`inert` spun ce etapă se vede. Lăsat pe rădăcină, regula lui
       CSS ar ține `intro` ascunsă și după „Fă-ți și tu scanarea". */
    root.removeAttribute('data-ns-viewer');

    /* Cârligul de test (node:vm, testele sarcinilor 63–68): expus DOAR când secțiunea poartă
       `data-ns-test` — pe magazin nu există niciodată. */
    var hook = null;
    if (root.hasAttribute('data-ns-test')) {
      hook = {
        util: util, track: track, api: ctx.api, stages: ctx.stages, actions: ctx.actions,
        images: ctx.images, camera: ctx.camera, capture: ctx.capture, review: ctx.review,
        processing: ctx.processing, result: ctx.result, email: ctx.email, plan: ctx.plan, cta: ctx.cta, funnel: ctx.funnel,
        orderHelp: ctx.orderHelp, history: ctx.history, progress: ctx.progress, endActions: ctx.endActions, shared: ctx.shared, testimonials: ctx.testimonials,
        callback: ctx.callback,
        createState: createState, boot: init,
        ctx: ctx, state: ctx.state, config: config, root: root
      };
      var w = getWindow();
      if (w) w.__NailScanTest = hook;
      root.__nailScan = hook;
    }

    function cleanup() {
      /* ORDINEA CONTEAZĂ. Întâi cârligele de resetare, cât starea e încă VIE: acolo se revocă
         object URL-urile pozelor și se opresc track-urile camerei. Dacă am pune `destroyed`
         înainte, cârligele n-ar mai avea voie să atingă nimic (sau s-ar opri singure) și fiecare
         `blob:` ar rămâne agățat în memorie la fiecare `shopify:section:unload`. */
      ctx.runReset();
      /* Abia acum: semnalul pentru tot restul codului — orice promisiune care se rezolvă de-aici
         înainte nu mai are voie să atingă DOM-ul (se verifică `state.destroyed` după `await`). */
      ctx.state.destroyed = true;
      ctx.releaseListeners();
      ctx.clearTimers();
      ctx.abortAll();
      ctx.runTeardown();
      var w = getWindow();
      if (w && w.__NailScanTest && w.__NailScanTest.root === root) w.__NailScanTest = null;
      if (root.__nailScan) root.__nailScan = null;
      root.__nailScanCleanup = null;
      delete root.dataset.bound;
    }
    if (window.DL && typeof window.DL.onDestroy === 'function') window.DL.onDestroy(root, cleanup);
    else root.__nailScanCleanup = cleanup; /* theme.js lipsă (randare izolată / teste) */
    return ctx;
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-ns-root]:not([data-bound])');
    for (var i = 0; i < roots.length; i++) bind(roots[i]);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    var roots = e.target.querySelectorAll('[data-ns-root]');
    for (var i = 0; i < roots.length; i++) {
      if (typeof roots[i].__nailScanCleanup === 'function') roots[i].__nailScanCleanup();
    }
  });

  function register() {
    if (window.DL && typeof window.DL.use === 'function') window.DL.use(init);
    else init(document);
  }
  if (document.readyState === 'complete') register();
  else document.addEventListener('DOMContentLoaded', register, { once: true });
})();
